import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  LinearProgress,
  Chip,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Fade,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  keyframes,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import CircularProgress from "@mui/material/CircularProgress";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import History from "@mui/icons-material/History";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowForward from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from "@mui/icons-material/Settings";
import StarIcon from "@mui/icons-material/Star";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import accellorLogo from "../../../assets/images/accellorAILabs_logo_s.svg";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { recordAgentAction } from "../../../store/agentSlice";

// Temporary stub functions until RFQ Redux actions are implemented
const saveRfqQuestion = (data: any) => ({ type: 'user/saveRfqQuestion', payload: data });
const saveRfqAnswer = (data: any) => ({ type: 'user/saveRfqAnswer', payload: data });
const saveRfqStep = (data: any) => ({ type: 'user/saveRfqStep', payload: data });
const goBackRfqStep = () => ({ type: 'user/goBackRfqStep' });
const clearRfqStepsAfter = (data: any) => ({ type: 'user/clearRfqStepsAfter', payload: data });
const clearRfqAnswersAfter = (data: any) => ({ type: 'user/clearRfqAnswersAfter', payload: data });
const clearRfqData = (sessionId: any) => ({ type: 'user/clearRfqData', payload: sessionId });
import { v4 as uuid } from "uuid";
import axiosClient from "../../../api/AxiosClient";
import ReactMarkdown from "react-markdown";
import styles from "../agent-response.module.scss";
import * as XLSX from "xlsx";
import mammoth from "mammoth";

type Question = {
  question_id: string;
  question_text: string;
  question_type: string;
  options: string[];
  is_required: boolean;
  help_text: string | null;
  suggested_answer?: string | any[];
  option_descriptions?: Record<string, string> | null;
  editable_label?: string | null;
};

type RfqBuilderAgentProps = {
  parsedContent: any;
};

// Helper function to determine step number and name from status
const getStepInfo = (status: string): { stepNumber: number; stepName: string } | null => {
  const stepMap: { [key: string]: { stepNumber: number; stepName: string } } = {
    step1_categorization: { stepNumber: 1, stepName: "Categorization" },
    step2_initial_questions: { stepNumber: 2, stepName: "Initial Questions" },
    step2_rfq_questions: { stepNumber: 2, stepName: "RFQ Questions" },
    step2_specific_questions: { stepNumber: 2, stepName: "Specific Questions" },
    step3_specific_questions: { stepNumber: 3, stepName: "Specific Questions" },
    step3_sow_questions: { stepNumber: 3, stepName: "SOW Questions" },
    step4_sow_questions: { stepNumber: 4, stepName: "SOW Questions" },
    asking_step5_questions: { stepNumber: 5, stepName: "Step 5 Questions" },
    asking_evaluation_offer: { stepNumber: 5, stepName: "Evaluation Offer" },
    asking_evaluation_type: { stepNumber: 6, stepName: "Evaluation Type" },
    collecting_evaluation_data: { stepNumber: 7, stepName: "Evaluation Data" },
    asking_rfq_package: { stepNumber: 8, stepName: "RFQ Package" },
    asking_questions_dynamic: { stepNumber: 9, stepName: "Dynamic Questions" },
    awaiting_confirmation: { stepNumber: 10, stepName: "Confirmation" },
    awaiting_sow_confirmation: { stepNumber: 9, stepName: "SOW Confirmation" },
    completed: { stepNumber: 10, stepName: "Completed" },
  };

  return stepMap[status] || null;
};

// Standardized input field styles
const INPUT_FIELD_STYLES = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    backgroundColor: "#fafafa",
    fontSize: "13px",
    padding: "0 !important",
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 10px !important",
    fontSize: "13px",
    lineHeight: "1.4 !important",
  },
  "& .MuiInputBase-inputMultiline": {
    padding: "8px 10px !important",
    lineHeight: "1.4 !important",
  },
};

const markdownSubtitleComponents = {
  p: ({ children }: { children: React.ReactNode }) => (
    <p style={{ fontSize: "15px", color: "#666", margin: "8px 0 0 0" }}>{children}</p>
  ),
};

// Map sidebar section IDs to backend target_step values for reset API
const SECTION_TO_STEP_MAP: Record<string, string> = {
  'categorization': 'categorization',
  'requirements': 'requirements',
  'specifications': 'specifications',
  'sow-creation': 'sow_creation',
  'sow-review': 'sow_review',
  'evaluation-criteria': 'evaluation_criteria',
  'package-generation': 'package_generation',
};

// Map target_step values to the action that triggers that step
// Action names from backend: rfq_builder_agent.py lines 734, 842, 1008, 1097, 1460, 1743
const STEP_ACTION_MAP: Record<string, string> = {
  'categorization': 'start',
  'requirements': 'proceed_to_initial_questions',
  'specifications': 'proceed_to_goods_services_questions',
  'sow_creation': 'proceed_to_sow_questions',
  'sow_review': 'confirm_and_generate_document',
  'evaluation_criteria': 'select_evaluation_type',
  'package_generation': 'submit_rfq_package_answers',
};

export default function RfqBuilderAgent({
  parsedContent,
}: RfqBuilderAgentProps): JSX.Element {
  // Helper function to normalize markdown content for consistent formatting
  const normalizeMarkdownContent = (content: string): string => {
    if (!content) return content;

    let normalized = content;

    // Normalize standalone key-value pairs on their own lines (e.g., "Category: value")
    // Only apply if they're not already bolded and not part of a list
    normalized = normalized.replace(
      /^(?!\s*[-*\d]|\*\*)([A-Z][A-Za-z\s&/]+):\s*(.+)$/gm,
      (match, key, value) => `**${key.trim()}:**${value.startsWith(' ') ? '' : ' '}${value}`
    );

    return normalized;
  };

  // console.log("====== RFQ BUILDER COMPONENT CALLED ======");
  // console.log("RFQ Builder Agent - Parsed Content:", parsedContent);
  // console.log("Type of parsedContent:", typeof parsedContent);
  // console.log("parsedContent keys:", Object.keys(parsedContent || {}));
  // console.log(
  //   "parsedContent stringified:",
  //   JSON.stringify(parsedContent, null, 2),
  // );

  const dispatch = useAppDispatch();
  const params = new URLSearchParams(location.search);
  const sessionIdFromUrl = params.get("sessionId");

  // Get current agent info from Redux
  const { selectedAgent } = useSelector((state: RootState) => state.user);
  const numericAgentId = selectedAgent?.id || 11;

  // Extract data from the response FIRST (before using sessionId)
  // userSlice processes rfq_builder through the generic handler which puts data in finalAnswer
  // parsedContent structure: { finalAnswer: { agent_id, status, all_questions, message, ... } }
  // OR for nested responses: { finalAnswer: { response: [{result: {...}}] } }
  let result = parsedContent?.finalAnswer || parsedContent?.result?.data || parsedContent;

  // Handle nested response array structure (for RFQ builder responses)
  if (result?.response && Array.isArray(result.response) && result.response.length > 0) {
    const lastResponse = result.response[result.response.length - 1];
    if (lastResponse?.result) {
      result = lastResponse.result;
    }
  }

  // Extract sessionId early so we can use it in selectors
  const sessionId = result?.session_id || result?.chat_id;

  // IMPORTANT: Save the original status from backend BEFORE any historical overwrites
  const backendStatus = result?.status || "";
  const backendStepInfo = getStepInfo(backendStatus);

  // Get RFQ data from Redux for this specific session (memoized to prevent infinite loops)
  // TODO: Add rfqQuestions, rfqAnswers, rfqStepHistory, rfqCurrentStep to userSlice state
  const rfqQuestionsForSession = useSelector((state: RootState) =>
    sessionId ? (state.user as any).rfqQuestions?.[sessionId] : undefined
  );
  const rfqAnswersForSession = useSelector((state: RootState) =>
    sessionId ? (state.user as any).rfqAnswers?.[sessionId] : undefined
  );
  const rfqStepHistory = useSelector((state: RootState) =>
    sessionId ? (state.user as any).rfqStepHistory?.[sessionId] : undefined
  );
  const rfqCurrentStepNumber = useSelector((state: RootState) =>
    sessionId ? (state.user as any).rfqCurrentStep?.[sessionId] : undefined
  );

  // Check if we're viewing a historical step (user clicked Back)
  // Compare Redux current step with the actual backend step
  const isViewingHistoricalStep =
    rfqCurrentStepNumber &&
    backendStepInfo &&
    rfqCurrentStepNumber < backendStepInfo.stepNumber;

  // Use historical data if viewing a previous step, otherwise use current result
  if (isViewingHistoricalStep && rfqStepHistory) {
    const historicalStep = rfqStepHistory.find(
      (step) => step.stepNumber === rfqCurrentStepNumber
    );
    if (historicalStep) {
      result = historicalStep.response;
      // console.log(`Viewing historical step ${rfqCurrentStepNumber} (${historicalStep.stepName}), backend is at step ${backendStepInfo?.stepNumber}`);
    }
  }

  // console.log("Extracted result:", result);
  // console.log("result stringified:", JSON.stringify(result, null, 2));
  // console.log("All questions:", result?.all_questions);
  // console.log("All questions length:", result?.all_questions?.length);

  const message = (result?.message || "").toLowerCase().includes("thinking") ? "" : (result?.message || "");
  const status = result?.status || "";
  const allQuestions: Question[] = result?.all_questions || [];
  const currentQuestion = result?.current_question || null; // NEW: Dynamic single question
  const totalQuestions = result?.total_questions || allQuestions.length;
  const answeredQuestionsCount = result?.answered_questions || 0; // NEW: Track answered count
  const chatId = result?.chat_id || parsedContent?.chatId || parsedContent?.finalAnswer?.chat_id;
  // Try multiple paths to extract workflow_id
  // Note: Backend may use session_id as workflow_id - using session_id as fallback
  const workflowId = result?.workflow_id ||
                     parsedContent?.workflow_id ||
                     parsedContent?.workflowId ||
                     parsedContent?.finalAnswer?.workflow_id ||
                     parsedContent?.result?.data?.workflow_id ||
                     parsedContent?.result?.data?.response?.[0]?.result?.workflow_id ||
                     sessionId; // Fallback: use session_id as workflow_id
  // sessionId already extracted above for use in selectors
  const generatedDocumentPath = result?.generated_document_path || null;
  const downloadUrlFromBackend = result?.download_url || null;
  const structuredPreview = result?.structured_preview || null;
  const collectedData = result?.collected_data || {};
  const nextAction = result?.next_action || null;

  // console.log("Component state:", {
  //   status,
  //   questionsCount: allQuestions.length,
  //   hasCurrentQuestion: !!currentQuestion,
  //   currentQuestionId: currentQuestion?.question_id,
  //   answeredQuestionsCount,
  //   totalQuestions,
  //   sessionId,
  //   hasQuestions: allQuestions.length > 0,
  //   hasStructuredPreview: !!structuredPreview,
  //   collectedDataKeys: Object.keys(collectedData),
  // });

  // console.log("Structured Preview:", structuredPreview);
  // console.log("Collected Data:", collectedData);

  // State management
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [customAnswers, setCustomAnswers] = useState<Record<string, string>>(
    {},
  );
  const [currentAnswer, setCurrentAnswer] = useState<string>(""); // NEW: For dynamic single question
  const [currentCustomAnswer, setCurrentCustomAnswer] = useState<string>(""); // NEW: For "Other" option
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [editedData, setEditedData] = useState<Record<string, string>>({});
  const [isEditingField, setIsEditingField] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isViewingHistory, setIsViewingHistory] = useState(false);
  const [editedAnswers, setEditedAnswers] = useState<Record<string, string>>({});
  const [updatingQuestionId, setUpdatingQuestionId] = useState<string | null>(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [contentKey, setContentKey] = useState(0); // Key for content transitions only
  const [dynamicQuestionPosition, setDynamicQuestionPosition] = useState<number>(0); // 0 = current, 1 = one back, etc.
  const [categorizationData, setCategorizationData] = useState(() => {
    // Get suggested answers from questions
    const categoryQ = allQuestions.find((q: Question) => q.question_id === "category");
    const deliveryQ = allQuestions.find((q: Question) => q.question_id === "delivery_location");

    return {
      category: collectedData?.category || (categoryQ as any)?.suggested_answer || "",
      delivery_location: collectedData?.delivery_location || (deliveryQ as any)?.suggested_answer || "",
      client_company: collectedData?.client_company || "", // Never prefill client company
    };
  });
  const [isProceedingToStep2, setIsProceedingToStep2] = useState(false);
  const [step2InitialAnswers, setStep2InitialAnswers] = useState<Record<string, string>>({});
  const [isProceedingToGoodsServices, setIsProceedingToGoodsServices] = useState(false);
  const [step2Answers, setStep2Answers] = useState<Record<string, string>>({});
  const [step3SpecificAnswers, setStep3SpecificAnswers] = useState<Record<string, string>>({});
  const [isProceedingToSow, setIsProceedingToSow] = useState(false);
  const [step4SowAnswers, setStep4SowAnswers] = useState<Record<string, string>>({});
  const [isGeneratingSowDocument, setIsGeneratingSowDocument] = useState(false);
  const [evaluationAnswer, setEvaluationAnswer] = useState<string>("");
  const [isSubmittingEvaluation, setIsSubmittingEvaluation] = useState(false);
  const [step5Answer, setStep5Answer] = useState<string>("");
  const [isSubmittingStep5, setIsSubmittingStep5] = useState(false);
  const [techEvalAnswer, setTechEvalAnswer] = useState<string>("");
  const [isSubmittingTechEval, setIsSubmittingTechEval] = useState(false);
  const [evaluationTypeAnswer, setEvaluationTypeAnswer] = useState<string>("");
  const [isSubmittingEvaluationType, setIsSubmittingEvaluationType] = useState(false);
  const [technoCommercialAnswers, setTechnoCommercialAnswers] = useState<Record<string, string>>({});
  const [isSubmittingTechnoCommercial, setIsSubmittingTechnoCommercial] = useState(false);
  const [rfqPackageAnswer, setRfqPackageAnswer] = useState<string>("");
  const [isSubmittingRfqPackage, setIsSubmittingRfqPackage] = useState(false);
  const [rfqPackageFormAnswers, setRfqPackageFormAnswers] = useState<Record<string, string>>({});
  const [isSubmittingRfqPackageForm, setIsSubmittingRfqPackageForm] = useState(false);
  const [currentRfqPackageQuestionIndex, setCurrentRfqPackageQuestionIndex] = useState(0);
  const [isProceedingToStep3, setIsProceedingToStep3] = useState(false);
  const [step3Answers, setStep3Answers] = useState<Record<string, string>>({});
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [editedSections, setEditedSections] = useState<Record<string, string>>({});
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [isConfirmingDocument, setIsConfirmingDocument] = useState(false);
  const [isResettingStep, setIsResettingStep] = useState(false);
  const [criterionTitles, setCriterionTitles] = useState<Record<string, string>>({});

  // State for custom criteria management (add/delete)
  type CustomCriterion = {
    id: string;
    title: string;
    description: string;
    weight: string;
  };
  const [customCriteria, setCustomCriteria] = useState<CustomCriterion[]>([]);
  const [deletedCriterionIds, setDeletedCriterionIds] = useState<Set<string>>(new Set());

  // State to track when fetching new question after answer change (prevents showing old cached question)
  const [isFetchingNewQuestion, setIsFetchingNewQuestion] = useState(false);

  // Ref to track step4 initialization to prevent resetting currentQuestionIndex on re-renders
  const step4InitializedRef = useRef<string | null>(null);

  // Ref to track RFQ package initialization
  const rfqPackageInitializedRef = useRef<string | null>(null);

  // State for document preview modal
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ name: string; url: string; type: string } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>("");

  // State for step2_specific_questions (Goods/Service form)
  type LineItem = {
    id: string;
    title: string;
    manufacturer_part_number: string;
    quantity: string;
    uom: string;
    unit_price: string;
    start_date?: string; // For services
    end_date?: string; // For services
  };
  const [procurementType, setProcurementType] = useState<"Goods" | "Service">("Goods");
  const [lineItems, setLineItems] = useState<LineItem[]>([
    {
      id: uuid(),
      title: "",
      manufacturer_part_number: "",
      quantity: "",
      uom: "",
      unit_price: "",
      start_date: "",
      end_date: "",
    },
  ]);

  // Loading messages that cycle through
  const loadingMessages = [
    "Understanding your requirement...",
    "Analyzing your response...",
    "Getting relatable next question...",
    "Processing information...",
  ];

  // Keyframe animations
  const pulse = keyframes`
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.8;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  `;

  const shimmer = keyframes`
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  `;

  const float = keyframes`
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  `;

  // Save current question to Redux and update content key for transition
  useEffect(() => {
    if (currentQuestion && sessionId) {
      dispatch(
        saveRfqQuestion({
          sessionId,
          question: currentQuestion,
        }),
      );
      // console.log("Dispatched saveRfqQuestion:", currentQuestion.question_id, currentQuestion.question_text);

      // Change key to trigger content transition
      setContentKey((prev) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion?.question_id, sessionId, dispatch]);

  // Also save all questions from allQuestions array to Redux (if available)
  // Use length as dependency to avoid re-firing unnecessarily
  useEffect(() => {
    if (allQuestions && allQuestions.length > 0 && sessionId) {
      allQuestions.forEach((q) => {
        dispatch(
          saveRfqQuestion({
            sessionId,
            question: q,
          }),
        );
      });
      // console.log("Dispatched all questions from allQuestions array:", allQuestions.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allQuestions.length, sessionId, dispatch]);

  // Update categorization data when backend sends suggested answers
  useEffect(() => {
    if (status === "step1_categorization" && allQuestions.length > 0) {
      const categoryQ = allQuestions.find((q: Question) => q.question_id === "category");
      const deliveryQ = allQuestions.find((q: Question) => q.question_id === "delivery_location");

      setCategorizationData((prev) => ({
        category: collectedData?.category || (categoryQ as any)?.suggested_answer || prev.category,
        delivery_location: collectedData?.delivery_location || (deliveryQ as any)?.suggested_answer || prev.delivery_location,
        client_company: prev.client_company, // Never auto-update client_company
      }));
    }
  }, [status, allQuestions.length, collectedData]);

  // Save current step to Redux when a new response arrives from backend
  // IMPORTANT: Only save when NOT viewing historical steps
  useEffect(() => {
    if (backendStatus && sessionId) {
      const stepInfo = getStepInfo(backendStatus);

      if (stepInfo) {
        // Check if this step is already saved in history
        const existingStep = rfqStepHistory?.find(s => s.stepNumber === stepInfo.stepNumber);
        const isNewStep = !existingStep;

        // Only save if:
        // 1. It's a completely new step OR
        // 2. We're at the current step (not viewing historical) AND step exists but might have updated data
        const shouldSave = isNewStep || (!isViewingHistoricalStep && rfqCurrentStepNumber === stepInfo.stepNumber);

        if (shouldSave) {
          // Get the original result from parsedContent (not the potentially overwritten result)
          let originalResult = parsedContent?.finalAnswer || parsedContent?.result?.data || parsedContent;

          // Handle nested response array structure
          if (originalResult?.response && Array.isArray(originalResult.response) && originalResult.response.length > 0) {
            const lastResponse = originalResult.response[originalResult.response.length - 1];
            if (lastResponse?.result) {
              originalResult = lastResponse.result;
            }
          }

          dispatch(
            saveRfqStep({
              sessionId,
              stepNumber: stepInfo.stepNumber,
              stepName: stepInfo.stepName,
              response: originalResult,
            }),
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendStatus, sessionId]);

  // Load saved answers when navigating between questions using currentQuestionIndex (old mode)
  useEffect(() => {
    if (allQuestions.length > 0 && currentQuestionIndex >= 0 && sessionId && rfqAnswersForSession) {
      const question = allQuestions[currentQuestionIndex];
      if (question) {
        const savedAnswer = rfqAnswersForSession.find(
          (ans) => ans.questionId === question.question_id
        );

        if (savedAnswer) {
          // Update answers state
          setAnswers((prev) => ({
            ...prev,
            [question.question_id]: savedAnswer.answer,
          }));

          // Check if this is an "Other" answer (custom text)
          const isOtherAnswer =
            question.options?.some(opt =>
              opt.toLowerCase().includes("other") &&
              savedAnswer.answer !== opt
            );

          if (isOtherAnswer) {
            setCustomAnswers((prev) => ({
              ...prev,
              [question.question_id]: savedAnswer.answer,
            }));
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionIndex, sessionId]);

  // Initialize categorization data from questions' suggested answers (runs once)
  useEffect(() => {
    if (status === "step1_categorization" && allQuestions.length > 0) {
      // Only set if current state is empty (initial load only)
      if (!categorizationData.category && !categorizationData.delivery_location && !categorizationData.client_company) {
        const initialData: Record<string, string> = {};

        allQuestions.forEach((q: Question) => {
          const questionId = q.question_id;
          const suggestedAnswer = q.suggested_answer || (q as any).suggested || "";

          if (questionId === "category" || questionId === "delivery_location" || questionId === "client_company") {
            initialData[questionId] = suggestedAnswer;
          }
        });

        // Prefer collectedData if available, otherwise use questions' suggested_answer
        setCategorizationData({
          category: collectedData.category || initialData.category || "",
          delivery_location: collectedData.delivery_location || initialData.delivery_location || "",
          client_company: collectedData.client_company || initialData.client_company || "",
        });
      }
    }
  }, [status, allQuestions.length]);

  // Initialize technoCommercialAnswers from questions' suggested answers (collecting_evaluation_data)
  useEffect(() => {
    if (status === "collecting_evaluation_data" && allQuestions.length > 0) {
      setTechnoCommercialAnswers((prev) => {
        // If user has already entered data, preserve it completely
        const hasUserData = Object.keys(prev).length > 0;
        if (hasUserData) {
          console.log("[Evaluation Data] User has entered data - preserving all values");
          return prev;
        }

        // First time - initialize with suggested answers
        console.log("[Evaluation Data] Initializing with suggested answers");
        const initialAnswers: Record<string, string> = {};
        allQuestions.forEach((q: Question) => {
          const suggestedAnswer = q.suggested_answer || (q as any).suggested || "";
          if (suggestedAnswer) {
            initialAnswers[q.question_id] = suggestedAnswer;
          }
        });
        return initialAnswers;
      });
    }
  }, [status, allQuestions.length]);

  // Initialize criterionTitles from questions' editable_label values (collecting_evaluation_data)
  useEffect(() => {
    if (status === "collecting_evaluation_data" && allQuestions.length > 0) {
      setCriterionTitles((prev) => {
        // If user has already edited titles, preserve them
        if (Object.keys(prev).length > 0) {
          return prev;
        }

        // Initialize with editable_label values
        const initialTitles: Record<string, string> = {};
        allQuestions.forEach((q: Question) => {
          if (q.editable_label !== undefined && q.editable_label !== null) {
            initialTitles[q.question_id] = q.editable_label;
          }
        });
        return initialTitles;
      });
    }
  }, [status, allQuestions.length]);

  // Initialize rfqPackageFormAnswers from questions' suggested answers (collecting_rfq_package_data)
  useEffect(() => {
    if (status === "collecting_rfq_package_data" && allQuestions.length > 0) {
      const sessionKey = `${sessionId}-${status}`;

      // Only initialize once per session
      if (rfqPackageInitializedRef.current !== sessionKey) {
        const initialAnswers: Record<string, string> = {};

        allQuestions.forEach((q: Question) => {
          const suggestedAnswer = q.suggested_answer || (q as any).suggested || "";
          if (suggestedAnswer) {
            initialAnswers[q.question_id] = suggestedAnswer;
          }
        });

        // Only set if rfqPackageFormAnswers is empty or has fewer entries
        if (Object.keys(rfqPackageFormAnswers).length < allQuestions.length) {
          setRfqPackageFormAnswers((prev) => ({
            ...initialAnswers,
            ...prev, // Keep any manually entered answers
          }));
        }

        // Reset question index when first entering this status
        setCurrentRfqPackageQuestionIndex(0);

        // Mark as initialized
        rfqPackageInitializedRef.current = sessionKey;
      }
    }
  }, [status, allQuestions.length, sessionId]);

  // Update form data when viewing historical step - Step 1
  useEffect(() => {
    if (isViewingHistoricalStep && status === "step1_categorization" && collectedData) {
      setCategorizationData({
        category: collectedData.category || "",
        delivery_location: collectedData.delivery_location || "",
        client_company: collectedData.client_company || "",
      });
    }
  }, [isViewingHistoricalStep, status]);

  // Update form data when viewing historical step - Step 2 Initial Answers
  useEffect(() => {
    if (isViewingHistoricalStep && status === "step2_initial_questions" && collectedData) {
      setStep2InitialAnswers(collectedData as Record<string, string>);
    }
  }, [isViewingHistoricalStep, status]);

  // Update form data when viewing historical step - Step 2 RFQ/Specific Questions
  useEffect(() => {
    if (isViewingHistoricalStep && (status === "step2_rfq_questions" || status === "step2_specific_questions") && collectedData) {
      setStep2Answers(collectedData as Record<string, string>);
    }
  }, [isViewingHistoricalStep, status]);

  // Update form data when viewing historical step - Step 3
  useEffect(() => {
    if (isViewingHistoricalStep && status === "step3_specific_questions" && collectedData) {
      setStep3SpecificAnswers(collectedData as Record<string, string>);
    }
  }, [isViewingHistoricalStep, status]);

  // Update form data when viewing historical step - Step 4 SOW
  useEffect(() => {
    if (isViewingHistoricalStep && status === "step4_sow_questions" && collectedData) {
      setStep4SowAnswers(collectedData as Record<string, string>);
    }
  }, [isViewingHistoricalStep, status]);

  // Initialize step2 answers with suggested answers from questions
  useEffect(() => {
    if (status === "step2_rfq_questions" && allQuestions.length > 0) {
      const initialAnswers: Record<string, string> = {};
      allQuestions.forEach((q: any) => {
        // Use suggested_answer if provided, otherwise generate a default based on question
        let defaultAnswer = q.suggested_answer || "";

        // If no suggested answer, generate a placeholder based on question_id/text
        if (!defaultAnswer) {
          if (q.question_id.includes("budget")) {
            defaultAnswer = "AED 50,000 - 100,000";
          } else if (q.question_id.includes("quantity")) {
            defaultAnswer = "10 units";
          } else if (q.question_id.includes("timeline") || q.question_id.includes("delivery")) {
            defaultAnswer = "30 days from order confirmation";
          } else if (q.question_id.includes("priority")) {
            defaultAnswer = "High";
          } else if (q.question_id.includes("technical") || q.question_id.includes("specs")) {
            defaultAnswer = "Standard specifications as per industry standards. Must meet ISO certification requirements.";
          } else if (q.question_id.includes("payment")) {
            defaultAnswer = "Net 30 days";
          } else if (q.question_id.includes("sourcing") || q.question_id.includes("strategy")) {
            defaultAnswer = "Tender";
          } else if (q.question_id.includes("evaluation") || q.question_id.includes("criteria")) {
            defaultAnswer = "Price competitiveness (40%), Quality certifications (30%), Delivery time (20%), Supplier reputation (10%)";
          } else {
            defaultAnswer = "To be determined based on requirements";
          }
        }

        initialAnswers[q.question_id] = defaultAnswer;
      });
      setStep2Answers(initialAnswers);
    }
  }, [status, allQuestions.length]);

  // Initialize step3 answers with suggested answers from questions
  useEffect(() => {
    if (status === "step3_sow_questions" && allQuestions.length > 0) {
      const initialAnswers: Record<string, string> = {};
      allQuestions.forEach((q: any) => {
        let defaultAnswer = q.suggested_answer || "";

        // If no suggested answer, generate defaults for SOW questions
        if (!defaultAnswer) {
          if (q.question_id.includes("background") || q.question_id.includes("context")) {
            defaultAnswer = "This procurement is required to fulfill business needs and operational requirements.";
          } else if (q.question_id.includes("evaluation")) {
            defaultAnswer = "Simple, Total Cost Project";
          } else if (q.question_id.includes("deliverable")) {
            defaultAnswer = "1. Product/Service Delivery\n   [To be specified based on requirements]";
          } else if (q.question_id.includes("performance") || q.question_id.includes("kpi")) {
            defaultAnswer = "- Compliance with all specified requirements\n- On-time delivery as per agreed timeline";
          } else {
            defaultAnswer = "To be determined";
          }
        }

        initialAnswers[q.question_id] = defaultAnswer;
      });
      setStep3Answers(initialAnswers);
    }
  }, [status, allQuestions.length]);

  // Set procurement type FIRST (separate effect to ensure it runs before lineItems initialization)
  useEffect(() => {
    if (status === "step2_specific_questions" && collectedData?.procurement_type) {
      console.log("Setting procurement type from backend:", collectedData.procurement_type);
      const normalizedType = collectedData.procurement_type === "Services" ? "Service" : collectedData.procurement_type;
      setProcurementType(normalizedType as "Goods" | "Service");
    }
  }, [status, collectedData?.procurement_type]);

  // Initialize line items for step2_specific_questions with pre-filled data (runs AFTER procurementType is set)
  useEffect(() => {
    if (status === "step2_specific_questions" && allQuestions && allQuestions.length > 0 && procurementType) {
      console.log("=== Step 2 Line Items Initialization ===");
      console.log("allQuestions:", allQuestions);
      console.log("collectedData:", collectedData);
      console.log("Current procurementType:", procurementType);
        // First, check if there's a question with type "line_items" that has suggested_answer array
        // Check for both goods_line_items and services_line_items question IDs
        const lineItemsQuestion = allQuestions.find((q: any) =>
          (q.question_type === "line_items" || q.question_id === "goods_line_items" || q.question_id === "services_line_items") &&
          q.suggested_answer &&
          Array.isArray(q.suggested_answer) &&
          q.suggested_answer.length > 0
        );

        console.log("Found line items question:", lineItemsQuestion);

        if (lineItemsQuestion) {
          // Map suggested_answer array to LineItem objects
          // Handle both goods (item_name) and services (service_description)
          const suggestedItems = lineItemsQuestion.suggested_answer.map((item: any) => ({
            id: uuid(),
            title: item.item_name || item.service_description || "",
            manufacturer_part_number: item.manufacturer_part_number || "",
            quantity: String(item.quantity || ""),
            uom: item.unit ? item.unit.toLowerCase() : "each", // Normalize to lowercase
            unit_price: String(item.unit_price || ""),
            start_date: item.start_date || "",
            end_date: item.end_date || "",
          }));

          console.log("✅ Pre-filling line items from suggested_answer:", suggestedItems);
          setLineItems(suggestedItems);
        } else {
          console.log("⚠️ No line items question found with suggested_answer");

          // Fallback to old logic if no line_items question found
          // Pre-fill line item from collected_data or questions
          // Try multiple sources for the title
          let itemTitle = collectedData?.title || collectedData?.request_title || "";

          // If no title, use category from categorization step
          if (!itemTitle && categorizationData.category) {
            // Extract the last part of the category hierarchy
            const categoryParts = categorizationData.category.split("→");
            itemTitle = categoryParts[categoryParts.length - 1]?.trim() || categorizationData.category;
          }

          const initialItem: LineItem = {
            id: uuid(),
            title: itemTitle,
            manufacturer_part_number: "",
            quantity: "500", // Default quantity
            uom: "each", // Default UoM
            unit_price: "",
            start_date: "",
            end_date: "",
          };

          // Try to extract values from questions if they have suggested_answer
          if (allQuestions && allQuestions.length > 0) {
            allQuestions.forEach((q: any) => {
              if (q.suggested_answer) {
                if (q.question_id.includes("quantity") || q.question_id.includes("goods_quantity_uom")) {
                  // Parse "500 each" or "10 units" format
                  const match = q.suggested_answer.match(/^(\d+)\s*(.*)$/);
                  if (match) {
                    initialItem.quantity = match[1];
                    initialItem.uom = match[2] || "";
                  }
                } else if (q.question_id.includes("title") || q.question_id.includes("product_specs")) {
                  if (!initialItem.title && q.suggested_answer) {
                    initialItem.title = q.suggested_answer.split("\n")[0] || q.suggested_answer;
                  }
                }
              }
            });
          }

          // Set initial line items if we have any pre-filled data
          if (initialItem.title || initialItem.quantity) {
            setLineItems([initialItem]);
          }
        }
    }
  }, [status, allQuestions.length, procurementType]); // Use allQuestions.length to avoid infinite loops

  // Initialize step2_initial_questions with suggested answers
  useEffect(() => {
    if (status === "step2_initial_questions" && allQuestions.length > 0) {
      const initialAnswers: Record<string, string> = {};
      allQuestions.forEach((q: any) => {
        if (q.suggested_answer) {
          initialAnswers[q.question_id] = q.suggested_answer;
        }
      });
      setStep2InitialAnswers(initialAnswers);
    }
  }, [status, allQuestions]);

  // Initialize step3_specific_questions with suggested answers
  useEffect(() => {
    if (status === "step3_specific_questions" && allQuestions.length > 0) {
      const initialAnswers: Record<string, string> = {};
      allQuestions.forEach((q: any) => {
        if (q.suggested_answer) {
          initialAnswers[q.question_id] = q.suggested_answer;
        }
      });
      setStep3SpecificAnswers(initialAnswers);
    }
  }, [status, allQuestions]);

  // Initialize step4_sow_questions with suggested answers
  useEffect(() => {
    if (status === "step4_sow_questions" && allQuestions.length > 0) {
      // Create a unique key for this session/status combination
      const initKey = `${sessionId}_${status}`;

      // Only initialize if we haven't already initialized for this session/status
      if (step4InitializedRef.current !== initKey) {
        const initialAnswers: Record<string, string> = {};
        allQuestions.forEach((q: any) => {
          if (q.suggested_answer) {
            initialAnswers[q.question_id] = q.suggested_answer;
          }
        });
        setStep4SowAnswers(initialAnswers);
        setCurrentQuestionIndex(0); // Reset to first question
        step4InitializedRef.current = initKey; // Mark as initialized
      }
    }
  }, [status, allQuestions.length, sessionId]); // Use length to avoid re-running on array reference changes

  // Pre-select suggested answer for dynamic questions (currentQuestion mode)
  useEffect(() => {
    if (currentQuestion) {
      let valueToSet = "";

      // Only pre-select if we have a suggested answer
      if (currentQuestion.suggested_answer && currentQuestion.options) {
        const suggestedAnswer = currentQuestion.suggested_answer;

        // Ensure suggestedAnswer is a string
        if (typeof suggestedAnswer === 'string') {
          // Try to find exact match first
          const exactMatch = currentQuestion.options.find((opt: string) => opt === suggestedAnswer);

          if (exactMatch) {
            // Exact match found - use it as is
            valueToSet = exactMatch;
          } else {
            // Try to find match by removing number prefix (e.g., "6. Other" -> "Other")
            const withoutNumber = suggestedAnswer.replace(/^\d+\.\s*/, '');
            const partialMatch = currentQuestion.options.find((opt: string) => opt === withoutNumber);

            if (partialMatch) {
              // Found a match after stripping number - use the actual option value
              valueToSet = partialMatch;
            }
            // If no match found at all, leave valueToSet as empty string (no pre-selection)
          }
        }
      }

      setCurrentAnswer(valueToSet);

      // IMPORTANT: Also update answers map (same as handleAnswerSelect does)
      if (valueToSet) {
        setAnswers((prev) => ({
          ...prev,
          [currentQuestion.question_id]: valueToSet,
        }));
      }

      setCurrentCustomAnswer(""); // Reset custom answer when question changes
    }
  }, [currentQuestion?.question_id]); // Trigger when question changes

  // Reset isFetchingNewQuestion when a new question arrives after answer change
  useEffect(() => {
    if (currentQuestion && isFetchingNewQuestion) {
      setIsFetchingNewQuestion(false);
    }
  }, [currentQuestion?.question_id]);

  // Initialize answers with suggested answers for allQuestions array (general fallback)
  useEffect(() => {
    if (!currentQuestion && allQuestions.length > 0) {
      const initialAnswers: Record<string, string> = {};
      let hasNewAnswers = false;

      allQuestions.forEach((q: any) => {
        if (q.suggested_answer) {
          const suggestedAnswer = q.suggested_answer;

          // Ensure suggestedAnswer is a string
          if (typeof suggestedAnswer === 'string') {
            // For select/radio questions with options
            if (q.options && q.options.length > 0) {
              // Try to find exact match first
              const exactMatch = q.options.find((opt: string) => opt === suggestedAnswer);

              if (exactMatch) {
                // Exact match - use it
                initialAnswers[q.question_id] = exactMatch;
                if (!answers[q.question_id]) hasNewAnswers = true;
              } else {
                // Try to find match by removing number prefix (e.g., "6. ")
                const withoutNumber = suggestedAnswer.replace(/^\d+\.\s*/, '');
                const partialMatch = q.options.find((opt: string) => opt === withoutNumber);

                if (partialMatch) {
                  // Found match after stripping - use the actual option value
                  initialAnswers[q.question_id] = partialMatch;
                  if (!answers[q.question_id]) hasNewAnswers = true;
                }
                // If no match, don't add to initialAnswers (no pre-selection for this question)
              }
            } else {
              // For text/text_long questions, directly use the suggested answer
              initialAnswers[q.question_id] = suggestedAnswer;
              if (!answers[q.question_id]) hasNewAnswers = true;
            }
          }
        }
      });

      console.log("Pre-filling form with suggested answers:", initialAnswers);
      console.log("Current answers state:", answers);
      console.log("Has new answers to fill:", hasNewAnswers);

      // Always update if we have initial answers, even if answers state is not empty
      // This ensures fresh forms get pre-filled
      if (Object.keys(initialAnswers).length > 0 && hasNewAnswers) {
        setAnswers((prev) => ({
          ...prev,
          ...initialAnswers,
        }));
      }
    }
  }, [allQuestions.length, currentQuestion, status]);

  // Cycle through loading messages
  useEffect(() => {
    if (isSubmitting) {
      const interval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 2000); // Change message every 2 seconds

      return () => clearInterval(interval);
    } else {
      setLoadingMessageIndex(0); // Reset when not submitting
    }
  }, [isSubmitting, loadingMessages.length]);

  // Get saved questions for this session (memoized to prevent unnecessary re-renders)
  const savedQuestions = useMemo(
    () => rfqQuestionsForSession || {},
    [rfqQuestionsForSession]
  );

  // Build question history from Redux saved answers (primary) or collectedData (fallback)
  const questionHistory = useMemo(() => {
    console.log("Building question history...");
    console.log("Redux answers for session:", rfqAnswersForSession);
    console.log("Collected data from backend:", collectedData);
    console.log("Saved questions:", savedQuestions);

    // Primary: Use Redux saved answers (most reliable for dynamic questions)
    if (rfqAnswersForSession && rfqAnswersForSession.length > 0) {
      console.log("Using Redux answers to build history");
      const history = rfqAnswersForSession.map((answer) => ({
        questionId: answer.questionId,
        questionText: answer.questionText,
        answer: answer.answerText || answer.answer, // Use answerText (new) or answer (fallback for old data)
        answerType: answer.answerType,
      }));
      console.log("Built history from Redux:", history);
      return history;
    }

    // Fallback: Use backend collectedData if available
    if (collectedData && Object.keys(collectedData).length > 0) {
      // console.log("Using collectedData to build history");
      const history = Object.entries(collectedData).map(([questionId, answer]) => {
        // Look up the question from Redux saved questions
        const matchingQuestion = savedQuestions[questionId];

        const questionText = matchingQuestion?.question_text ||
          // Fallback: format the question ID nicely
          questionId
            .replace(/_/g, ' ')
            .replace(/^q\d+\s*/i, '')
            .replace(/\b\w/g, (l: string) => l.toUpperCase())
            .trim();

        // console.log(`Question ${questionId}: matched=${!!matchingQuestion}, text="${questionText}"`);

        return {
          questionId,
          questionText,
          answer: answer as string,
        };
      });

      return history;
    }

    console.log("No history available yet");
    return [];
  }, [rfqAnswersForSession, collectedData, savedQuestions]);

  console.log("Question History Length:", questionHistory.length, "Dynamic Position:", dynamicQuestionPosition);

  // Determine which question to display based on dynamicQuestionPosition
  // MUST be defined BEFORE any useEffects that use it
  const isViewingPreviousDynamicQuestion = dynamicQuestionPosition > 0;
  const displayedDynamicQuestion = useMemo(() => {
    // Don't show old cached question while fetching new one after answer change
    if (isFetchingNewQuestion && !isViewingPreviousDynamicQuestion) {
      return null;
    }

    if (!currentQuestion || questionHistory.length === 0) return currentQuestion;

    if (isViewingPreviousDynamicQuestion) {
      // Show historical question (from the end going backwards)
      const historyIndex = questionHistory.length - dynamicQuestionPosition;
      if (historyIndex >= 0 && historyIndex < questionHistory.length) {
        const historicalAnswer = questionHistory[historyIndex];
        // Get the full question details from saved questions
        const fullQuestion = savedQuestions[historicalAnswer.questionId];
        return fullQuestion || {
          question_id: historicalAnswer.questionId,
          question_text: historicalAnswer.questionText,
          question_type: historicalAnswer.answerType || 'text',
          options: [],
          is_required: true,
          help_text: null,
        };
      }
    }

    return currentQuestion;
  }, [currentQuestion, dynamicQuestionPosition, questionHistory, savedQuestions, isViewingPreviousDynamicQuestion, isFetchingNewQuestion]);

  const displayedDynamicAnswer = useMemo(() => {
    if (!isViewingPreviousDynamicQuestion) return currentAnswer;

    const historyIndex = questionHistory.length - dynamicQuestionPosition;
    if (historyIndex >= 0 && historyIndex < questionHistory.length) {
      return questionHistory[historyIndex].answer;
    }

    return currentAnswer;
  }, [isViewingPreviousDynamicQuestion, dynamicQuestionPosition, questionHistory, currentAnswer]);

  // Load saved answer from Redux when displayedDynamicQuestion changes (for back navigation in dynamic mode)
  useEffect(() => {
    if (displayedDynamicQuestion && sessionId && rfqAnswersForSession) {
      const savedAnswer = rfqAnswersForSession.find(
        (ans) => ans.questionId === displayedDynamicQuestion.question_id
      );

      if (savedAnswer) {
        // Check if the saved answer is one of the predefined options
        const isPreDefinedOption = displayedDynamicQuestion.options?.some(
          opt => opt === savedAnswer.answerText
        );

        // It's an "Other" answer if it's NOT a predefined option
        // AND there is an "Other" option available
        const otherOption = displayedDynamicQuestion.options?.find(opt =>
          opt.toLowerCase().includes("other")
        );
        const isOtherAnswer = !isPreDefinedOption && otherOption;

        if (isOtherAnswer) {
          // Set the "Other" option as selected
          setCurrentAnswer(otherOption);
          setAnswers((prev) => ({
            ...prev,
            [displayedDynamicQuestion.question_id]: otherOption,
          }));
          setCurrentCustomAnswer(savedAnswer.answerText);
          setCustomAnswers((prev) => ({
            ...prev,
            [displayedDynamicQuestion.question_id]: savedAnswer.answerText,
          }));
        } else {
          // Regular answer - it's one of the predefined options
          setCurrentAnswer(savedAnswer.answerText);
          setCurrentCustomAnswer("");
          setAnswers((prev) => ({
            ...prev,
            [displayedDynamicQuestion.question_id]: savedAnswer.answerText,
          }));
        }
      } else if (!isViewingPreviousDynamicQuestion) {
        // No saved answer and viewing current question, clear the state
        setCurrentAnswer("");
        setCurrentCustomAnswer("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedDynamicQuestion?.question_id, sessionId, dynamicQuestionPosition]);

  // Check if message contains validation results
  const hasValidationResults = message.includes("## 🔍 Validation Results") ||
    message.includes("Validation Results") ||
    message.includes("You can proceed with these warnings");

  // Handle both old (all_questions array) and new (current_question) approaches
  // Also consider viewing historical questions
  const displayQuestion = isViewingPreviousDynamicQuestion
    ? displayedDynamicQuestion
    : (currentQuestion || allQuestions[currentQuestionIndex]);

  // For displayAnswer: when viewing history, use current answers state (for user changes)
  // falling back to historical answer if no current selection
  const displayAnswer = isViewingPreviousDynamicQuestion
    ? (answers[displayedDynamicQuestion?.question_id] || displayedDynamicAnswer)
    : (currentQuestion
        ? currentAnswer
        : (answers[allQuestions[currentQuestionIndex]?.question_id] || ""));
  const displayCustomAnswer = isViewingPreviousDynamicQuestion
    ? (customAnswers[displayedDynamicQuestion?.question_id] || "")
    : (currentQuestion
        ? currentCustomAnswer
        : (customAnswers[allQuestions[currentQuestionIndex]?.question_id] || ""));

  // Progress calculation
  const progress = totalQuestions > 0
    ? ((currentQuestionIndex + 1) / totalQuestions) * 100
    : 0;
  const answeredCount = currentQuestion
    ? answeredQuestionsCount
    : Object.keys(answers).length;

  // Handlers
  const handleAnswerSelect = (questionId: string, value: string) => {
    if (currentQuestion) {
      // Dynamic question mode - update local state only
      setCurrentAnswer(value);
      // Clear custom answer if switching away from "Other"
      if (!value.toLowerCase().includes("other")) {
        setCurrentCustomAnswer("");
      }
      setAnswers((prev) => ({
        ...prev,
        [questionId]: value,
      }));

    } else {
      // Old mode - update answers map
      setAnswers((prev) => ({
        ...prev,
        [questionId]: value,
      }));
      // Clear custom answer if switching away from "Other"
      if (!value.toLowerCase().includes("other") && customAnswers[questionId]) {
        setCustomAnswers((prev) => {
          const updated = { ...prev };
          delete updated[questionId];
          return updated;
        });
      }
    }
  };

  const handleCustomAnswerChange = (questionId: string, value: string) => {
    if (currentQuestion) {
      // Dynamic question mode
      setCurrentCustomAnswer(value);
      setCustomAnswers((prev) => ({
        ...prev,
        [questionId]: value,
      }));
    } else {
      // Old mode
      setCustomAnswers((prev) => ({
        ...prev,
        [questionId]: value,
      }));
    }
  };

  const handleNext = () => {
    // Save current answer to Redux before moving forward (if in old question mode)
    if (allQuestions.length > 0 && currentQuestionIndex >= 0 && sessionId) {
      const question = allQuestions[currentQuestionIndex];
      const answer = answers[question?.question_id];

      if (question && answer) {
        const finalAnswer = answer.toLowerCase().includes("other") && customAnswers[question.question_id]
          ? customAnswers[question.question_id]
          : answer;

        dispatch(
          saveRfqAnswer({
            sessionId,
            answer: {
              questionId: question.question_id,
              questionText: question.question_text,
              answerText: finalAnswer,
              answerType: question.question_type,
            },
          }),
        );
      }
    }

    // Navigate to next question
    if (currentQuestionIndex < allQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    // Save current answer to Redux before going back
    if (sessionId) {
      // Dynamic question mode
      if (currentQuestion && currentAnswer) {
        const finalAnswer = currentAnswer.toLowerCase().includes("other") && currentCustomAnswer
          ? currentCustomAnswer
          : currentAnswer;

        dispatch(
          saveRfqAnswer({
            sessionId,
            answer: {
              questionId: currentQuestion.question_id,
              questionText: currentQuestion.question_text,
              answerText: finalAnswer,
              answerType: currentQuestion.question_type,
            },
          }),
        );
      }
      // Old mode with question index
      else if (allQuestions.length > 0 && currentQuestionIndex >= 0) {
        const question = allQuestions[currentQuestionIndex];
        const answer = answers[question?.question_id];

        if (question && answer) {
          const finalAnswer = answer.toLowerCase().includes("other") && customAnswers[question.question_id]
            ? customAnswers[question.question_id]
            : answer;

          dispatch(
            saveRfqAnswer({
              sessionId,
              answer: {
                questionId: question.question_id,
                questionText: question.question_text,
                answerText: finalAnswer,
                answerType: question.question_type,
              },
            }),
          );
        }
      }
    }

    // Navigate to previous question
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  // Handle going back in dynamic Q&A
  const handleDynamicQuestionBack = () => {
    if (questionHistory.length > 0) {
      setDynamicQuestionPosition((prev) => Math.min(prev + 1, questionHistory.length));
    }
  };

  // Handle going forward to current question in dynamic Q&A
  const handleDynamicQuestionForward = () => {
    setDynamicQuestionPosition(0);
  };

  // NEW: Handle dynamic single answer submission
  const handleSubmitDynamicAnswer = async (answerOverride?: string) => {
    const answerToSubmit = answerOverride || displayedDynamicAnswer;
    if (!displayedDynamicQuestion || !answerToSubmit) {
      setSubmitError("Please select an answer before continuing.");
      return;
    }

    // If we're submitting from a previous question, clear all answers after that point
    if (isViewingPreviousDynamicQuestion && sessionId && rfqAnswersForSession) {
      const clearFromIndex = questionHistory.length - dynamicQuestionPosition;
      // Remove answers from Redux after this position
      const answersToKeep = rfqAnswersForSession.slice(0, clearFromIndex);
      // Clear the entire answers array and rebuild with only kept answers
      dispatch(clearRfqData(sessionId));
      answersToKeep.forEach((ans) => {
        dispatch(saveRfqAnswer({ sessionId, answer: ans }));
      });
    }

    // Reset position to current
    setDynamicQuestionPosition(0);

    // If we're re-submitting from a historical step, clear all subsequent steps
    if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
      dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
    }

    // Defensive checks
    if (!selectedAgent) {
      console.error("selectedAgent is null or undefined");
      setSubmitError("Agent not found. Please refresh the page.");
      return;
    }

    if (!sessionId) {
      console.error("sessionId is null or undefined");
      setSubmitError("Session not found. Please start a new conversation.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Determine final answer value
      let finalAnswer = answerToSubmit;
      if (answerToSubmit.toLowerCase().includes("other") && currentCustomAnswer) {
        finalAnswer = currentCustomAnswer;
      }

      // Validate required fields before sending
      if (!sessionId) {
        throw new Error("session_id is missing");
      }
      if (!currentQuestion.question_id) {
        throw new Error("question_id is missing");
      }
      if (!finalAnswer || finalAnswer.trim() === "") {
        throw new Error("answer is missing or empty");
      }

      // Prepare the answer submission data with special marker for display
      const answerData = {
        action: "provide_answer",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        question_id: displayedDynamicQuestion.question_id,
        answer: finalAnswer,
        __display_answer: finalAnswer, // Special field for UI display
      };

      // Send via Redux - this will handle SSE streaming and add agent response to chat
      const timestamp = new Date().toISOString();
      const questionId = uuid();

      await dispatch(
        sendMessageToAgent({
          agentId: "rfq_builder",
          message: JSON.stringify(answerData),
          questionId,
          timestamp,
          agentEndpoint: "/stream/rfq-builder",
          sessionId: sessionIdFromUrl,
        }),
      ).unwrap();

      console.log("Dynamic answer submitted successfully");

      // Save the answer to Redux
      if (sessionId && displayedDynamicQuestion) {
        console.log("Saving answer to Redux:", {
          sessionId,
          questionId: displayedDynamicQuestion.question_id,
          answerText: finalAnswer,
        });
        dispatch(
          saveRfqAnswer({
            sessionId,
            answer: {
              questionId: displayedDynamicQuestion.question_id,
              questionText: displayedDynamicQuestion.question_text,
              answerText: finalAnswer,
              answerType: displayedDynamicQuestion.question_type,
            },
          }),
        );
        console.log("Dispatched saveRfqAnswer for", displayedDynamicQuestion.question_id);
      } else {
        console.error("Cannot save answer - missing sessionId or displayedDynamicQuestion:", {
          sessionId,
          displayedDynamicQuestion,
        });
      }

      // Clear current answer for next question
      setCurrentAnswer("");
      setCurrentCustomAnswer("");
    } catch (error: any) {
      console.error("Error submitting dynamic answer:", error);

      // Provide more detailed error message
      let errorMessage = "Failed to submit answer. Please try again.";
      if (error.message) {
        errorMessage = error.message;
      }
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitAllAnswers = async () => {
    // Check if we're viewing a previous question from history
    if (isViewingPreviousDynamicQuestion && sessionId && displayQuestion) {
      const currentQuestionAnswer = answers[displayQuestion.question_id];
      const savedAnswer = rfqAnswersForSession?.find(
        (ans) => ans.questionId === displayQuestion.question_id
      );

      // Get the final answer value (handle "Other" option)
      const finalAnswer = currentQuestionAnswer?.toLowerCase().includes("other") && customAnswers[displayQuestion.question_id]
        ? customAnswers[displayQuestion.question_id]
        : currentQuestionAnswer;

      // Check if answer has changed
      const answerChanged = savedAnswer && finalAnswer !== savedAnswer.answerText;

      if (!answerChanged) {
        // Answer hasn't changed - just navigate forward through history (no API call)
        console.log("Navigating forward through history - no API call needed");
        setDynamicQuestionPosition((prev) => Math.max(prev - 1, 0));
        return;
      } else {
        // Answer has changed - need to call API and clear subsequent answers
        console.log("Answer changed while viewing history - calling API");

        // Set flag to prevent showing old cached question while fetching new one
        setIsFetchingNewQuestion(true);

        // Calculate the index of the current question in history
        // historyIndex is the position in the array (0-indexed)
        const historyIndex = questionHistory.length - dynamicQuestionPosition - 1;
        console.log("Clearing answers after index:", historyIndex);

        // Clear all answers AFTER the current question (keep current and before)
        dispatch(
          clearRfqAnswersAfter({
            sessionId,
            questionIndex: historyIndex,
          }),
        );

        // Update the current answer in Redux
        dispatch(
          saveRfqAnswer({
            sessionId,
            answer: {
              questionId: displayQuestion.question_id,
              questionText: displayQuestion.question_text,
              answerText: finalAnswer,
              answerType: displayQuestion.question_type,
            },
          }),
        );

        // Reset position to current (API will return new question)
        setDynamicQuestionPosition(0);
      }
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      // Save current question and answer to Redux BEFORE submitting to backend
      // This allows the Back button to navigate through answered questions
      if (sessionId && displayQuestion) {
        // Save the question details
        dispatch(
          saveRfqQuestion({
            sessionId,
            question: displayQuestion,
          }),
        );

        const currentQuestionAnswer = answers[displayQuestion.question_id];
        if (currentQuestionAnswer) {
          const finalAnswer = currentQuestionAnswer.toLowerCase().includes("other") && customAnswers[displayQuestion.question_id]
            ? customAnswers[displayQuestion.question_id]
            : currentQuestionAnswer;

          dispatch(
            saveRfqAnswer({
              sessionId,
              answer: {
                questionId: displayQuestion.question_id,
                questionText: displayQuestion.question_text,
                answerText: finalAnswer,
                answerType: displayQuestion.question_type,
              },
            }),
          );
          console.log("Saved question and answer to Redux before submission:", displayQuestion.question_id, finalAnswer);
        }
      }

      // Format answers for submission
      const formattedAnswers: {
        question_id: string,
        answer: string
      } = {
        question_id: "",
        answer: ""
      };
      console.log("answers", answers)
      Object.keys(answers).forEach((questionId) => {
        const answer = answers[questionId];
        const customAnswer = customAnswers[questionId];
        formattedAnswers.question_id = questionId

        // If "Other" was selected and there's a custom answer, use the custom answer
        if (answer.toLowerCase().includes("other") && customAnswer) {
          formattedAnswers.answer = customAnswer;
        } else {
          formattedAnswers.answer = answer;
        }
      });

      // Prepare the answer submission data
      const answerData = {
        action: nextAction,
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        answers: formattedAnswers,
      };

      // Send via Redux - this will handle SSE streaming and add agent response to chat
      const timestamp = new Date().toISOString();
      const questionId = uuid();

      await dispatch(
        sendMessageToAgent({
          agentId: "rfq_builder",
          message: JSON.stringify(answerData),
          questionId,
          timestamp,
          agentEndpoint: "/stream/rfq-builder",
          sessionId: sessionIdFromUrl,
        }),
      ).unwrap();

      // console.log("Answers submitted successfully");
      // Don't set submitSuccess - backend will respond with awaiting_confirmation status
    } catch (error: any) {
      console.error("Error submitting answers:", error);
      setSubmitError(
        error.message || "Failed to submit answers. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle document download
  // Handle confirmation of collected data
  const handleConfirmData = async () => {
    setIsConfirming(true);
    setSubmitError(null);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      // Merge edited data with collected data
      const finalData = {
        ...collectedData,
        ...editedData,
      };

      const confirmData = {
        action: "confirm_data",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        collected_data: finalData,
      };

      const timestamp = new Date().toISOString();
      const questionId = uuid();

      await dispatch(
        sendMessageToAgent({
          agentId: "rfq_builder",
          message: JSON.stringify(confirmData),
          questionId,
          timestamp,
          agentEndpoint: "/stream/rfq-builder",
          sessionId: sessionIdFromUrl,
        }),
      ).unwrap();

      // console.log("Data confirmed successfully");
    } catch (error: any) {
      console.error("Error confirming data:", error);
      setSubmitError(
        error.message || "Failed to confirm data. Please try again.",
      );
    } finally {
      setIsConfirming(false);
    }
  };

  // Handle field edit
  const handleFieldEdit = (fieldId: string, newValue: string) => {
    setEditedData((prev) => ({
      ...prev,
      [fieldId]: newValue,
    }));
  };

  // Handle updating a previous answer
  const handleUpdateAnswer = async (questionId: string, newAnswer: string) => {
    setUpdatingQuestionId(questionId);
    setSubmitError(null);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      // Get the question details from saved questions
      const question = savedQuestions[questionId];
      if (!question) {
        setSubmitError("Question not found");
        return;
      }

      // Prepare the answer submission data
      const answerData = {
        action: "provide_answer",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        question_id: questionId,
        answer: newAnswer,
      };

      const timestamp = new Date().toISOString();
      const msgQuestionId = uuid();

      await dispatch(
        sendMessageToAgent({
          agentId: "rfq_builder",
          message: JSON.stringify(answerData),
          questionId: msgQuestionId,
          timestamp,
          agentEndpoint: "/stream/rfq-builder",
          sessionId: sessionIdFromUrl,
        }),
      ).unwrap();

      // console.log("Updated answer submitted successfully");

      // Update Redux with new answer
      if (sessionId) {
        dispatch(
          saveRfqAnswer({
            sessionId,
            answer: {
              questionId,
              questionText: question.question_text,
              answer: newAnswer,
              timestamp,
            },
          }),
        );
        // console.log("Updated answer saved to Redux for", questionId);
      }

      // Clear edited state for this question
      setEditedAnswers((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });

      // Optionally return to current question after update
      // setIsViewingHistory(false);
    } catch (error: any) {
      console.error("Error updating answer:", error);
      setSubmitError(
        error.message || "Failed to update answer. Please try again.",
      );
    } finally {
      setUpdatingQuestionId(null);
    }
  };

  const handleDownload = async (documentType: "sow" | "evaluation" | "evaluation_file" = "sow", specificFilename?: string) => {
    let filename: string | null = null;
    let urlToDownload: string | null = null;

    if (documentType === "sow") {
      filename = generatedDocumentPath
        ? generatedDocumentPath.split(/[\\\/]/).pop()
        : null;

      // If filename doesn't have extension, construct it
      if (filename && !filename.includes('.')) {
        filename = `SOW_${sessionId}.docx`;
      }

      if (!filename) {
        alert("SOW document information is not available.");
        return;
      }

      // Determine download URL for SOW
      urlToDownload = downloadUrlFromBackend;
      if (!urlToDownload && sessionId) {
        urlToDownload = `/rfq/download/${sessionId}`;
      }
    } else if (documentType === "evaluation_file") {
      // For individual evaluation files
      if (specificFilename && sessionId) {
        // Safety check: if specificFilename is a full path, extract just the filename
        filename = specificFilename;
        if (specificFilename.startsWith('/') || specificFilename.includes('\\')) {
          filename = specificFilename.split(/[\\\/]/).pop() || specificFilename;
        }
        // Backend endpoint pattern: /rfq/download/{session_id}/{filename}
        urlToDownload = `/rfq/download/${sessionId}/${filename}`;
      }
    } else if (documentType === "evaluation") {
      // For evaluation document (legacy/fallback)
      filename = specificFilename || `Evaluation_${sessionId}.pdf`;

      if (sessionId) {
        urlToDownload = `/rfq/download/${sessionId}/evaluation`;
      }
    }

    if (!urlToDownload) {
      alert("Download URL is not available. Please contact support.");
      return;
    }

    setIsDownloading(true);

    try {
      const response = await axiosClient.get(urlToDownload, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Record download action - fire and forget (non-blocking)
      dispatch(
        recordAgentAction({
          agentId: numericAgentId,
          actionType: "download",
          content: JSON.stringify({
            documentType,
            filename: filename || "download",
            sessionId,
            timestamp: new Date().toISOString(),
          }),
        })
      );
    } catch (error) {
      console.error(`Error downloading ${documentType} document:`, error);
      alert(`Failed to download the document. Please try again or contact support if the issue persists.`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePreview = async (filename: string) => {
    console.log("handlePreview called with:", { filename, sessionId });

    if (!sessionId || !filename) {
      console.error("Missing sessionId or filename:", { sessionId, filename });
      alert("Cannot preview: Session information is missing");
      return;
    }

    // Safety check: if filename is actually a full path, extract just the filename
    let cleanFilename = filename;
    if (filename.startsWith('/') || filename.includes('\\')) {
      cleanFilename = filename.split(/[\\\/]/).pop() || filename;
      console.log("Extracted clean filename from path:", cleanFilename);

      // If still no extension after extraction, construct proper filename
      if (!cleanFilename.includes('.')) {
        cleanFilename = `SOW_${sessionId}.docx`;
        console.log("Constructed filename:", cleanFilename);
      }
    }

    console.log("Opening preview modal...");
    setLoadingPreview(true);
    setPreviewModalOpen(true);
    setPreviewHtml(""); // Reset HTML content

    try {
      const urlToDownload = `/rfq/download/${sessionId}/${cleanFilename}`;
      console.log("Fetching document from:", urlToDownload);

      const response = await axiosClient.get(urlToDownload, {
        responseType: "blob",
      });

      console.log("Document fetched successfully, response:", response);

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });
      const blobUrl = window.URL.createObjectURL(blob);

      // Determine file type using clean filename
      const fileExtension = cleanFilename.split('.').pop()?.toLowerCase();
      console.log("File extension detected:", fileExtension);
      let fileType = "document";
      let htmlContent = "";

      if (fileExtension === "xlsx" || fileExtension === "xls") {
        fileType = "excel";

        // Convert Excel to HTML using XLSX
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });

        // Process all sheets
        let allSheetsHtml = "";
        workbook.SheetNames.forEach((sheetName, index) => {
          const worksheet = workbook.Sheets[sheetName];
          const sheetHtml = XLSX.utils.sheet_to_html(worksheet);
          allSheetsHtml += `
            <div style="margin-bottom: 30px;">
              <h3 style="background: #00a888; color: white; padding: 10px; margin: 0; font-family: Arial, sans-serif;">
                ${sheetName}
              </h3>
              ${sheetHtml}
            </div>
          `;
        });

        htmlContent = `
          <style>
            table { border-collapse: collapse; width: 100%; margin-bottom: 20px; font-family: Arial, sans-serif; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f0fbf9; font-weight: 600; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            tr:hover { background-color: #f0fbf9; }
          </style>
          ${allSheetsHtml}
        `;

        setPreviewHtml(htmlContent);
      } else if (fileExtension === "docx" || fileExtension === "doc") {
        fileType = "word";

        // Convert Word to HTML using Mammoth
        const arrayBuffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer: arrayBuffer });
        htmlContent = `
          <style>
            body { font-family: 'Calibri', Arial, sans-serif; line-height: 1.6; }
            p { margin: 10px 0; }
            h1, h2, h3, h4, h5, h6 { margin-top: 20px; margin-bottom: 10px; }
            table { border-collapse: collapse; width: 100%; margin: 10px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; }
            th { background-color: #f0fbf9; }
          </style>
          <div style="padding: 20px; max-width: 800px; margin: 0 auto;">
            ${result.value}
          </div>
        `;

        setPreviewHtml(htmlContent);
      } else if (fileExtension === "pdf") {
        fileType = "pdf";
      }

      setPreviewFile({
        name: cleanFilename,
        url: blobUrl,
        type: fileType
      });
      console.log("Preview file set:", { name: cleanFilename, type: fileType });
    } catch (error) {
      console.error("Error loading preview:", error);
      console.error("Failed for filename:", cleanFilename);
      console.error("File extension:", cleanFilename.split('.').pop());
      alert("Failed to load preview. You can still download the document.");
      setPreviewModalOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    if (previewFile?.url) {
      window.URL.revokeObjectURL(previewFile.url);
    }
    setPreviewModalOpen(false);
    setPreviewFile(null);
    setPreviewHtml("");
  };

  // Wrapper functions for document preview/download
  const handlePreviewDocument = async (documentPath: string) => {
    let filename = documentPath.split(/[\\\/]/).pop();

    // If filename doesn't have an extension, it's likely just a session ID
    // Construct the proper filename based on context
    if (filename && !filename.includes('.')) {
      // For SOW documents, construct the filename
      if (status === 'asking_step5_questions' || documentPath.includes('sow')) {
        filename = `SOW_${sessionId}.docx`;
      } else {
        // For other documents, try to construct based on session ID
        filename = `${filename}.docx`;
      }
    }

    if (filename) {
      await handlePreview(filename);
    }
  };

  const handleDownloadDocument = async (documentPath: string) => {
    let filename = documentPath.split(/[\\\/]/).pop();

    // If filename doesn't have an extension, it's likely just a session ID
    // Construct the proper filename based on context
    if (filename && !filename.includes('.')) {
      // For SOW documents, construct the filename
      if (status === 'asking_step5_questions' || documentPath.includes('sow')) {
        filename = `SOW_${sessionId}.docx`;
      } else {
        // For other documents, try to construct based on session ID
        filename = `${filename}.docx`;
      }
    }

    if (filename) {
      // Determine document type based on file extension or filename
      if (filename.includes('TECHNICAL_TEMPLATE') || filename.includes('EVALUATION_CRITERIA')) {
        await handleDownload("evaluation_file", filename);
      } else if (filename.includes('RFQ_DOCUMENT')) {
        await handleDownload("evaluation_file", filename);
      } else if (filename.includes('SOW')) {
        await handleDownload("sow", filename);
      } else {
        await handleDownload("evaluation_file", filename);
      }
    }
  };

  // Validation
  const isCurrentQuestionAnswered = currentQuestion
    ? (!!currentAnswer && (!currentAnswer.toLowerCase().includes("other") || !!currentCustomAnswer))
    : (!!answers[allQuestions[currentQuestionIndex]?.question_id]);
  const canSubmit = !currentQuestion && answeredCount === totalQuestions;

  // Handle Go Back functionality - Navigate to previous step using Redux history
  const handleGoBack = () => {
    if (sessionId && rfqCurrentStepNumber && rfqCurrentStepNumber > 1) {
      dispatch(goBackRfqStep(sessionId));
    }
  };

  // Handle Forward functionality - Return to the latest step
  const handleGoForward = () => {
    if (sessionId && backendStepInfo) {
      dispatch(
        saveRfqStep({
          sessionId,
          stepNumber: backendStepInfo.stepNumber,
          stepName: backendStepInfo.stepName,
          response: parsedContent?.finalAnswer || parsedContent?.result?.data || parsedContent,
        })
      );
    }
  };

  // Handle Step Reset - Navigate to a previous step in the wizard
  const handleResetToStep = async (sectionId: string) => {
    const targetStep = SECTION_TO_STEP_MAP[sectionId];

    if (!targetStep) {
      console.error("Invalid sectionId - no targetStep found:", sectionId);
      return;
    }

    if (!workflowId) {
      console.error("No workflowId available - cannot reset step");
      alert("Unable to navigate: workflow ID not found. Please try refreshing.");
      return;
    }

    setIsResettingStep(true);

    try {
      // Call the reset-step endpoint
      const response = await axiosClient.post('/stream/rfq-builder/reset-step', {
        workflow_id: workflowId,
        target_step: targetStep,
      });

      if (response.data?.status === 'reset') {
        // Clear local state before triggering new step
        setAnswers({});
        setCurrentAnswer("");
        setCurrentCustomAnswer("");
        setCustomAnswers({});
        setDynamicQuestionPosition(0);
        setCurrentQuestionIndex(0);

        // After successful reset, trigger the appropriate step's API call
        const action = STEP_ACTION_MAP[targetStep];

        // Trigger the appropriate step's API call
        const payload = {
          action,
          session_id: sessionId,
          chat_id: chatId,
          workflow_id: response.data.workflow_id || workflowId,
        };

        await dispatch(
          sendMessageToAgent({
            message: JSON.stringify(payload),
            agentId: "rfq_builder",
            agentEndpoint: "/stream/rfq-builder",
          })
        );
      }
    } catch (error) {
      console.error("Error resetting to step:", error);
      alert("Failed to reset to step. Please try again.");
    } finally {
      setIsResettingStep(false);
    }
  };

  // Handle Step 4 SOW Questions navigation
  const handleStep4Next = () => {
    if (currentQuestionIndex < allQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handleStep4Back = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  // Handle Step 1: Categorization
  const handleProceedToStep2 = async () => {
    setIsProceedingToStep2(true);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      const payload = {
        action: nextAction || "proceed_to_step2", // Use next_action from backend
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        // Backend expects flat structure, not nested in updated_data
        category: categorizationData.category,
        delivery_location: categorizationData.delivery_location,
        client_company: categorizationData.client_company,
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error proceeding to step 2:", error);
      alert("Failed to proceed. Please try again.");
    } finally {
      setIsProceedingToStep2(false);
    }
  };

  // Handle Step 2: Initial Questions (NEW step added by backend)
  const handleProceedToGoodsServices = async () => {
    setIsProceedingToGoodsServices(true);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      const payload = {
        action: nextAction || "proceed_to_goods_services_questions",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        // Backend expects 'initial_answers'
        initial_answers: step2InitialAnswers,
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error proceeding to goods/services questions:", error);
      alert("Failed to proceed. Please try again.");
    } finally {
      setIsProceedingToGoodsServices(false);
    }
  };

  // Handle Step 2: RFQ Questions
  const handleProceedToStep3 = async () => {
    setIsProceedingToStep3(true);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      const payload = {
        action: nextAction || "proceed_to_step3", // Use next_action from backend
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        // Backend expects 'rfq_answers', not 'answers'
        rfq_answers: step2Answers,
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error proceeding to step 3:", error);
      alert("Failed to proceed. Please try again.");
    } finally {
      setIsProceedingToStep3(false);
    }
  };

  // Handle Step 3: Specific Questions (Goods/Service questions)
  const handleProceedToSowQuestions = async () => {
    setIsProceedingToSow(true);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      const payload = {
        action: nextAction || "proceed_to_sow_questions",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        goods_service_answers: step3SpecificAnswers,
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error proceeding to SOW questions:", error);
      alert("Failed to proceed. Please try again.");
    } finally {
      setIsProceedingToSow(false);
    }
  };

  // Handle Step 4: Generate SOW Document
  const handleGenerateSowDocument = async () => {
    setIsGeneratingSowDocument(true);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      const payload = {
        action: nextAction || "generate_sow_document",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        sow_answers: step4SowAnswers,
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error generating SOW document:", error);
      alert("Failed to generate document. Please try again.");
    } finally {
      setIsGeneratingSowDocument(false);
    }
  };

  // Handle Evaluation Offer Answer (Yes/No)
  const handleSubmitEvaluationAnswer = async () => {
    if (!evaluationAnswer) return;

    setIsSubmittingEvaluation(true);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      const payload = {
        action: "submit_answers",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        answers: {
          wants_evaluation: evaluationAnswer,
        },
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error submitting evaluation answer:", error);
      alert("Failed to submit. Please try again.");
    } finally {
      setIsSubmittingEvaluation(false);
    }
  };

  // Handle Step 5 Answer Submission
  const handleSubmitStep5Answer = async () => {
    if (!step5Answer) return;

    setIsSubmittingStep5(true);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      const question = allQuestions[0];
      const payload = {
        action: nextAction || "submit_answers",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        answers: {
          [question.question_id]: step5Answer,
        },
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error submitting step 5 answer:", error);
      alert("Failed to submit. Please try again.");
    } finally {
      setIsSubmittingStep5(false);
    }
  };

  // Handle Technical Evaluation Answer Submission
  const handleSubmitTechEvalAnswer = async () => {
    if (!techEvalAnswer) return;

    setIsSubmittingTechEval(true);

    try {
      const question = allQuestions[0];
      const payload = {
        action: nextAction || "submit_answers",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        answers: {
          [question.question_id]: techEvalAnswer,
        },
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error submitting technical evaluation answer:", error);
      alert("Failed to submit. Please try again.");
    } finally {
      setIsSubmittingTechEval(false);
    }
  };

  // Handle Evaluation Type Selection
  const handleSubmitEvaluationType = async () => {
    if (!evaluationTypeAnswer) return;

    setIsSubmittingEvaluationType(true);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      const payload = {
        action: "submit_answers",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        answers: {
          evaluation_type: evaluationTypeAnswer,
        },
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error submitting evaluation type:", error);
      alert("Failed to submit. Please try again.");
    } finally {
      setIsSubmittingEvaluationType(false);
    }
  };

  // Handle adding a new custom criterion
  const handleAddCriterion = () => {
    const newCriterion: CustomCriterion = {
      id: `custom_criterion_${Date.now()}`,
      title: "",
      description: "",
      weight: "",
    };
    setCustomCriteria([...customCriteria, newCriterion]);
  };

  // Handle deleting a criterion (original or custom)
  const handleDeleteCriterion = (criterionId: string, isCustom: boolean) => {
    if (isCustom) {
      setCustomCriteria(customCriteria.filter(c => c.id !== criterionId));
    } else {
      setDeletedCriterionIds(new Set([...deletedCriterionIds, criterionId]));
    }
  };

  // Handle updating a custom criterion
  const handleUpdateCustomCriterion = (id: string, field: keyof CustomCriterion, value: string) => {
    setCustomCriteria(customCriteria.map(c =>
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  // Handle Techno-Commercial Answers Submission
  const handleSubmitTechnoCommercial = async () => {
    setIsSubmittingTechnoCommercial(true);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      // Build evaluation_answers excluding deleted criteria
      const evaluationAnswers: Record<string, string> = {};

      // Copy answers for non-deleted questions
      Object.entries(technoCommercialAnswers).forEach(([questionId, answer]) => {
        if (!deletedCriterionIds.has(questionId)) {
          evaluationAnswers[questionId] = answer;
        }
      });

      // Add criterion titles for questions with editable_label (excluding deleted)
      allQuestions.forEach((question: Question) => {
        if (question.editable_label !== undefined && question.editable_label !== null) {
          if (!deletedCriterionIds.has(question.question_id)) {
            // Use edited title if available, otherwise use original editable_label
            const titleValue = criterionTitles[question.question_id] ?? question.editable_label;
            evaluationAnswers[`${question.question_id}_title`] = titleValue;
          }
        }
      });

      // Add custom criteria to answers
      customCriteria.forEach((criterion, index) => {
        evaluationAnswers[criterion.id] = criterion.description;
        evaluationAnswers[`${criterion.id}_title`] = criterion.title;
        evaluationAnswers[`${criterion.id}_weight`] = criterion.weight;
      });

      const payload = {
        action: nextAction || "submit_techno_commercial_answers",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        evaluation_answers: evaluationAnswers,
        deleted_criteria: Array.from(deletedCriterionIds),
        custom_criteria: customCriteria.map(c => ({
          id: c.id,
          title: c.title,
          description: c.description,
          weight: c.weight,
        })),
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error submitting techno-commercial answers:", error);
      alert("Failed to submit. Please try again.");
    } finally {
      setIsSubmittingTechnoCommercial(false);
    }
  };

  // Handle RFQ Package Answer Submission
  const handleSubmitRfqPackage = async () => {
    if (!rfqPackageAnswer) return;

    setIsSubmittingRfqPackage(true);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      const payload = {
        action: "submit_answers",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        answers: {
          wants_rfq_package: rfqPackageAnswer,
        },
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error submitting RFQ package answer:", error);
      alert("Failed to submit. Please try again.");
    } finally {
      setIsSubmittingRfqPackage(false);
    }
  };

  // Handle RFQ Package Form Data Submission
  const handleSubmitRfqPackageForm = async () => {
    setIsSubmittingRfqPackageForm(true);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      const payload = {
        action: nextAction || "submit_rfq_package_answers",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        rfq_package_answers: rfqPackageFormAnswers,
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
          questionId: uuid(),
          timestamp: new Date().toISOString(),
          sessionId,
        })
      );
    } catch (error) {
      console.error("Error submitting RFQ package form answers:", error);
      alert("Failed to submit. Please try again.");
    } finally {
      setIsSubmittingRfqPackageForm(false);
    }
  };

  // Handlers for Goods/Service line items
  const handleAddItem = () => {
    setLineItems([
      ...lineItems,
      {
        id: uuid(),
        title: "",
        manufacturer_part_number: "",
        quantity: "",
        uom: "",
        unit_price: "",
        start_date: "",
        end_date: "",
      },
    ]);
  };

  const handleUpdateItem = (id: string, field: keyof LineItem, value: string) => {
    console.log(`Updating item ${id}, field: ${field}, value:`, value);
    setLineItems(
      lineItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handleRemoveItem = (id: string) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((item) => item.id !== id));
    }
  };

  const handleProcurementTypeChange = (newType: "Goods" | "Service") => {
    console.log("Procurement type changing from", procurementType, "to", newType);
    setProcurementType(newType);

    // Check if there are suggested answers for the new type
    const questionId = newType === "Service" ? "services_line_items" : "goods_line_items";
    const lineItemsQuestion = allQuestions.find((q: any) =>
      (q.question_id === questionId || q.question_type === "line_items") &&
      q.suggested_answer &&
      Array.isArray(q.suggested_answer) &&
      q.suggested_answer.length > 0
    );

    if (lineItemsQuestion) {
      // Pre-fill with suggested answers for the new type
      const suggestedItems = lineItemsQuestion.suggested_answer.map((item: any) => ({
        id: uuid(),
        title: item.item_name || item.service_description || "",
        manufacturer_part_number: item.manufacturer_part_number || "",
        quantity: String(item.quantity || ""),
        uom: item.unit ? item.unit.toLowerCase() : "each",
        unit_price: String(item.unit_price || ""),
        start_date: item.start_date || "",
        end_date: item.end_date || "",
      }));
      console.log(`Pre-filling ${newType} with suggested answers:`, suggestedItems);
      setLineItems(suggestedItems);
    } else {
      // No suggested answers, reset to empty
      setLineItems([
        {
          id: uuid(),
          title: "",
          manufacturer_part_number: "",
          quantity: "",
          uom: "",
          unit_price: "",
          start_date: "",
          end_date: "",
        },
      ]);
    }
  };

  const calculateTotal = () => {
    return lineItems.reduce((total, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unit_price) || 0;
      return total + quantity * unitPrice;
    }, 0);
  };

  const handleSubmitGoodsForm = async () => {
    setIsProceedingToStep3(true);

    try {
      // If we're re-submitting from a historical step, clear all subsequent steps
      if (sessionId && isViewingHistoricalStep && rfqCurrentStepNumber) {
        dispatch(clearRfqStepsAfter({ sessionId, stepNumber: rfqCurrentStepNumber }));
      }

      // Format line items as answers (include dates for Services)
      const rfq_answers = {
        procurement_type: procurementType === "Service" ? "Services" : procurementType, // Backend expects "Services" plural
        line_items: lineItems.map(item => {
          const baseItem: any = {
            title: item.title,
            quantity: item.quantity,
            uom: item.uom,
            unit_price: item.unit_price,
          };

          // Add manufacturer_part_number only for Goods
          if (procurementType === "Goods") {
            baseItem.manufacturer_part_number = item.manufacturer_part_number;
          }

          // Add start_date and end_date only for Services
          if (procurementType === "Service") {
            baseItem.start_date = item.start_date;
            baseItem.end_date = item.end_date;
          }

          return baseItem;
        }),
      };

      const payload = {
        action: nextAction || "proceed_to_step3", // Use next_action from backend
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        // Backend expects 'rfq_answers', not 'answers'
        rfq_answers: rfq_answers,
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error submitting goods form:", error);
      alert("Failed to proceed. Please try again.");
    } finally {
      setIsProceedingToStep3(false);
    }
  };

  // Handle Step 3: SOW Questions - Generate document
  const handleGenerateDocument = async () => {
    setIsGeneratingDocument(true);

    try {
      const payload = {
        action: nextAction || "generate_sow_document", // Use next_action from backend
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        // Backend expects 'sow_answers', not 'answers'
        sow_answers: step3Answers,
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error generating document:", error);
      alert("Failed to generate document. Please try again.");
    } finally {
      setIsGeneratingDocument(false);
    }
  };

  // Handle SOW Confirmation and Final Document Generation
  const handleConfirmAndGenerateDocument = async () => {
    setIsConfirmingDocument(true);

    try {
      // Merge edited sections with structured preview
      const updatedSections = structuredPreview?.sections?.map((section: any) => ({
        ...section,
        content: editedSections[section.section_id] !== undefined
          ? editedSections[section.section_id]
          : section.content,
      }));

      const payload = {
        action: nextAction || "confirm_and_generate_document",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        structured_preview: {
          sections: updatedSections,
          total_sections: updatedSections?.length || 0,
        },
      };

      await dispatch(
        sendMessageToAgent({
          message: JSON.stringify(payload),
          agentId: "rfq_builder",
          agentEndpoint: "/stream/rfq-builder",
        })
      );
    } catch (error) {
      console.error("Error confirming document:", error);
      alert("Failed to generate final document. Please try again.");
    } finally {
      setIsConfirmingDocument(false);
    }
  };

  // Historical Step Indicator Banner
  const HistoricalStepBanner = isViewingHistoricalStep && rfqCurrentStepNumber ? (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2,
        backgroundColor: "#fff3cd",
        border: "1px solid #ffc107",
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <History sx={{ color: "#856404", fontSize: 20 }} />
        <Typography sx={{ color: "#856404", fontSize: "14px", fontWeight: 500 }}>
          Viewing previous step ({rfqStepHistory?.find(s => s.stepNumber === rfqCurrentStepNumber)?.stepName || `Step ${rfqCurrentStepNumber}`})
        </Typography>
      </Box>
      <Button
        onClick={handleGoForward}
        endIcon={<ArrowForwardIcon />}
        size="small"
        variant="contained"
        sx={{
          backgroundColor: "#00a888",
          textTransform: "none",
          fontSize: "13px",
          "&:hover": {
            backgroundColor: "#008f75",
          },
        }}
      >
        Resume to latest
      </Button>
    </Paper>
  ) : null;

  // RFQ Builder Sidebar Component
  const RfqBuilderSidebar = () => {
    // Calculate progress based on answered questions
    const progressPercent = totalQuestions > 0
      ? Math.round((answeredQuestionsCount / totalQuestions) * 100)
      : 0;

    // Debug: Log current status for sidebar
    console.log("Sidebar - Current status:", status);

    // Determine which section is completed/active based on workflow status
    // Mapping based on actual UI content:
    // - step2_specific_questions = Requirements (line items form - "Step 2: Services/Goods Questions")
    // - step3_asking_initial_dynamic = Specifications (dynamic Q&A questions - "Step 3")

    // Section 1: Categorization (step1_categorization)
    const isCategorization = status === 'step1_categorization';
    const isCategorizationComplete = status !== 'step1_categorization' && status !== '';

    // Section 2: Requirements (step2_specific_questions - line items form)
    const isRequirements = status === 'step2_specific_questions';
    const isRequirementsComplete = status === 'step3_asking_initial_dynamic' || status.includes('step4') ||
                                    status.includes('awaiting_sow') || status.includes('asking_step5') ||
                                    status.includes('evaluation') || status.includes('asking_rfq_package') ||
                                    status.includes('collecting_rfq_package') || status === 'completed';

    // Section 3: Specifications (step3_asking_initial_dynamic - dynamic Q&A)
    const isSpecifications = status === 'step3_asking_initial_dynamic';
    const isSpecificationsComplete = status.includes('step4') || status.includes('awaiting_sow') ||
                                      status.includes('asking_step5') || status.includes('evaluation') ||
                                      status.includes('asking_rfq_package') || status.includes('collecting_rfq_package') ||
                                      status === 'completed';

    // Section 4: SOW Creation (step4_sow_questions)
    const isSOWCreation = status === 'step4_sow_questions';
    const isSOWCreationComplete = status === 'awaiting_sow_confirmation' || status === 'asking_step5_questions' ||
                                   status.includes('evaluation') || status.includes('asking_rfq_package') ||
                                   status.includes('collecting_rfq_package') || status === 'completed';

    // Section 5: SOW Review (awaiting_sow_confirmation, asking_step5_questions)
    const isSOWReview = status === 'awaiting_sow_confirmation' || status === 'asking_step5_questions';
    const isSOWReviewComplete = status === 'asking_evaluation_type' || status === 'collecting_evaluation_data' ||
                                 status.includes('asking_rfq_package') || status.includes('collecting_rfq_package') ||
                                 status === 'completed';

    // Section 6: Evaluation Criteria (asking_evaluation_type, collecting_evaluation_data)
    const isEvaluationCriteria = status === 'asking_evaluation_type' || status === 'collecting_evaluation_data';
    const isEvaluationCriteriaComplete = status.includes('asking_rfq_package') ||
                                          status.includes('collecting_rfq_package') || status === 'completed';

    // Section 7: Package Generation (collecting_rfq_package_data, asking_rfq_package)
    const isPackageGeneration = status === 'collecting_rfq_package_data' ||
                                 status === 'asking_rfq_package';
    const isPackageGenerationComplete = status === 'completed';

    // Define workflow sections with detailed breakdown
    const workflowSections = [
      {
        id: 'categorization',
        title: 'Categorization',
        subtitle: 'Basic information',
        icon: <DescriptionIcon />,
        active: isCategorization,
        completed: isCategorizationComplete,
      },
      {
        id: 'requirements',
        title: 'Requirements',
        subtitle: 'Goods details',
        icon: <SettingsIcon />,
        active: isRequirements,
        completed: isRequirementsComplete,
      },
      {
        id: 'specifications',
        title: 'Specifications',
        subtitle: 'Detailed requirements',
        icon: <ListAltIcon />,
        active: isSpecifications,
        completed: isSpecificationsComplete,
      },
      {
        id: 'sow-creation',
        title: 'SOW Creation',
        subtitle: 'Scope definition',
        icon: <AssignmentIcon />,
        active: isSOWCreation,
        completed: isSOWCreationComplete,
      },
      {
        id: 'sow-review',
        title: 'SOW Review',
        subtitle: 'Confirmation',
        icon: <VisibilityIcon />,
        active: isSOWReview,
        completed: isSOWReviewComplete,
      },
      {
        id: 'evaluation-criteria',
        title: 'Evaluation Criteria',
        subtitle: 'Technical setup',
        icon: <StarIcon />,
        active: isEvaluationCriteria,
        completed: isEvaluationCriteriaComplete,
      },
      {
        id: 'package-generation',
        title: 'Package Generation',
        subtitle: 'Final documents',
        icon: <DescriptionIcon />,
        active: isPackageGeneration,
        completed: isPackageGenerationComplete,
      },
    ];

    // Count completed sections
    const completedSectionsCount = workflowSections.filter(section => section.completed).length;
    const totalSections = workflowSections.length;

    // Calculate progress percentage based on completed sections
    const sectionProgressPercent = Math.round((completedSectionsCount / totalSections) * 100);

    return (
      <div className={styles.rfqBuilderSidebar}>
        {/* Header */}
        <div className={styles.rfqSidebarHeader}>
          <div className={styles.rfqLogo}>
            <img src={accellorLogo} alt="Accellor Logo" className={styles.g42LogoIcon} style={{ height: '24px', width: 'auto' }} />
            <Typography component="span" sx={{ fontWeight: 600, fontSize: '16px', color: '#333', marginLeft: '8px' }}>accellor</Typography>
            <Typography component="h2" sx={{ fontSize: '18px', fontWeight: 700, color: '#1a1a1a', marginLeft: '16px' }}>RFQ Builder</Typography>
          </div>
          <p className={styles.rfqSubtitle}>Build your sourcing package step by step</p>
        </div>

        {/* Progress Section */}
        <div className={styles.rfqProgress}>
          <div className={styles.progressHeader}>
            <span className={styles.progressPercent}>{sectionProgressPercent}%</span>
            <span className={styles.progressLabel}>complete</span>
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${sectionProgressPercent}%` }}
            />
          </div>
          <p className={styles.progressText}>
            {completedSectionsCount} of {totalSections} sections completed
          </p>
        </div>

        {/* Sections List */}
        <ul
          className={styles.rfqSectionsList}
          style={isResettingStep ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
        >
          {workflowSections.map((section) => {
            // All sections (except currently active) are clickable to navigate
            const isClickable = !section.active && !isResettingStep;
            return (
              <li
                key={section.id}
                className={`${styles.rfqSectionItem} ${section.active ? styles.active : ''} ${section.completed ? styles.completed : ''} ${isClickable ? styles.clickable : ''}`}
                onClick={isClickable ? () => handleResetToStep(section.id) : undefined}
                style={isClickable ? { cursor: 'pointer' } : undefined}
              >
                <div className={styles.sectionIcon}>
                  {section.completed && !section.active ? <CheckCircleIcon /> : section.icon}
                </div>
                <div className={styles.sectionContent}>
                  <h3 className={styles.sectionTitle}>{section.title}</h3>
                  {!section.active && <p className={styles.sectionSubtitle}>{section.subtitle}</p>}
                </div>
              </li>
            );
          })}
        </ul>
        {isResettingStep && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} sx={{ color: '#00a888' }} />
          </Box>
        )}
      </div>
    );
  };

  if (status === "step1_categorization") {
    // Get options from backend for all three fields
    const categoryQuestion = allQuestions.find((q: Question) => q.question_id === "category");
    const categoryOptions = categoryQuestion?.options || [];
    const clientCompanyQuestion = allQuestions.find((q: Question) => q.question_id === "client_company");
    const clientCompanyOptions = clientCompanyQuestion?.options || [];

    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          {/* Historical Step Indicator */}
          {HistoricalStepBanner}

        {/* Header Navigation */}
        {/* <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              color: "#666",
              fontSize: "13px",
              padding: "6px 12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            To the requests
          </Button>
          <Button
            startIcon={<History sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              color: "#666",
              fontSize: "13px",
              padding: "6px 12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            Start request over
          </Button>
          <Button
            disabled
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              color: "#666",
              fontSize: "13px",
              padding: "6px 12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
              "&.Mui-disabled": {
                color: "#bbb",
              },
            }}
          >
            Go back
          </Button>
        </Box> */}

        <div className={styles.rfqContentCard}>
          {/* Top Bar: Question Progress */}
          <div className={styles.rfqTopBar}>
            <span className={styles.questionProgress}>Basic Information</span>
          </div>

          {/* Title */}
          <h1 className={styles.rfqContentTitle}>Categorization</h1>

          {/* Scrollable Questions Area */}
          <div className={styles.rfqContentScrollWrapper}>
            {/* Question 1: Client Company */}
            <div className={styles.rfqQuestion}>
              <p className={styles.questionText}>
                Client Company <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
              </p>
              <FormControl fullWidth className={styles.questionField}>
                <Select
                  value={categorizationData.client_company}
                  onChange={(e) =>
                    setCategorizationData({
                      ...categorizationData,
                      client_company: e.target.value,
                    })
                  }
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select client company
                  </MenuItem>
                  {clientCompanyOptions.map((option: string) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Question 2: Category */}
            <div className={styles.rfqQuestion}>
              <p className={styles.questionText}>
                Category <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
              </p>
              <FormControl fullWidth className={styles.questionField}>
                <Select
                  value={categorizationData.category}
                  onChange={(e) =>
                    setCategorizationData({
                      ...categorizationData,
                      category: e.target.value,
                    })
                  }
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select a category
                  </MenuItem>
                  {categoryOptions.map((option: string) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Question 3: Delivery Location */}
            <div className={styles.rfqQuestion}>
              <p className={styles.questionText}>
                Delivery Location <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
              </p>
              <FormControl fullWidth className={styles.questionField}>
                <Select
                  value={categorizationData.delivery_location}
                  onChange={(e) =>
                    setCategorizationData({
                      ...categorizationData,
                      delivery_location: e.target.value,
                    })
                  }
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select delivery location
                  </MenuItem>
                  <MenuItem value="UAE">UAE</MenuItem>
                  <MenuItem value="Saudi Arabia">Saudi Arabia</MenuItem>
                  <MenuItem value="Qatar">Qatar</MenuItem>
                  <MenuItem value="Kuwait">Kuwait</MenuItem>
                  <MenuItem value="Bahrain">Bahrain</MenuItem>
                  <MenuItem value="Oman">Oman</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>

          {/* Bottom Navigation */}
          <div className={styles.rfqBottomNav}>
            {/* Back Button */}
            <Button
              variant="outlined"
              className={styles.navBack}
              disabled
              sx={{
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 500,
                color: "#666",
                borderColor: "#e0e0e0",
                borderRadius: "8px",
                padding: "10px 24px",
                "&:hover": {
                  borderColor: "#00a888",
                  backgroundColor: "#f0fbf9",
                },
                "&.Mui-disabled": {
                  color: "#ccc",
                  borderColor: "#e0e0e0",
                },
              }}
            >
              Back
            </Button>

            {/* Progress Dots */}
            <div className={styles.navDots}>
              <span className={`${styles.dot} ${styles.active}`}></span>
              <span className={styles.dot}></span>
              <span className={styles.dot}></span>
            </div>

            {/* Continue Button */}
            <Button
              variant="contained"
              className={styles.navContinue}
              onClick={handleProceedToStep2}
              disabled={
                !categorizationData.category ||
                !categorizationData.delivery_location ||
                !categorizationData.client_company ||
                isProceedingToStep2
              }
              sx={{
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 600,
                backgroundColor: "#00a888",
                color: "#fff",
                borderRadius: "8px",
                padding: "10px 32px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#008f75",
                  boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#9e9e9e",
                },
              }}
            >
              {isProceedingToStep2 ? (
                <>
                  <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                  Processing...
                </>
              ) : (
                "Continue"
              )}
            </Button>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Handle NEW Step 2: Initial Questions (with suggested answers)
  if (status === "step2_initial_questions") {
    const allRequiredAnswered = allQuestions
      .filter((q: Question) => q.is_required)
      .every((q: Question) => step2InitialAnswers[q.question_id]?.trim());

    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          {/* Historical Step Indicator */}
          {HistoricalStepBanner}

        <div className={styles.rfqContentCard}>
          {/* Top Bar: Question Progress */}
          <div className={styles.rfqTopBar}>
            <span className={styles.questionProgress}>Step 2: Goods Details</span>
            <span className={styles.questionsRemaining}>{allQuestions.length} questions</span>
          </div>

          {/* Title */}
          <h1 className={styles.rfqContentTitle}>Goods Questions</h1>

          {/* Questions */}
          <Box sx={{ mb: 3 }}>
            {allQuestions.map((question: any, index: number) => {
              // Check if this is a weight question that follows a criterion
              const isCriterionWeightPair =
                question.question_text?.toLowerCase().includes('weight for criterion') &&
                index > 0 &&
                allQuestions[index - 1]?.question_text?.toLowerCase().includes('criterion');

              // Skip rendering weight questions here - they'll be rendered with their criterion
              if (isCriterionWeightPair) {
                return null;
              }

              // Check if next question is a weight for this criterion
              const isCriterion = question.question_text?.toLowerCase().includes('criterion') &&
                !question.question_text?.toLowerCase().includes('weight');
              const nextQuestion = allQuestions[index + 1];
              const hasWeightPair = isCriterion &&
                nextQuestion?.question_text?.toLowerCase().includes('weight for criterion');

              // Render criterion and weight side by side
              if (hasWeightPair) {
                return (
                  <div key={question.question_id} className={styles.rfqQuestion}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      {/* Criterion field - takes 70% width */}
                      <Box sx={{ flex: '0 0 70%' }}>
                        <p className={styles.questionText}>
                          {question.question_text}
                          {question.is_required && (
                            <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                          )}
                        </p>
                        <TextField
                          fullWidth
                          multiline
                          minRows={question.question_type === "text_long" ? 3 : 1}
                          type={question.question_type === "date" ? "date" : "text"}
                          value={step2InitialAnswers[question.question_id] || ""}
                          onChange={(e) =>
                            setStep2InitialAnswers({
                              ...step2InitialAnswers,
                              [question.question_id]: e.target.value,
                            })
                          }
                          placeholder={question.help_text || ""}
                          className={styles.questionField}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "#fafafa",
                              fontSize: "15px",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                            },
                          }}
                        />
                        {step2InitialAnswers[question.question_id] && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newAnswers = { ...step2InitialAnswers };
                              delete newAnswers[question.question_id];
                                setStep2InitialAnswers(newAnswers);
                              }}
                              sx={{
                                position: "absolute",
                                right: "8px",
                                top: "8px",
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          )}
                      </Box>

                      {/* Weight field - takes 30% width */}
                      <Box sx={{ flex: '0 0 calc(30% - 16px)' }}>
                        <p className={styles.questionText}>
                          Weightage (out of 100)
                          {nextQuestion.is_required && (
                            <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                          )}
                        </p>
                        <TextField
                          fullWidth
                          type="number"
                          value={step2InitialAnswers[nextQuestion.question_id] || ""}
                          onChange={(e) =>
                            setStep2InitialAnswers({
                              ...step2InitialAnswers,
                              [nextQuestion.question_id]: e.target.value,
                            })
                          }
                          placeholder={nextQuestion.help_text || ""}
                          className={styles.questionField}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "#fafafa",
                              fontSize: "15px",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </div>
                );
              }

              // Regular question rendering (not part of a criterion-weight pair)
              return (
                <div key={question.question_id} className={styles.rfqQuestion}>
                  <p className={styles.questionText}>
                    {question.question_text}
                    {question.is_required && (
                      <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                    )}
                  </p>
                  {question.question_type === "select" ? (
                    <FormControl fullWidth className={styles.questionField}>
                      <Select
                        value={step2InitialAnswers[question.question_id] || ""}
                        onChange={(e) =>
                          setStep2InitialAnswers({
                            ...step2InitialAnswers,
                            [question.question_id]: e.target.value,
                          })
                        }
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select {question.question_text.toLowerCase()}
                        </MenuItem>
                        {question.options?.map((option: string) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      multiline
                      minRows={question.question_type === "text_long" ? 3 : 1}
                      type={question.question_type === "date" ? "date" : "text"}
                      value={step2InitialAnswers[question.question_id] || ""}
                      onChange={(e) =>
                        setStep2InitialAnswers({
                          ...step2InitialAnswers,
                          [question.question_id]: e.target.value,
                        })
                      }
                      placeholder={question.help_text || ""}
                      className={styles.questionField}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#fafafa",
                          fontSize: "15px",
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "14px 16px",
                        },
                      }}
                    />
                  )}
                </div>
              );
            })}
          </Box>

          {/* Bottom Navigation */}
          <div className={styles.rfqBottomNav}>
            {/* Back Button */}
            <Button
              variant="outlined"
              className={styles.navBack}
              onClick={handleGoBack}
              sx={{
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 500,
                color: "#666",
                borderColor: "#e0e0e0",
                borderRadius: "8px",
                padding: "10px 24px",
                "&:hover": {
                  borderColor: "#00a888",
                  backgroundColor: "#f0fbf9",
                },
              }}
            >
              Back
            </Button>

            {/* Progress Dots */}
            <div className={styles.navDots}>
              <span className={styles.dot}></span>
              <span className={`${styles.dot} ${styles.active}`}></span>
              <span className={styles.dot}></span>
            </div>

            {/* Continue Button */}
            <Button
              variant="contained"
              className={styles.navContinue}
              onClick={handleProceedToGoodsServices}
              disabled={isProceedingToGoodsServices || !allRequiredAnswered}
              sx={{
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 600,
                backgroundColor: "#00a888",
                color: "#fff",
                borderRadius: "8px",
                padding: "10px 32px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#008f75",
                  boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#9e9e9e",
                },
              }}
            >
              {isProceedingToGoodsServices ? (
                <>
                  <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                  Processing...
                </>
              ) : (
                "Continue"
              )}
            </Button>
          </div>
        </div>
      </div>
      </div>
    );
  }

  // Handle Step 3: Specific Questions (Goods/Service detailed questions)
  if (status === "step3_specific_questions") {
    const allRequiredAnswered = allQuestions
      .filter((q: Question) => q.is_required)
      .every((q: Question) => step3SpecificAnswers[q.question_id]?.trim());

    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          <div className={styles.rfqContentCard}>
            {/* Top Bar */}
            <div className={styles.rfqTopBar}>
              <span className={styles.questionProgress}>Step 3: Specific Questions</span>
              <span className={styles.questionsRemaining}>{allQuestions.length} questions</span>
            </div>

            {/* Title */}
            <h1 className={styles.rfqContentTitle}>Understanding Your Requirements</h1>

            {/* Questions */}
            <Box sx={{ mb: 3 }}>
              {allQuestions.map((question: any, index: number) => {
              // Check if this is a weight question that follows a criterion
              const isCriterionWeightPair =
                question.question_text?.toLowerCase().includes('weight for criterion') &&
                index > 0 &&
                allQuestions[index - 1]?.question_text?.toLowerCase().includes('criterion');

              // Skip rendering weight questions here - they'll be rendered with their criterion
              if (isCriterionWeightPair) {
                return null;
              }

              // Check if next question is a weight for this criterion
              const isCriterion = question.question_text?.toLowerCase().includes('criterion') &&
                !question.question_text?.toLowerCase().includes('weight');
              const nextQuestion = allQuestions[index + 1];
              const hasWeightPair = isCriterion &&
                nextQuestion?.question_text?.toLowerCase().includes('weight for criterion');

              // Render criterion and weight side by side
              if (hasWeightPair) {
                return (
                  <div key={question.question_id} className={styles.rfqQuestion}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      {/* Criterion field - takes 70% width */}
                      <Box sx={{ flex: '0 0 70%' }}>
                        <p className={styles.questionText}>
                          {question.question_text}
                          {question.is_required && (
                            <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                          )}
                        </p>
                        <TextField
                          fullWidth
                          multiline
                          minRows={question.question_type === "text_long" ? 3 : 1}
                          type={question.question_type === "date" ? "date" : "text"}
                          value={step3SpecificAnswers[question.question_id] || ""}
                          onChange={(e) =>
                            setStep3SpecificAnswers({
                              ...step3SpecificAnswers,
                              [question.question_id]: e.target.value,
                            })
                          }
                          placeholder={question.help_text || ""}
                          className={styles.questionField}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "#fafafa",
                              fontSize: "15px",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                            },
                          }}
                        />
                      </Box>

                      {/* Weight field - takes 30% width */}
                      <Box sx={{ flex: '0 0 calc(30% - 16px)' }}>
                        <p className={styles.questionText}>
                          Weightage (out of 100)
                          {nextQuestion.is_required && (
                            <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                          )}
                        </p>
                        <TextField
                          fullWidth
                          type="number"
                          value={step3SpecificAnswers[nextQuestion.question_id] || ""}
                          onChange={(e) =>
                            setStep3SpecificAnswers({
                              ...step3SpecificAnswers,
                              [nextQuestion.question_id]: e.target.value,
                            })
                          }
                          placeholder={nextQuestion.help_text || ""}
                          className={styles.questionField}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "#fafafa",
                              fontSize: "15px",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </div>
                );
              }

              // Regular question rendering
              return (
                <div key={question.question_id} className={styles.rfqQuestion}>
                  <p className={styles.questionText}>
                    {question.question_text}
                    {question.is_required && (
                      <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                    )}
                  </p>
                  <TextField
                    fullWidth
                    multiline
                    minRows={question.question_type === "text_long" ? 3 : 1}
                    type={question.question_type === "date" ? "date" : "text"}
                    value={step3SpecificAnswers[question.question_id] || ""}
                    onChange={(e) =>
                      setStep3SpecificAnswers({
                        ...step3SpecificAnswers,
                        [question.question_id]: e.target.value,
                      })
                    }
                    placeholder={question.help_text || ""}
                    className={styles.questionField}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#fafafa",
                        fontSize: "15px",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "14px 16px",
                      },
                    }}
                  />
                </div>
              );
              })}
            </Box>

            {/* Bottom Navigation */}
            <div className={styles.rfqBottomNav}>
              <Button
                variant="outlined"
                className={styles.navBack}
                onClick={handleGoBack}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#666",
                  borderColor: "#e0e0e0",
                  borderRadius: "8px",
                  padding: "10px 24px",
                  "&:hover": {
                    borderColor: "#00a888",
                    backgroundColor: "#f0fbf9",
                  },
                }}
              >
                Back
              </Button>

              {/* Progress Dots */}
              <div className={styles.navDots}>
                <span className={styles.dot}></span>
                <span className={styles.dot}></span>
                <span className={`${styles.dot} ${styles.active}`}></span>
              </div>

              <Button
                variant="contained"
                className={styles.navContinue}
                onClick={handleProceedToSowQuestions}
                disabled={isProceedingToSow || !allRequiredAnswered}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  backgroundColor: "#00a888",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "10px 32px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {isProceedingToSow ? (
                  <>
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle Step 4: SOW Questions
  if (status === "step4_sow_questions") {
    const displayQuestion = allQuestions[currentQuestionIndex];
    const displayAnswer = displayQuestion ? step4SowAnswers[displayQuestion.question_id] || "" : "";

    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          <div className={styles.rfqContentCard}>
            {/* Top Bar */}
            <div className={styles.rfqTopBar}>
              <span className={styles.questionProgress}>Step 4: SOW Creation</span>
              <span className={styles.questionsRemaining}>
                Question {currentQuestionIndex + 1} / {allQuestions.length}
              </span>
            </div>

            {/* Title and Description */}
            {message ? (
              <Box sx={{ mb: 3 }}>
                <h1 className={styles.rfqContentTitle}>
                  {message.split('\n')[0].replace(/📝\s*\*\*/g, '').replace(/\*\*/g, '').trim()}
                </h1>
                {message.split('\n\n')[1] && (
                  <ReactMarkdown
                    components={markdownSubtitleComponents}
                  >
                    {message.split('\n\n')[1].trim()}
                  </ReactMarkdown>
                )}
              </Box>
            ) : (
              <h1 className={styles.rfqContentTitle}>Scope of Work</h1>
            )}

            {/* Question */}
            {displayQuestion && (
              <div className={styles.rfqQuestion}>
                <p className={styles.questionText}>
                  {displayQuestion.question_text}
                  {displayQuestion.is_required && (
                    <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                  )}
                </p>

                {/* Text Long Field */}
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  value={displayAnswer}
                  onChange={(e) =>
                    setStep4SowAnswers({
                      ...step4SowAnswers,
                      [displayQuestion.question_id]: e.target.value,
                    })
                  }
                  placeholder={displayQuestion.help_text || "Enter your answer here..."}
                  className={styles.questionField}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fafafa",
                      fontSize: "15px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "14px 16px",
                    },
                  }}
                />
              </div>
            )}

            {/* Bottom Navigation */}
            <div className={styles.rfqBottomNav}>
              <Button
                variant="outlined"
                className={styles.navBack}
                onClick={handleStep4Back}
                disabled={currentQuestionIndex === 0}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#666",
                  borderColor: "#e0e0e0",
                  borderRadius: "8px",
                  padding: "10px 24px",
                  "&:hover": {
                    borderColor: "#00a888",
                    backgroundColor: "#f0fbf9",
                  },
                  "&.Mui-disabled": {
                    borderColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                Back
              </Button>

              {/* Progress Dots */}
              <div className={styles.navDots}>
                {allQuestions.map((_, idx) => (
                  <span
                    key={idx}
                    className={`${styles.dot} ${idx === currentQuestionIndex ? styles.active : ''}`}
                  ></span>
                ))}
              </div>

              {currentQuestionIndex < allQuestions.length - 1 ? (
                <Button
                  variant="contained"
                  className={styles.navContinue}
                  onClick={handleStep4Next}
                  sx={{
                    textTransform: "none",
                    fontSize: "15px",
                    fontWeight: 600,
                    backgroundColor: "#00a888",
                    color: "#fff",
                    borderRadius: "8px",
                    padding: "10px 32px",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#008f75",
                      boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                    },
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  className={styles.navContinue}
                  onClick={handleGenerateSowDocument}
                  disabled={isGeneratingSowDocument || !allQuestions.filter((q: Question) => q.is_required).every((q: Question) => step4SowAnswers[q.question_id]?.trim())}
                  sx={{
                    textTransform: "none",
                    fontSize: "15px",
                    fontWeight: 600,
                    backgroundColor: "#00a888",
                    color: "#fff",
                    borderRadius: "8px",
                    padding: "10px 32px",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#008f75",
                      boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#e0e0e0",
                      color: "#9e9e9e",
                    },
                  }}
                >
                  {isGeneratingSowDocument ? (
                    <>
                      <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                      Generating...
                    </>
                  ) : (
                    "Generate Document"
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }


  // Handle asking_step5_questions (Step 5 with document preview/download)
  if (status === "asking_step5_questions") {
    const question = allQuestions[0];

    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          <div className={styles.rfqContentCard}>
            {/* Success Icon and Message */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: '#d4f4ec',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 48, color: '#00a888' }} />
              </Box>
              <h1 className={styles.rfqContentTitle} style={{ marginBottom: '8px' }}>
                SOW Document Created Successfully!
              </h1>
              <p style={{ fontSize: '15px', color: '#999', margin: 0 }}>
                1 Document Created
              </p>
            </Box>

            {/* Document Card */}
            {generatedDocumentPath && (() => {
              // Extract filename from path, or construct it from session ID if path is just base URL
              let filename = generatedDocumentPath.split(/[\\\/]/).pop() || "";
              console.log("Generated Document Path:", generatedDocumentPath);
              console.log("Extracted filename (before):", filename);

              // If filename doesn't have an extension, it's likely just the session ID
              // Construct the proper SOW filename
              if (!filename.includes('.')) {
                filename = `SOW_${sessionId}.docx`;
              }
              console.log("Final filename (after):", filename);

              return (
                <Box
                  sx={{
                    mb: 3,
                    p: 3,
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    border: "1px solid #e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 3,
                  }}
                >
                  {/* Document Info */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "8px",
                        backgroundColor: "#e8f5f2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 28, color: "#00a888" }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "15px",
                          fontWeight: 600,
                          color: "#333",
                          mb: 0.5,
                        }}
                      >
                        {filename}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#999",
                        }}
                      >
                        Scope of Work document
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
                      onClick={() => handlePreview(filename)}
                      sx={{
                        borderColor: "#e0e0e0",
                        color: "#666",
                        padding: "8px 20px",
                        fontSize: "14px",
                        fontWeight: 500,
                        textTransform: "none",
                        borderRadius: "8px",
                        "&:hover": {
                          borderColor: "#00a888",
                          backgroundColor: "#f0fbf9",
                          color: "#00a888",
                        },
                      }}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                      onClick={() => handleDownload("sow")}
                      disabled={isDownloading}
                      sx={{
                        backgroundColor: "#00a888",
                        color: "#fff",
                        padding: "8px 20px",
                        fontSize: "14px",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "8px",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#008f75",
                          boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#e0e0e0",
                          color: "#9e9e9e",
                        },
                      }}
                    >
                      {isDownloading ? "Downloading..." : "Download"}
                    </Button>
                  </Box>
                </Box>
              );
            })()}

            {/* Next Steps Info Box */}
            <Box
              sx={{
                p: 2.5,
                backgroundColor: "#fffbeb",
                borderRadius: "8px",
                border: "1px solid #fde68a",
                mb: 3,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#92400e",
                  lineHeight: 1.6,
                }}
              >
                <strong>Next Steps:</strong> Your complete evaluation framework is ready for assessing vendor proposals. Download all documents to begin your evaluation process.
              </Typography>
            </Box>

            {/* Evaluation Question */}
            {question && (
              <div>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#00a888',
                    fontWeight: 500,
                    margin: '0 0 12px 0',
                  }}
                >
                  {question.question_text}
                  {question.is_required && (
                    <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                  )}
                </p>
                <RadioGroup
                  value={step5Answer}
                  onChange={(e) => setStep5Answer(e.target.value)}
                >
                  {question.options?.map((option: string) => (
                    <FormControlLabel
                      key={option}
                      value={option}
                      control={
                        <Radio
                          sx={{
                            color: "#00a888",
                            "&.Mui-checked": {
                              color: "#00a888",
                            },
                          }}
                        />
                      }
                      label={option}
                      sx={{
                        mb: 1,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "15px",
                          color: "#333",
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Bottom Navigation */}
            <div className={styles.rfqBottomNav}>
              <Button
                variant="outlined"
                className={styles.navBack}
                onClick={handleGoBack}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#666",
                  borderColor: "#e0e0e0",
                  borderRadius: "8px",
                  padding: "10px 24px",
                  "&:hover": {
                    borderColor: "#00a888",
                    backgroundColor: "#f0fbf9",
                  },
                }}
              >
                Back
              </Button>

              {/* Progress Dots - Empty for this step */}
              <div className={styles.navDots}></div>

              <Button
                variant="contained"
                className={styles.navContinue}
                onClick={handleSubmitStep5Answer}
                disabled={!step5Answer || isSubmittingStep5}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  backgroundColor: "#00a888",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "10px 32px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {isSubmittingStep5 ? (
                  <>
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    Submitting...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Preview Modal */}
        <Dialog
          open={previewModalOpen}
          onClose={handleClosePreview}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              height: "90vh",
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#f0fbf9",
              borderBottom: "1px solid #b2e5de",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#004141" }}>
                Document Preview
              </Typography>
              {previewFile && (
                <Typography variant="body2" sx={{ color: "#666", marginTop: 0.5 }}>
                  {previewFile.name}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              padding: 0,
              backgroundColor: "#fafafa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {loadingPreview ? (
              <Box sx={{ textAlign: "center", padding: 4 }}>
                <CircularProgress size={48} sx={{ color: "#00a888" }} />
                <Typography sx={{ marginTop: 2, color: "#666" }}>
                  Loading preview...
                </Typography>
              </Box>
            ) : previewFile ? (
              previewFile.type === "pdf" ? (
                <iframe
                  src={previewFile.url}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  title="Document Preview"
                />
              ) : previewHtml ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    padding: 3,
                  }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 4,
                  }}
                >
                  <Typography variant="h6" sx={{ marginBottom: 2, color: "#666" }}>
                    {previewFile.type === "excel" && "📊 Excel Document"}
                    {previewFile.type === "word" && "📄 Word Document"}
                  </Typography>
                  <Typography sx={{ marginBottom: 3, color: "#666", textAlign: "center" }}>
                    Preview is not available for this file type in the browser.
                    <br />
                    Please download the file to view its contents.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      if (previewFile) {
                        handleDownload("sow", previewFile.name);
                      }
                    }}
                    sx={{
                      backgroundColor: "#00a888",
                      "&:hover": {
                        backgroundColor: "#008f75",
                      },
                    }}
                  >
                    Download {previewFile.name}
                  </Button>
                </Box>
              )
            ) : null}
          </DialogContent>
          <DialogActions sx={{ padding: 2, backgroundColor: "#f0fbf9" }}>
            {previewFile && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  if (previewFile) {
                    handleDownload("sow", previewFile.name);
                  }
                }}
                sx={{
                  backgroundColor: "#00a888",
                  "&:hover": {
                    backgroundColor: "#008f75",
                  },
                }}
              >
                Download
              </Button>
            )}
            <Button onClick={handleClosePreview} sx={{ color: "#666" }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  // Handle asking_evaluation_offer (Yes/No question)
  if (status === "asking_evaluation_offer") {
    return (
      <div className={styles.pressReleaseContent}>
        {/* Header Navigation */}
        {/* <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              color: "#666",
              fontSize: "13px",
              padding: "6px 12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            To the requests
          </Button>
          <Button
            startIcon={<History sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              color: "#666",
              fontSize: "13px",
              padding: "6px 12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            Start request over
          </Button>
        </Box> */}

        <Paper
          elevation={0}
          sx={{
            padding: "32px",
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          {/* Message - Split to insert download button before "Next Step" */}
          {(() => {
            const messageParts = message.split(/(\ud83d\udcca \*\*Next Step)/);
            const beforeNextStep = messageParts[0] || message;
            const nextStepAndAfter = messageParts.length > 1 ? messageParts.slice(1).join('') : '';

            return (
              <>
                {/* First part of message (before Next Step) */}
                <Box
                  sx={{
                    mb: 3,
                    "& p:first-of-type": {
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "#00a888",
                      marginBottom: "8px",
                    },
                    "& p": {
                      fontSize: "15px",
                      color: "#666",
                      marginBottom: "8px",
                    },
                    "& strong": {
                      fontWeight: 700,
                      color: "#00a888",
                    },
                  }}
                >
                  <ReactMarkdown>{beforeNextStep}</ReactMarkdown>
                </Box>

                {/* Download SOW Button */}
                {generatedDocumentPath && (
                  <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload("sow")}
                      disabled={isDownloading}
                      sx={{
                        borderColor: "#00a888",
                        color: "#00a888",
                        padding: "10px 24px",
                        fontSize: "14px",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "6px",
                        "&:hover": {
                          borderColor: "#008f75",
                          backgroundColor: "rgba(0, 168, 136, 0.08)",
                        },
                        "&.Mui-disabled": {
                          borderColor: "#e0e0e0",
                          color: "#9e9e9e",
                        },
                      }}
                    >
                      {isDownloading ? (
                        <>
                          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                          Downloading...
                        </>
                      ) : (
                        `Download SOW (${generatedDocumentPath.split(/[\\\/]/).pop()})`
                      )}
                    </Button>
                  </Box>
                )}

                {/* Second part of message (Next Step and after) */}
                {nextStepAndAfter && (
                  <Box
                    sx={{
                      mb: 3,
                      "& p:first-of-type": {
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "#00a888",
                        marginBottom: "8px",
                      },
                      "& p": {
                        fontSize: "15px",
                        color: "#666",
                        marginBottom: "8px",
                      },
                      "& strong": {
                        fontWeight: 700,
                        color: "#00a888",
                      },
                    }}
                  >
                    <ReactMarkdown>{nextStepAndAfter}</ReactMarkdown>
                  </Box>
                )}
              </>
            );
          })()}

          {/* Question */}
          {allQuestions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                component="p"
                sx={{
                  fontSize: "14px",
                  color: "#333",
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                {allQuestions[0].question_text}
              </Typography>

              {/* Yes/No Radio Buttons */}
              <RadioGroup
                value={evaluationAnswer}
                onChange={(e) => setEvaluationAnswer(e.target.value)}
                sx={{ mb: 2 }}
              >
                {allQuestions[0].options.map((option: string) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={
                      <Radio
                        sx={{
                          color: "#00a888",
                          "&.Mui-checked": {
                            color: "#00a888",
                          },
                        }}
                      />
                    }
                    label={option}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        color: "#333",
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </Box>
          )}

          {/* Submit Button */}
          <Button
            variant="contained"
            onClick={handleSubmitEvaluationAnswer}
            disabled={isSubmittingEvaluation || !evaluationAnswer}
            sx={{
              backgroundColor: "#00a888",
              color: "#fff",
              padding: "10px 24px",
              fontSize: "14px",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "6px",
              boxShadow: "0 2px 8px rgba(0, 168, 136, 0.3)",
              "&:hover": {
                backgroundColor: "#008f75",
                boxShadow: "0 4px 12px rgba(0, 168, 136, 0.4)",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            {isSubmittingEvaluation ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Submitting...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </Paper>
      </div>
    );
  }

  // Handle asking_evaluation_type (Select evaluation approach)
  if (status === "asking_evaluation_type") {
    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          <div className={styles.rfqContentCard}>
            {/* Top Bar */}
            <div className={styles.rfqTopBar}>
              <span className={styles.questionProgress}>Evaluation Type</span>
              <span className={styles.questionsRemaining}>Select approach</span>
            </div>

            {/* Title and Description */}
            {message ? (
              <Box sx={{ mb: 3 }}>
                <h1 className={styles.rfqContentTitle}>
                  {message.split('\n')[0].replace(/📊\s*\*\*/g, '').replace(/\*\*/g, '').trim()}
                </h1>
                {message.split('\n\n')[1] && (
                  <ReactMarkdown
                    components={markdownSubtitleComponents}
                  >
                    {message.split('\n\n')[1].trim()}
                  </ReactMarkdown>
                )}
              </Box>
            ) : (
              <h1 className={styles.rfqContentTitle}>Evaluation Type</h1>
            )}

            {/* Question */}
            {allQuestions.length > 0 && (
              <div className={styles.rfqQuestion}>
                <p className={styles.questionText}>
                  {allQuestions[0].question_text}
                  {allQuestions[0].is_required && (
                    <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                  )}
                </p>

                {/* Help Text */}
                {/* Evaluation Type Radio Buttons */}
                <RadioGroup
                  value={evaluationTypeAnswer}
                  onChange={(e) => setEvaluationTypeAnswer(e.target.value)}
                >
                  {allQuestions[0].options.map((option: string) => {
                    const optionDescriptions = allQuestions[0].option_descriptions;
                    const description = optionDescriptions?.[option];

                    return (
                      <FormControlLabel
                        key={option}
                        value={option}
                        control={
                          <Radio
                            sx={{
                              color: "#00a888",
                              "&.Mui-checked": {
                                color: "#00a888",
                              },
                              alignSelf: description ? "flex-start" : "center",
                              mt: description ? "4px" : 0,
                            }}
                          />
                        }
                        label={
                          description ? (
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "15px",
                                  color: "#333",
                                  fontWeight: 500,
                                }}
                              >
                                {option}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  color: "#666",
                                  mt: 0.5,
                                  lineHeight: 1.4,
                                }}
                              >
                                {description}
                              </Typography>
                            </Box>
                          ) : (
                            option
                          )
                        }
                        sx={{
                          mb: description ? 2 : 1,
                          alignItems: description ? "flex-start" : "center",
                          "& .MuiFormControlLabel-label": {
                            fontSize: "15px",
                            color: "#333",
                          },
                        }}
                      />
                    );
                  })}
                </RadioGroup>
              </div>
            )}

            {/* Bottom Navigation */}
            <div className={styles.rfqBottomNav}>
              <Button
                variant="outlined"
                className={styles.navBack}
                onClick={handleGoBack}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#666",
                  borderColor: "#e0e0e0",
                  borderRadius: "8px",
                  padding: "10px 24px",
                  "&:hover": {
                    borderColor: "#00a888",
                    backgroundColor: "#f0fbf9",
                  },
                }}
              >
                Back
              </Button>

              {/* Progress Dots - Empty for this step */}
              <div className={styles.navDots}></div>

              <Button
                variant="contained"
                className={styles.navContinue}
                onClick={handleSubmitEvaluationType}
                disabled={isSubmittingEvaluationType || !evaluationTypeAnswer}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  backgroundColor: "#00a888",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "10px 32px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {isSubmittingEvaluationType ? (
                  <>
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    Submitting...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle collecting_evaluation_data (31 questions with suggested answers)
  if (status === "collecting_evaluation_data") {
    const allRequiredAnswered = allQuestions
      .filter((q: Question) => q.is_required)
      .every((q: Question) => technoCommercialAnswers[q.question_id]?.trim());

    // Parse message to extract title
    const messageTitle = message
      ? message.split('\n')[0].replace(/\*\*/g, '').replace(/📋\s*/g, '').trim()
      : "Technical Evaluation Form";

    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          <div className={styles.rfqContentCard}>
            {/* Top Bar */}
            <div className={styles.rfqTopBar}>
              <span className={styles.questionProgress}>Evaluation Data</span>
            </div>

            {/* Title */}
            <h1 className={styles.rfqContentTitle}>{messageTitle}</h1>

            {/* Scrollable Questions Area */}
            <div className={styles.rfqContentScrollWrapper}>
              <Box>
                {allQuestions.map((question: any, index: number) => {
                // Check if this is a weight question that follows a criterion
                const questionText = question.question_text?.toLowerCase() || '';
                const isWeightQuestion = questionText.includes('weight') &&
                  (questionText.includes('criterion') || questionText.includes('out of 100'));

                const isCriterionWeightPair = isWeightQuestion && index > 0;

                // Skip rendering weight questions here - they'll be rendered with their criterion
                if (isCriterionWeightPair) {
                  return null;
                }

                // Check if next question is a weight for this criterion
                const isCriterion = questionText.includes('criterion') && !isWeightQuestion;
                const nextQuestion = allQuestions[index + 1];
                const nextQuestionText = nextQuestion?.question_text?.toLowerCase() || '';
                const hasWeightPair = isCriterion &&
                  nextQuestionText.includes('weight') &&
                  (nextQuestionText.includes('criterion') || nextQuestionText.includes('out of 100'));

                // Render criterion and weight side by side
                if (hasWeightPair) {
                  // Skip if this criterion has been deleted
                  if (deletedCriterionIds.has(question.question_id)) {
                    return null;
                  }

                  const editableLabel = question.editable_label;
                  const criterionTitleValue = criterionTitles[question.question_id] ?? editableLabel ?? "";

                  return (
                    <Box key={question.question_id} sx={{ mb: 3, position: 'relative', p: 2, border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#fafafa' }}>
                      {/* Delete button */}
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCriterion(question.question_id, false)}
                        sx={{
                          position: "absolute",
                          right: "8px",
                          top: "8px",
                          color: "#d32f2f",
                          "&:hover": {
                            backgroundColor: "rgba(211, 47, 47, 0.1)",
                          },
                        }}
                        title="Delete criterion"
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                      </IconButton>

                      {/* Static prefix (e.g., "Criterion 1") */}
                      <Typography
                        component="p"
                        sx={{
                          fontSize: "13px",
                          color: "#00a888",
                          lineHeight: 1.4,
                          margin: "0 0 4px 0 !important",
                          fontWeight: 600,
                          paddingRight: '40px',
                        }}
                      >
                        {question.question_text}
                        {question.is_required && (
                          <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                        )}
                      </Typography>

                      {/* Editable criterion title (if editable_label is present) */}
                      {editableLabel !== undefined && editableLabel !== null && (
                        <Box sx={{ mb: 1 }}>
                          <TextField
                            fullWidth
                            value={criterionTitleValue}
                            onChange={(e) =>
                              setCriterionTitles({
                                ...criterionTitles,
                                [question.question_id]: e.target.value,
                              })
                            }
                            placeholder="Enter criterion name"
                            sx={{
                              ...INPUT_FIELD_STYLES,
                              "& .MuiOutlinedInput-root": {
                                ...INPUT_FIELD_STYLES["& .MuiOutlinedInput-root"],
                                fontWeight: 500,
                              },
                            }}
                          />
                        </Box>
                      )}

                      {/* Weight field row */}
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ flex: 1 }} />
                        {/* Weight field inline */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "13px",
                              color: "#666",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Weight:
                          </Typography>
                          <TextField
                            type="number"
                            value={technoCommercialAnswers[nextQuestion.question_id] || ""}
                            onChange={(e) =>
                              setTechnoCommercialAnswers({
                                ...technoCommercialAnswers,
                                [nextQuestion.question_id]: e.target.value,
                              })
                            }
                            placeholder="0-100"
                            sx={{
                              width: '80px',
                              ...INPUT_FIELD_STYLES,
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Criterion description textarea - full width */}
                      <Box>
                          <Box
                            sx={{
                              position: "relative",
                              display: "flex",
                              alignItems: "flex-start",
                            }}
                          >
                            <TextField
                              fullWidth
                              multiline
                              minRows={question.question_type === "text_long" ? 3 : 1}
                              type={question.question_type === "date" ? "date" : "text"}
                              value={technoCommercialAnswers[question.question_id] || ""}
                              onChange={(e) =>
                                setTechnoCommercialAnswers({
                                  ...technoCommercialAnswers,
                                  [question.question_id]: e.target.value,
                                })
                              }
                              placeholder={question.help_text || ""}
                              sx={{
                                ...INPUT_FIELD_STYLES,
                                "& .MuiOutlinedInput-root": {
                                  ...INPUT_FIELD_STYLES["& .MuiOutlinedInput-root"],
                                  paddingRight: technoCommercialAnswers[question.question_id]
                                    ? "36px"
                                    : "10px",
                                },
                              }}
                            />
                            {technoCommercialAnswers[question.question_id] && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const newAnswers = { ...technoCommercialAnswers };
                                  delete newAnswers[question.question_id];
                                  setTechnoCommercialAnswers(newAnswers);
                                }}
                                sx={{
                                  position: "absolute",
                                  right: "8px",
                                  top: "8px",
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                    </Box>
                  );
                }

                // Handle select dropdowns for specific fields
                if (question.question_id === 'payment_terms' ||
                    question.question_id === 'priority_level' ||
                    question.question_id === 'sourcing_strategy') {
                  return (
                    <Box key={question.question_id} sx={{ mb: 2 }}>
                      <Typography
                        component="p"
                        sx={{
                          fontSize: "13px",
                          color: "#00a888",
                          lineHeight: 1.4,
                          margin: "0 0 3px 0 !important",
                        }}
                      >
                        {question.question_text}
                        {question.is_required && (
                          <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                        )}
                      </Typography>
                      <FormControl fullWidth sx={{ ...INPUT_FIELD_STYLES }}>
                        <Select
                          value={technoCommercialAnswers[question.question_id] || ""}
                          onChange={(e) =>
                            setTechnoCommercialAnswers({
                              ...technoCommercialAnswers,
                              [question.question_id]: e.target.value,
                            })
                          }
                          displayEmpty
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#e0e0e0",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#00a888",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#00a888",
                              borderWidth: "1px",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            <em>{question.help_text || "Select an option"}</em>
                          </MenuItem>
                          {question.options && question.options.map((option: string) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  );
                }

                // Regular question rendering
                return (
                  <Box key={question.question_id} sx={{ mb: 2 }}>
                    <Typography
                      component="p"
                      sx={{
                        fontSize: "13px",
                        color: "#00a888",
                        lineHeight: 1.4,
                        margin: "0 0 3px 0 !important",
                      }}
                    >
                      {question.question_text}
                      {question.is_required && (
                        <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                      )}
                    </Typography>
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "flex-start",
                      }}
                    >
                      <TextField
                        fullWidth
                        multiline
                        minRows={question.question_type === "text_long" ? 3 : 1}
                        type={question.question_type === "date" ? "date" : "text"}
                        value={technoCommercialAnswers[question.question_id] || ""}
                        onChange={(e) =>
                          setTechnoCommercialAnswers({
                            ...technoCommercialAnswers,
                            [question.question_id]: e.target.value,
                          })
                        }
                        placeholder={question.help_text || ""}
                        sx={{
                          ...INPUT_FIELD_STYLES,
                          "& .MuiOutlinedInput-root": {
                            ...INPUT_FIELD_STYLES["& .MuiOutlinedInput-root"],
                            paddingRight: technoCommercialAnswers[question.question_id]
                              ? "36px"
                              : "10px",
                          },
                        }}
                      />
                      {technoCommercialAnswers[question.question_id] && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newAnswers = { ...technoCommercialAnswers };
                            delete newAnswers[question.question_id];
                            setTechnoCommercialAnswers(newAnswers);
                          }}
                          sx={{
                            position: "absolute",
                            right: "8px",
                            top: "8px",
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}

                {/* Custom Criteria */}
                {customCriteria.map((criterion, index) => (
                  <Box key={criterion.id} sx={{ mb: 3, position: 'relative', p: 2, border: '1px solid #00a888', borderRadius: '8px', backgroundColor: '#f0fbf9' }}>
                    {/* Delete button */}
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteCriterion(criterion.id, true)}
                      sx={{
                        position: "absolute",
                        right: "8px",
                        top: "8px",
                        color: "#d32f2f",
                        "&:hover": {
                          backgroundColor: "rgba(211, 47, 47, 0.1)",
                        },
                      }}
                      title="Delete criterion"
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                    </IconButton>

                    {/* Criterion header */}
                    <Typography
                      component="p"
                      sx={{
                        fontSize: "13px",
                        color: "#00a888",
                        lineHeight: 1.4,
                        margin: "0 0 4px 0 !important",
                        fontWeight: 600,
                        paddingRight: '40px',
                      }}
                    >
                      Custom Criterion {index + 1}
                    </Typography>

                    {/* Criterion title */}
                    <Box sx={{ mb: 1 }}>
                      <TextField
                        fullWidth
                        value={criterion.title}
                        onChange={(e) => handleUpdateCustomCriterion(criterion.id, 'title', e.target.value)}
                        placeholder="Enter criterion name"
                        sx={{
                          ...INPUT_FIELD_STYLES,
                          "& .MuiOutlinedInput-root": {
                            ...INPUT_FIELD_STYLES["& .MuiOutlinedInput-root"],
                            fontWeight: 500,
                          },
                        }}
                      />
                    </Box>

                    {/* Weight field row */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ flex: 1 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "13px",
                            color: "#666",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Weight:
                        </Typography>
                        <TextField
                          type="number"
                          value={criterion.weight}
                          onChange={(e) => handleUpdateCustomCriterion(criterion.id, 'weight', e.target.value)}
                          placeholder="0-100"
                          sx={{
                            width: '80px',
                            ...INPUT_FIELD_STYLES,
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Criterion description */}
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={criterion.description}
                        onChange={(e) => handleUpdateCustomCriterion(criterion.id, 'description', e.target.value)}
                        placeholder="Enter criterion description"
                        sx={INPUT_FIELD_STYLES}
                      />
                    </Box>
                  </Box>
                ))}

                {/* Add New Criterion Button */}
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleAddCriterion}
                    startIcon={<AddIcon />}
                    sx={{
                      textTransform: "none",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#00a888",
                      borderColor: "#00a888",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      "&:hover": {
                        borderColor: "#008f75",
                        backgroundColor: "#f0fbf9",
                      },
                    }}
                  >
                    Add New Criterion
                  </Button>
                </Box>
              </Box>
            </div>

            {/* Bottom Navigation */}
            <div className={styles.rfqBottomNav}>
              <Button
                variant="outlined"
                className={styles.navBack}
                onClick={handleGoBack}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#666",
                  borderColor: "#e0e0e0",
                  borderRadius: "8px",
                  padding: "10px 24px",
                  "&:hover": {
                    borderColor: "#00a888",
                    backgroundColor: "#f0fbf9",
                  },
                }}
              >
                Back
              </Button>

              {/* Progress Dots - Empty for this step */}
              <div className={styles.navDots}></div>

              <Button
                variant="contained"
                className={styles.navContinue}
                onClick={handleSubmitTechnoCommercial}
                disabled={isSubmittingTechnoCommercial || !allRequiredAnswered}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  backgroundColor: "#00a888",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "10px 32px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {isSubmittingTechnoCommercial ? (
                  <>
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    Submitting...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle collecting_rfq_package_data (RFQ Package Document Form - Show One Question at a Time)
  if (status === "collecting_rfq_package_data") {
    // Get current question
    const currentQuestion = allQuestions[currentRfqPackageQuestionIndex];
    const isLastQuestion = currentRfqPackageQuestionIndex === allQuestions.length - 1;
    const currentAnswer = rfqPackageFormAnswers[currentQuestion?.question_id] || "";
    const isCurrentQuestionAnswered = currentQuestion?.is_required
      ? currentAnswer.trim() !== ""
      : true; // Optional questions don't need answer

    // Parse message to extract title
    const messageTitle = message
      ? message.split('\n')[0].replace(/\*\*/g, '').replace(/📦\s*/g, '').trim()
      : "RFQ Package Document Form";

    // Handler for moving to next question or submitting
    const handleContinueRfqPackage = () => {
      if (isLastQuestion) {
        // Submit all answers
        handleSubmitRfqPackageForm();
      } else {
        // Move to next question
        setCurrentRfqPackageQuestionIndex(prev => prev + 1);
      }
    };

    // Handler for going back to previous question
    const handleBackRfqPackage = () => {
      if (currentRfqPackageQuestionIndex > 0) {
        setCurrentRfqPackageQuestionIndex(prev => prev - 1);
      } else {
        handleGoBack();
      }
    };

    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          <div className={styles.rfqContentCard}>
            {/* Top Bar */}
            <div className={styles.rfqTopBar}>
              <span className={styles.questionProgress}>RFQ Package Data</span>
              <span className={styles.questionsRemaining}>
                Question {currentRfqPackageQuestionIndex + 1} / {allQuestions.length}
              </span>
            </div>

            {/* Title */}
            <h1 className={styles.rfqContentTitle}>{messageTitle}</h1>

            {/* Scrollable Questions Area */}
            <div className={styles.rfqContentScrollWrapper}>
              <Box>
                {currentQuestion && (() => {
                  // Handle select dropdowns
                  if (currentQuestion.question_type === "select" && currentQuestion.options && currentQuestion.options.length > 0) {
                    return (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          component="p"
                          sx={{
                            fontSize: "13px",
                            color: "#00a888",
                            lineHeight: 1.4,
                            margin: "0 0 3px 0 !important",
                          }}
                        >
                          {currentQuestion.question_text}
                          {currentQuestion.is_required && (
                            <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                          )}
                        </Typography>
                        <FormControl fullWidth sx={{ ...INPUT_FIELD_STYLES }}>
                          <Select
                            value={currentAnswer}
                            onChange={(e) =>
                              setRfqPackageFormAnswers({
                                ...rfqPackageFormAnswers,
                                [currentQuestion.question_id]: e.target.value,
                              })
                            }
                            displayEmpty
                            sx={{
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#e0e0e0",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#00a888",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#00a888",
                                borderWidth: "1px",
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              <em>{currentQuestion.help_text || "Select an option"}</em>
                            </MenuItem>
                            {currentQuestion.options.map((option: string) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    );
                  }

                  // Handle regular text fields
                  return (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        component="p"
                        sx={{
                          fontSize: "13px",
                          color: "#00a888",
                          lineHeight: 1.4,
                          margin: "0 0 3px 0 !important",
                        }}
                      >
                        {currentQuestion.question_text}
                        {currentQuestion.is_required && (
                          <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                        )}
                      </Typography>
                      <Box
                        sx={{
                          position: "relative",
                          display: "flex",
                          alignItems: "flex-start",
                        }}
                      >
                        <TextField
                          fullWidth
                          multiline
                          minRows={currentQuestion.question_type === "text_long" ? 3 : 1}
                          type={currentQuestion.question_type === "date" ? "date" : "text"}
                          value={currentAnswer}
                          onChange={(e) =>
                            setRfqPackageFormAnswers({
                              ...rfqPackageFormAnswers,
                              [currentQuestion.question_id]: e.target.value,
                            })
                          }
                          placeholder={currentQuestion.help_text || ""}
                          sx={{
                            ...INPUT_FIELD_STYLES,
                            "& .MuiOutlinedInput-root": {
                              ...INPUT_FIELD_STYLES["& .MuiOutlinedInput-root"],
                              paddingRight: currentAnswer ? "36px" : "10px",
                            },
                          }}
                        />
                        {currentAnswer && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newAnswers = { ...rfqPackageFormAnswers };
                              delete newAnswers[currentQuestion.question_id];
                              setRfqPackageFormAnswers(newAnswers);
                            }}
                            sx={{
                              position: "absolute",
                              right: "8px",
                              top: "8px",
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            </div>

            {/* Bottom Navigation */}
            <div className={styles.rfqBottomNav}>
              <Button
                variant="outlined"
                className={styles.navBack}
                onClick={handleBackRfqPackage}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#666",
                  borderColor: "#e0e0e0",
                  borderRadius: "8px",
                  padding: "10px 24px",
                  "&:hover": {
                    borderColor: "#00a888",
                    backgroundColor: "#f0fbf9",
                  },
                }}
              >
                Back
              </Button>

              {/* Progress Dots - Empty for this step */}
              <div className={styles.navDots}></div>

              <Button
                variant="contained"
                className={styles.navContinue}
                onClick={handleContinueRfqPackage}
                disabled={isSubmittingRfqPackageForm || !isCurrentQuestionAnswered}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  backgroundColor: "#00a888",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "10px 32px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {isSubmittingRfqPackageForm ? (
                  <>
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    Submitting...
                  </>
                ) : isLastQuestion ? (
                  "Submit"
                ) : (
                  "Continue"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle asking_rfq_package (Technical Template Generated + RFQ Package Question)
  if (status === "asking_rfq_package") {
    // Parse message for title and sections
    const messageParts = message.split('\n\n');
    const successTitle = messageParts[0] || "✅ Technical Evaluation Template Generated!";
    const documentInfo = messageParts[1] || "";
    const nextStepInfo = messageParts[2] || "";

    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          <div className={styles.rfqContentCard}>
            {/* Success Icon */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: '#e8f5f1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 48, color: '#00a888' }} />
              </Box>
              <h1 style={{ fontSize: '24px', fontWeight: 600, color: '#333', margin: '0 0 8px 0' }}>
                {successTitle.replace(/✅\s*\*\*/g, '').replace(/\*\*/g, '').trim()}
              </h1>
              <p style={{ fontSize: '15px', color: '#666', margin: 0 }}>
                1 Document Created
              </p>
            </Box>

            {/* Document Download Card */}
            {generatedDocumentPath && (
              <Box
                sx={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  padding: '20px',
                  mb: 4,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 28, color: '#00a888' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#333', mb: 0.5 }}>
                        {(() => {
                          const filename = generatedDocumentPath.split(/[\\\/]/).pop();
                          console.log("[RFQ Package] Generated Document Path:", generatedDocumentPath);
                          console.log("[RFQ Package] Extracted Filename:", filename);
                          return filename;
                        })()}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#666' }}>
                        Technical Evaluation Template
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
                      onClick={() => {
                        console.log("[RFQ Package] Preview button clicked!");
                        console.log("[RFQ Package] Calling handlePreviewDocument with:", generatedDocumentPath);
                        handlePreviewDocument(generatedDocumentPath);
                      }}
                      sx={{
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#666',
                        borderColor: '#e0e0e0',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        '&:hover': {
                          borderColor: '#00a888',
                          backgroundColor: '#f0fbf9',
                        },
                      }}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                      onClick={() => handleDownloadDocument(generatedDocumentPath)}
                      disabled={isDownloading}
                      sx={{
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 600,
                        backgroundColor: '#00a888',
                        color: '#fff',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#008f75',
                          boxShadow: '0 4px 12px rgba(0, 168, 136, 0.2)',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#e0e0e0',
                          color: '#9e9e9e',
                        },
                      }}
                    >
                      {isDownloading ? (
                        <>
                          <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                          Downloading...
                        </>
                      ) : (
                        'Download'
                      )}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Info Box */}
            <Box
              sx={{
                backgroundColor: '#fffbf0',
                border: '1px solid #ffeaa7',
                borderRadius: '8px',
                padding: '16px',
                mb: 4,
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#856404', fontWeight: 500 }}>
                {nextStepInfo.replace(/📦\s*\*\*/g, '').replace(/\*\*/g, '').trim()}
              </Typography>
            </Box>

            {/* Question */}
            {allQuestions.length > 0 && (
              <div>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#00a888',
                    fontWeight: 500,
                    margin: '0 0 12px 0',
                  }}
                >
                  {allQuestions[0].question_text}
                  {allQuestions[0].is_required && (
                    <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                  )}
                </p>

                {/* Yes/No Radio Buttons */}
                <RadioGroup
                  value={rfqPackageAnswer}
                  onChange={(e) => setRfqPackageAnswer(e.target.value)}
                >
                  {allQuestions[0].options.map((option: string) => (
                    <FormControlLabel
                      key={option}
                      value={option}
                      control={
                        <Radio
                          sx={{
                            color: "#00a888",
                            "&.Mui-checked": {
                              color: "#00a888",
                            },
                          }}
                        />
                      }
                      label={option}
                      sx={{
                        mb: 1,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "15px",
                          color: "#333",
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Bottom Navigation */}
            <div className={styles.rfqBottomNav}>
              <Button
                variant="outlined"
                className={styles.navBack}
                onClick={handleGoBack}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#666",
                  borderColor: "#e0e0e0",
                  borderRadius: "8px",
                  padding: "10px 24px",
                  "&:hover": {
                    borderColor: "#00a888",
                    backgroundColor: "#f0fbf9",
                  },
                }}
              >
                Back
              </Button>

              {/* Progress Dots - Empty for this step */}
              <div className={styles.navDots}></div>

              <Button
                variant="contained"
                className={styles.navContinue}
                onClick={handleSubmitRfqPackage}
                disabled={isSubmittingRfqPackage || !rfqPackageAnswer}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  backgroundColor: "#00a888",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "10px 32px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {isSubmittingRfqPackage ? (
                  <>
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    Submitting...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Preview Modal */}
        <Dialog
          open={previewModalOpen}
          onClose={handleClosePreview}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Document Preview: {previewFile?.name}</span>
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {loadingPreview ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
              </Box>
            ) : previewFile?.type === 'excel' || previewFile?.type === 'word' ? (
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            ) : previewFile?.type === 'pdf' ? (
              <iframe
                src={previewFile.url}
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="PDF Preview"
              />
            ) : (
              <Typography>Preview not available for this file type.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePreview}>Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  // Handle Step 2: Specific Questions (Goods/Service structured form)
  if (status === "step2_specific_questions") {
    const isFormValid = lineItems.some(
      (item) => item.title.trim() && item.quantity.trim() && item.uom.trim()
    );

    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          <div className={styles.rfqContentCard}>

            {/* Title and Description */}
            {message ? (
              <Box sx={{ mb: 3 }}>
                <h1 className={styles.rfqContentTitle}>
                  {message.split('\n')[0].replace(/📦\s*\*\*/g, '').replace(/\*\*/g, '').trim()}
                </h1>
                {message.split('\n\n')[1] && (
                  <ReactMarkdown
                    components={markdownSubtitleComponents}
                  >
                    {message.split('\n\n')[1].trim()}
                  </ReactMarkdown>
                )}
              </Box>
            ) : (
              <h1 className={styles.rfqContentTitle}>Add the goods/products you need to procure</h1>
            )}

          {/* Line Items */}
          {lineItems.map((item, index) => (
            <Box
              key={item.id}
              sx={{
                mb: 3,
                p: 3,
                backgroundColor: "#fafafa",
                borderRadius: "8px",
                position: "relative",
              }}
            >
              {/* Title and Remove Button */}
              <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    fontSize: "16px",
                  }}
                >
                  {item.title || `${procurementType === "Service" ? "Service" : "Item"} ${index + 1}`}
                </Typography>
                {lineItems.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveItem(item.id)}
                    sx={{
                      color: "#d32f2f",
                      "&:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.08)",
                      },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {/* Type Selection */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#333",
                    mb: 0.5,
                  }}
                >
                  Type <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                </Typography>
                <RadioGroup
                  row
                  value={procurementType}
                  onChange={(e) => handleProcurementTypeChange(e.target.value as "Goods" | "Service")}
                  sx={{ gap: 1 }}
                >
                  <FormControlLabel
                    value="Goods"
                    control={<Radio size="small" />}
                    label="Goods"
                    sx={{ mr: 0 }}
                  />
                  <FormControlLabel
                    value="Service"
                    control={<Radio size="small" />}
                    label="Service"
                    sx={{ mr: 0 }}
                  />
                </RadioGroup>
              </Box>

              {/* Title Field */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#333",
                    mb: 0.5,
                  }}
                >
                  {procurementType === "Service" ? "Service description" : "Item name"}{" "}
                  <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                </Typography>
                <TextField
                  fullWidth
                  value={item.title}
                  onChange={(e) => handleUpdateItem(item.id, "title", e.target.value)}
                  placeholder={procurementType === "Service" ? "Enter service description" : "Enter item name"}
                  sx={{
                    ...INPUT_FIELD_STYLES,
                    "& .MuiOutlinedInput-root": {
                      ...INPUT_FIELD_STYLES["& .MuiOutlinedInput-root"],
                      backgroundColor: "#fff",
                    },
                  }}
                />
              </Box>

              {/* Manufacturer Part Number - Only show for Goods */}
              {procurementType === "Goods" && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#333",
                      mb: 0.5,
                    }}
                  >
                    Manufacturer part number
                  </Typography>
                  <TextField
                    fullWidth
                    value={item.manufacturer_part_number}
                    onChange={(e) =>
                      handleUpdateItem(item.id, "manufacturer_part_number", e.target.value)
                    }
                    placeholder="Enter part number"
                    sx={{
                      ...INPUT_FIELD_STYLES,
                      "& .MuiOutlinedInput-root": {
                        ...INPUT_FIELD_STYLES["& .MuiOutlinedInput-root"],
                        backgroundColor: "#fff",
                      },
                    }}
                  />
                </Box>
              )}

              {/* Quantity, UoM, Unit Price Row */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#333",
                      mb: 0.5,
                    }}
                  >
                    Quantity <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleUpdateItem(item.id, "quantity", e.target.value)}
                    placeholder="0"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                        fontSize: "13px",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "10px 12px",
                      },
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#333",
                      mb: 0.5,
                    }}
                  >
                    {procurementType === "Service" ? "Unit" : "UoM"}{" "}
                    <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={item.uom}
                      onChange={(e) => {
                        console.log("UoM Select onChange fired, value:", e.target.value);
                        handleUpdateItem(item.id, "uom", e.target.value);
                      }}
                      displayEmpty
                      sx={{
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                        fontSize: "13px",
                        "& .MuiSelect-select": {
                          padding: "10px 12px",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select {procurementType === "Service" ? "unit" : "UoM"}
                      </MenuItem>
                      {(() => {
                        const serviceOptions = ["each", "hour", "day", "week", "month", "year", "project", "fte"];
                        const goodsOptions = ["each", "unit", "piece", "set", "box", "kg", "liter"];
                        const currentOptions = procurementType === "Service" ? serviceOptions : goodsOptions;

                        // If current value exists but is not in the options list, add it temporarily
                        const optionsToRender = [...currentOptions];
                        if (item.uom && !currentOptions.includes(item.uom)) {
                          optionsToRender.push(item.uom);
                        }

                        return optionsToRender.map(option => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ));
                      })()}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#333",
                      mb: 0.5,
                    }}
                  >
                    {procurementType === "Service" ? "Rate" : "Unit price"}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={item.unit_price}
                    onChange={(e) => handleUpdateItem(item.id, "unit_price", e.target.value)}
                    placeholder="0.00"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                        fontSize: "13px",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "10px 12px",
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Start Date and End Date - Only show for Services */}
              {procurementType === "Service" && (
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#333",
                        mb: 0.5,
                      }}
                    >
                      Start date
                    </Typography>
                    <TextField
                      fullWidth
                      type="date"
                      value={item.start_date}
                      onChange={(e) => handleUpdateItem(item.id, "start_date", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "6px",
                          backgroundColor: "#fff",
                          fontSize: "13px",
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "10px 12px",
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#333",
                        mb: 0.5,
                      }}
                    >
                      End date
                    </Typography>
                    <TextField
                      fullWidth
                      type="date"
                      value={item.end_date}
                      onChange={(e) => handleUpdateItem(item.id, "end_date", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "6px",
                          backgroundColor: "#fff",
                          fontSize: "13px",
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "10px 12px",
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          ))}

          {/* Add New Section Button */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              sx={{
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                color: "#00a888",
                borderColor: "#00a888",
                borderStyle: "dashed",
                borderRadius: "8px",
                padding: "10px 20px",
                width: "100%",
                "&:hover": {
                  borderColor: "#008f75",
                  backgroundColor: "#f0fbf9",
                  borderStyle: "dashed",
                },
              }}
            >
              Add New {procurementType === "Service" ? "Service" : "Item"}
            </Button>
          </Box>

            {/* Total Display */}
            {calculateTotal() > 0 && (
              <Box sx={{ mb: 3, textAlign: "right" }}>
                <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#333" }}>
                  Total: AED {calculateTotal().toFixed(2)}
                </Typography>
              </Box>
            )}

            {/* Bottom Navigation */}
            <div className={styles.rfqBottomNav}>
              <Button
                variant="outlined"
                className={styles.navBack}
                onClick={handleGoBack}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#666",
                  borderColor: "#e0e0e0",
                  borderRadius: "8px",
                  padding: "10px 24px",
                  "&:hover": {
                    borderColor: "#00a888",
                    backgroundColor: "#f0fbf9",
                  },
                }}
              >
                Back
              </Button>

              {/* Progress Dots */}
              <div className={styles.navDots}>
                <span className={styles.dot}></span>
                <span className={`${styles.dot} ${styles.active}`}></span>
                <span className={styles.dot}></span>
              </div>

              <Button
                variant="contained"
                className={styles.navContinue}
                onClick={handleSubmitGoodsForm}
                disabled={isProceedingToStep3 || !isFormValid}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  backgroundColor: "#00a888",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "10px 32px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {isProceedingToStep3 ? (
                  <>
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle Step 3: SOW Questions
  if (status === "step3_sow_questions") {
    const allRequiredAnswered = allQuestions
      .filter((q: Question) => q.is_required)
      .every((q: Question) => step3Answers[q.question_id]?.trim());

    return (
      <div className={styles.pressReleaseContent}>
        {/* Header Navigation */}
        {/* <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              color: "#666",
              fontSize: "13px",
              padding: "6px 12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            To the requests
          </Button>
          <Button
            startIcon={<History sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              color: "#666",
              fontSize: "13px",
              padding: "6px 12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            Start request over
          </Button>
          <Button
            onClick={handleGoBack}
            disabled={!rfqCurrentStepNumber || rfqCurrentStepNumber <= 1}
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              color: "#666",
              fontSize: "13px",
              padding: "6px 12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            Go back
          </Button>
          {isViewingHistoricalStep && (
            <Button
              onClick={handleGoForward}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
              sx={{
                textTransform: "none",
                color: "#00a888",
                fontSize: "13px",
                padding: "6px 12px",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "rgba(0, 168, 136, 0.08)",
                },
              }}
            >
              Resume to latest
            </Button>
          )}
        </Box> */}

        <Paper
          elevation={0}
          sx={{
            padding: "32px",
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          {/* Message */}
          <Box
            sx={{
              mb: 3,
              "& p:first-of-type": {
                fontSize: "18px",
                fontWeight: 600,
                color: "#00a888",
                marginBottom: "8px",
              },
              "& p:last-of-type": {
                fontSize: "15px",
                color: "#666",
                margin: 0,
              },
              "& strong": {
                fontWeight: 700,
                color: "#00a888",
              },
            }}
          >
            <ReactMarkdown>
              {message || "I attempted to answer some questions that suppliers are likely ask you during the sourcing event. Can you review and adjust the answer?"}
            </ReactMarkdown>
          </Box>

          {/* Request Title */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#333",
              mb: 3,
              fontSize: "18px",
            }}
          >
            {collectedData?.request_title || collectedData?.category || lineItems[0]?.title || "RFQ Request"}
          </Typography>

          {/* Questions and Answers */}
          <Box sx={{ mb: 3 }}>
            {allQuestions.map((question: any, index: number) => (
              <Box key={question.question_id} sx={{ mb: 2 }}>
                <Typography
                  component="p"
                  sx={{
                    fontSize: "13px",
                    color: "#00a888",
                    lineHeight: 1.4,
                    margin: "0 0 3px 0 !important",
                  }}
                >
                  {question.question_text}
                  {question.is_required && (
                    <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                  )}
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    value={step3Answers[question.question_id] || ""}
                    onChange={(e) =>
                      setStep3Answers({
                        ...step3Answers,
                        [question.question_id]: e.target.value,
                      })
                    }
                    sx={{
                      ...INPUT_FIELD_STYLES,
                      "& .MuiOutlinedInput-root": {
                        ...INPUT_FIELD_STYLES["& .MuiOutlinedInput-root"],
                        paddingRight: step3Answers[question.question_id]
                          ? "36px"
                          : "10px",
                      },
                    }}
                  />
                  {step3Answers[question.question_id] && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newAnswers = { ...step3Answers };
                        delete newAnswers[question.question_id];
                        setStep3Answers(newAnswers);
                      }}
                      sx={{
                        position: "absolute",
                        right: "8px",
                        top: "8px",
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Proceed Button */}
          <Button
            variant="contained"
            onClick={handleGenerateDocument}
            disabled={isGeneratingDocument || !allRequiredAnswered}
            sx={{
              backgroundColor: "#00a888",
              color: "#fff",
              padding: "10px 24px",
              fontSize: "14px",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "6px",
              boxShadow: "0 2px 8px rgba(0, 168, 136, 0.3)",
              "&:hover": {
                backgroundColor: "#008f75",
                boxShadow: "0 4px 12px rgba(0, 168, 136, 0.4)",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            {isGeneratingDocument ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Generating...
              </>
            ) : (
              "Proceed with request"
            )}
          </Button>
        </Paper>
      </div>
    );
  }

  // Handle Step 2: RFQ Questions - AI suggested answers for review
  if (status === "step2_rfq_questions") {
    const allRequiredAnswered = allQuestions
      .filter((q: Question) => q.is_required)
      .every((q: Question) => step2Answers[q.question_id]?.trim());

    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          <div className={styles.rfqContentCard}>
            {/* Top Bar */}
            <div className={styles.rfqTopBar}>
              <span className={styles.questionProgress}>Step 2: RFQ Questions</span>
              <span className={styles.questionsRemaining}>{allQuestions.length} questions</span>
            </div>

            {/* Title and Description */}
            {message ? (
              <Box sx={{ mb: 3 }}>
                <h1 className={styles.rfqContentTitle}>
                  {message.split('\n')[0].replace(/📦\s*\*\*/g, '').replace(/\*\*/g, '').trim()}
                </h1>
                {message.split('\n\n')[1] && (
                  <ReactMarkdown
                    components={markdownSubtitleComponents}
                  >
                    {message.split('\n\n')[1].trim()}
                  </ReactMarkdown>
                )}
              </Box>
            ) : (
              <h1 className={styles.rfqContentTitle}>Review AI-suggested answers</h1>
            )}

            {/* Questions */}
            <Box sx={{ mb: 3 }}>
              {allQuestions.map((question: any, index: number) => (
                <div key={question.question_id} className={styles.rfqQuestion}>
                  <p className={styles.questionText}>
                    {question.question_text}
                    {question.is_required && (
                      <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                    )}
                  </p>
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    value={step2Answers[question.question_id] || ""}
                    onChange={(e) =>
                      setStep2Answers({
                        ...step2Answers,
                        [question.question_id]: e.target.value,
                      })
                    }
                    placeholder={question.help_text || ""}
                    className={styles.questionField}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#fafafa",
                        fontSize: "15px",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "14px 16px",
                      },
                    }}
                  />
                </div>
              ))}
            </Box>

            {/* Bottom Navigation */}
            <div className={styles.rfqBottomNav}>
              <Button
                variant="outlined"
                className={styles.navBack}
                onClick={handleGoBack}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#666",
                  borderColor: "#e0e0e0",
                  borderRadius: "8px",
                  padding: "10px 24px",
                  "&:hover": {
                    borderColor: "#00a888",
                    backgroundColor: "#f0fbf9",
                  },
                }}
              >
                Back
              </Button>

              {/* Progress Dots */}
              <div className={styles.navDots}>
                <span className={styles.dot}></span>
                <span className={`${styles.dot} ${styles.active}`}></span>
                <span className={styles.dot}></span>
              </div>

              <Button
                variant="contained"
                className={styles.navContinue}
                onClick={handleProceedToStep3}
                disabled={isProceedingToStep3 || !allRequiredAnswered}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  backgroundColor: "#00a888",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "10px 32px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {isProceedingToStep3 ? (
                  <>
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle awaiting_sow_confirmation - Show structured preview for review
  if (status === "awaiting_sow_confirmation" && structuredPreview) {
    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          <div className={styles.rfqContentCard}>
            {/* Top Bar */}
            <div className={styles.rfqTopBar}>
              <span className={styles.questionProgress}>SOW Confirmation</span>
              <span className={styles.questionsRemaining}>
                {structuredPreview.sections?.length || 0} sections
              </span>
            </div>

            {/* Title and Description */}
            {message ? (
              <Box sx={{ mb: 3 }}>
                <h1 className={styles.rfqContentTitle}>
                  {message.split('\n')[0].replace(/📋\s*\*\*/g, '').replace(/\*\*/g, '').trim()}
                </h1>
                {message.split('\n\n')[1] && (
                  <ReactMarkdown
                    components={markdownSubtitleComponents}
                  >
                    {message.split('\n\n')[1].trim()}
                  </ReactMarkdown>
                )}
              </Box>
            ) : (
              <h1 className={styles.rfqContentTitle}>SOW Preview</h1>
            )}

            {/* Structured Preview Sections */}
            <Box sx={{ mb: 3 }}>
              {structuredPreview.sections?.map((section: any, index: number) => {
                const isOpen = openSections[section.section_id] !== false; // Default to open
                return (
                  <Box
                    key={section.section_id}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      mb: 2,
                      overflow: "hidden",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        background: "#f8f9fa",
                        padding: "16px 20px",
                        cursor: "pointer",
                        transition: "background 0.2s",
                        "&:hover": {
                          background: "#f0f1f3",
                        },
                      }}
                      onClick={() => {
                        setOpenSections({
                          ...openSections,
                          [section.section_id]: !isOpen,
                        });
                      }}
                    >
                      <Typography
                        component="h3"
                        sx={{
                          fontSize: "15px",
                          fontWeight: 600,
                          color: "#333",
                          margin: "0 !important",
                          padding: 0,
                          lineHeight: 1.4,
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {isOpen ? (
                          <ExpandLessIcon
                            sx={{
                              fontSize: "20px",
                              color: "#666",
                            }}
                          />
                        ) : (
                          <ExpandMoreIcon
                            sx={{
                              fontSize: "20px",
                              color: "#666",
                            }}
                          />
                        )}
                        {section.title}
                      </Typography>
                    </Box>
                    {isOpen && (
                      <Box
                        sx={{
                          padding: "20px",
                          background: "#ffffff",
                        }}
                      >
                        {section.description && (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#00a888",
                              mb: 1.5,
                              fontStyle: "italic",
                            }}
                          >
                            {section.description}
                          </Typography>
                        )}

                        <Box
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                            fontFamily: "inherit",
                            backgroundColor: "#fafafa",
                            padding: "16px",
                            borderRadius: "8px",
                            border: "1px solid #f0f0f0",
                            "& strong": { fontWeight: 600 },
                            "& p": {
                              margin: "0 0 12px 0",
                              lineHeight: 1.6,
                              "&:last-child": { margin: 0 },
                            },
                            "& ol, & ul": {
                              margin: "0 0 12px 0",
                              paddingLeft: "24px",
                              "&:last-child": { margin: 0 },
                            },
                            "& li": {
                              margin: "6px 0",
                              lineHeight: 1.6,
                            },
                            "& ol li": {
                              paddingLeft: "4px",
                            },
                          }}
                        >
                          <ReactMarkdown>
                            {normalizeMarkdownContent(section.content.replace(/(?<!\n)\n(?!\n)/g, '\n\n'))}
                          </ReactMarkdown>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Bottom Navigation */}
            <div className={styles.rfqBottomNav}>
              <Button
                variant="outlined"
                className={styles.navBack}
                onClick={handleGoBack}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#666",
                  borderColor: "#e0e0e0",
                  borderRadius: "8px",
                  padding: "10px 24px",
                  "&:hover": {
                    borderColor: "#00a888",
                    backgroundColor: "#f0fbf9",
                  },
                }}
              >
                Back
              </Button>

              {/* Progress Dots - Empty for this step */}
              <div className={styles.navDots}></div>

              <Button
                variant="contained"
                className={styles.navContinue}
                onClick={handleConfirmAndGenerateDocument}
                disabled={isConfirmingDocument}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  backgroundColor: "#00a888",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "10px 32px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {isConfirmingDocument ? (
                  <>
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    Generating...
                  </>
                ) : (
                  "Confirm & Generate"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show completion state (new message from backend with status="completed")
  if (status === "completed") {
    // Parse message to extract document information and build document list
    const documents: Array<{ name: string; path: string; displayName: string; icon: string }> = [];

    // Add technical template if present
    if (generatedDocumentPath) {
      const filename = generatedDocumentPath.split(/[\\\/]/).pop() || "";
      documents.push({
        name: filename,
        path: generatedDocumentPath,
        displayName: "Technical Evaluation Template",
        icon: "📊"
      });
    }

    // Add RFQ document if present (stored in generated_txt_path)
    const generatedTxtPath = result?.generated_txt_path || null;
    if (generatedTxtPath) {
      const filename = generatedTxtPath.split(/[\\\/]/).pop() || "";
      documents.push({
        name: filename,
        path: generatedTxtPath,
        displayName: "RFQ Package Document",
        icon: "📄"
      });
    }

    // Parse success message
    const successTitle = message
      ? message.split('\n')[0].replace(/✅\s*\*\*/g, '').replace(/\*\*/g, '').trim()
      : "RFQ Package Generated Successfully!";

    return (
      <div className={styles.rfqBuilderContainer}>
        {/* RFQ Builder Sidebar */}
        <RfqBuilderSidebar />

        {/* Main Content Area */}
        <div className={styles.rfqBuilderMainContent}>
          <div className={styles.rfqContentCard}>
            {/* Success Icon */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: '#e8f5f1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 48, color: '#00a888' }} />
              </Box>
              <h1 style={{ fontSize: '24px', fontWeight: 600, color: '#333', margin: '0 0 8px 0' }}>
                {successTitle}
              </h1>
              <p style={{ fontSize: '15px', color: '#666', margin: 0 }}>
                {documents.length} {documents.length === 1 ? 'Document' : 'Documents'} Created
              </p>
            </Box>

            {/* Document Download Cards */}
            {documents.map((doc, index) => (
              <Box
                key={index}
                sx={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  padding: '20px',
                  mb: 3,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 28, color: '#00a888' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#333', mb: 0.5 }}>
                        {doc.icon} {doc.name}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#666' }}>
                        {doc.displayName}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
                      onClick={() => handlePreviewDocument(doc.path)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#666',
                        borderColor: '#e0e0e0',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        '&:hover': {
                          borderColor: '#00a888',
                          backgroundColor: '#f0fbf9',
                        },
                      }}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                      onClick={() => handleDownloadDocument(doc.path)}
                      disabled={isDownloading}
                      sx={{
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 600,
                        backgroundColor: '#00a888',
                        color: '#fff',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#008f75',
                          boxShadow: '0 4px 12px rgba(0, 168, 136, 0.2)',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#e0e0e0',
                          color: '#9e9e9e',
                        },
                      }}
                    >
                      {isDownloading ? (
                        <>
                          <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                          Downloading...
                        </>
                      ) : (
                        'Download'
                      )}
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}

            {/* Info Box */}
            <Box
              sx={{
                backgroundColor: '#e8f5f1',
                border: '1px solid #b2e5de',
                borderRadius: '8px',
                padding: '16px',
                mb: 4,
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#004141', fontWeight: 500, lineHeight: 1.6 }}>
                💡 <strong>Next Steps:</strong> Your complete RFQ package is ready to send to vendors. Download all documents to begin your procurement process.
              </Typography>
            </Box>
          </div>
        </div>

        {/* Preview Modal */}
        <Dialog
          open={previewModalOpen}
          onClose={handleClosePreview}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              height: "90vh",
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#f0fbf9",
              borderBottom: "1px solid #b2e5de",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#004141" }}>
                Document Preview
              </Typography>
              {previewFile && (
                <Typography variant="body2" sx={{ color: "#666", marginTop: 0.5 }}>
                  {previewFile.name}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              padding: 0,
              backgroundColor: "#fafafa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {loadingPreview ? (
              <Box sx={{ textAlign: "center", padding: 4 }}>
                <CircularProgress size={48} sx={{ color: "#00a888" }} />
                <Typography sx={{ marginTop: 2, color: "#666" }}>
                  Loading preview...
                </Typography>
              </Box>
            ) : previewFile ? (
              previewFile.type === "pdf" ? (
                <iframe
                  src={previewFile.url}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  title="Document Preview"
                />
              ) : previewHtml ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    padding: 3,
                  }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 4,
                  }}
                >
                  <Typography variant="h6" sx={{ marginBottom: 2, color: "#666" }}>
                    {previewFile.type === "excel" && "📊 Excel Document"}
                    {previewFile.type === "word" && "📄 Word Document"}
                  </Typography>
                  <Typography sx={{ marginBottom: 3, color: "#666", textAlign: "center" }}>
                    Preview is not available for this file type in the browser.
                    <br />
                    Please download the file to view its contents.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      if (previewFile) {
                        handleDownload("evaluation_file", previewFile.name);
                      }
                    }}
                    sx={{
                      backgroundColor: "#00a888",
                      "&:hover": {
                        backgroundColor: "#008f75",
                      },
                    }}
                  >
                    Download {previewFile.name}
                  </Button>
                </Box>
              )
            ) : null}
          </DialogContent>
          <DialogActions sx={{ padding: 2, backgroundColor: "#f0fbf9" }}>
            {previewFile && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  if (previewFile) {
                    handleDownload("evaluation_file", previewFile.name);
                  }
                }}
                sx={{
                  backgroundColor: "#00a888",
                  "&:hover": {
                    backgroundColor: "#008f75",
                  },
                }}
              >
                Download
              </Button>
            )}
            <Button onClick={handleClosePreview} sx={{ color: "#666" }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  // Note: Removed intermediate success screen - backend immediately responds with awaiting_confirmation

  // Show data preview and confirmation state
  // console.log("Checking awaiting_confirmation condition:", {
  //   status,
  //   hasStructuredPreview: !!structuredPreview,
  //   structuredPreviewSections: structuredPreview?.sections?.length,
  //   hasCollectedData: Object.keys(collectedData).length > 0,
  // });

  if (status === "awaiting_confirmation") {
    // If no structured preview, create a basic one from collected data
    const displayPreview = structuredPreview || {
      sections: [
        {
          section_id: "all_answers",
          section_title: "All Collected Answers",
          fields: Object.entries(collectedData).map(([key, value]) => ({
            field_id: key,
            field_label: key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l: string) => l.toUpperCase()),
            field_value: value as string,
            editable: true,
          })),
        },
      ],
    };
    return (
      <div className={styles.pressReleaseContent}>
        {/* Historical Step Indicator */}
        {HistoricalStepBanner}

        {/* Header Message */}
        {message && (
          <Paper
            elevation={0}
            sx={{
              padding: "20px",
              marginBottom: "20px",
              backgroundColor: "#f0fbf9",
              border: "1px solid #b2e5de",
              borderRadius: "8px",
            }}
          >
            <Box
              sx={{
                fontSize: "15px",
                fontWeight: 500,
                color: "#004141",
                "& p": { margin: "8px 0" },
                "& strong": { fontWeight: 600 },
                "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
                "& li": { marginBottom: "4px" },
              }}
            >
              <ReactMarkdown>{message}</ReactMarkdown>
            </Box>
          </Paper>
        )}

        {/* Structured Preview with Sections */}
        <Box sx={{ marginBottom: 4 }}>
          {displayPreview.sections?.map(
            (section: any, sectionIndex: number) => {
              // console.log(
              //   `Rendering section ${sectionIndex}:`,
              //   section.section_title,
              //   `with ${section.fields?.length} fields`,
              // );
              return (
                <Accordion
                  key={section.section_id}
                  defaultExpanded={true}
                  sx={{
                    marginBottom: 3,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    border: "1px solid #d0e8e4",
                    "&:before": { display: "none" },
                    borderRadius: "8px !important",
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#00a888" }} />}
                    sx={{
                      backgroundColor: "#f0fbf9",
                      borderRadius: "8px 8px 0 0",
                      minHeight: "56px",
                      "& .MuiAccordionSummary-content": {
                        margin: "16px 0",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "17px",
                        fontWeight: 600,
                        color: "#004141",
                        letterSpacing: "0.2px",
                      }}
                    >
                      {section.section_title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      padding: "24px 24px 28px 24px",
                      backgroundColor: "#fafcfb",
                    }}
                  >
                    {section.fields?.map((field: any, fieldIndex: number) => {
                      const currentValue =
                        editedData[field.field_id] ||
                        collectedData[field.field_id] ||
                        field.field_value;
                      const isEditing = isEditingField === field.field_id;

                      return (
                        <Box
                          key={field.field_id}
                          sx={{
                            marginBottom:
                              fieldIndex < section.fields.length - 1 ? 4 : 0,
                            paddingBottom:
                              fieldIndex < section.fields.length - 1 ? 3 : 0,
                            borderBottom:
                              fieldIndex < section.fields.length - 1
                                ? "1px solid #f0f0f0"
                                : "none",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: 1.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#004141",
                                flex: 1,
                                lineHeight: 1.5,
                              }}
                            >
                              {field.field_label}
                            </Typography>
                            {field.editable && !isEditing && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setIsEditingField(field.field_id)
                                }
                                sx={{
                                  color: "#00a888",
                                  padding: "6px",
                                  backgroundColor: "#e0f2f1",
                                  "&:hover": {
                                    backgroundColor: "#b2dfdb",
                                  },
                                }}
                              >
                                <EditIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            )}
                          </Box>

                          {isEditing ? (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              <TextField
                                fullWidth
                                value={currentValue}
                                onChange={(e) =>
                                  handleFieldEdit(
                                    field.field_id,
                                    e.target.value,
                                  )
                                }
                                size="small"
                                autoFocus
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#00a888",
                                    },
                                  },
                                }}
                              />
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => setIsEditingField(null)}
                                sx={{
                                  backgroundColor: "#00a888",
                                  minWidth: "60px",
                                  "&:hover": {
                                    backgroundColor: "#008f75",
                                  },
                                }}
                              >
                                Done
                              </Button>
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: "15px",
                                color: "#2a312b",
                                backgroundColor: "#f9fafb",
                                padding: "14px 16px",
                                borderRadius: "6px",
                                border: "1px solid #e0e7ee",
                                lineHeight: 1.6,
                                fontWeight: 500,
                              }}
                            >
                              {currentValue}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              );
            },
          )}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            marginTop: 4,
            paddingTop: 3,
            borderTop: "1px solid #e0e7ee",
          }}
        >
          <Button
            variant="contained"
            onClick={handleConfirmData}
            disabled={isConfirming}
            startIcon={
              isConfirming ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )
            }
            sx={{
              backgroundColor: "#00a888",
              color: "#fff",
              padding: "12px 32px",
              fontSize: "15px",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 2px 8px rgba(0, 168, 136, 0.3)",
              "&:hover": {
                backgroundColor: "#008f75",
                boxShadow: "0 4px 12px rgba(0, 168, 136, 0.4)",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            {isConfirming ? "Confirming..." : "Confirm & Generate Document"}
          </Button>
        </Box>

        {/* Error Message */}
        {submitError && (
          <Paper
            elevation={0}
            sx={{
              padding: 2,
              marginTop: 2,
              backgroundColor: "#ffebee",
              border: "1px solid #ef9a9a",
              borderRadius: "8px",
            }}
          >
            <Typography sx={{ fontSize: "14px", color: "#c62828" }}>
              {submitError}
            </Typography>
          </Paper>
        )}
      </div>
    );
  }

  // VIEWING HISTORY - Show all answered questions
  if (isViewingHistory && questionHistory.length > 0) {
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, #f0fbf9 0%, #e0f7f4 100%)",
          py: 3,
          borderRadius: 2,
        }}
      >
        <Container maxWidth="md">
          <Fade in timeout={600}>
            <Box>
              {/* Header */}
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    margin: "0 auto 12px",
                    backgroundColor: "#d0f2ed",
                    color: "#00a888",
                  }}
                >
                  <History sx={{ fontSize: 24 }} />
                </Avatar>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{ color: "#1f2937" }}
                >
                  Your Answers ({answeredQuestionsCount})
                </Typography>
                <Button
                  size="small"
                  startIcon={<ArrowForward />}
                  onClick={() => {
                    setIsViewingHistory(false);
                    setEditedAnswers({});
                    setSubmitError(null);
                  }}
                  sx={{
                    color: "#00a888",
                    textTransform: "none",
                    fontWeight: 600,
                    mt: 1,
                    "&:hover": { backgroundColor: "#d0f2ed" },
                  }}
                >
                  Back to Current Question
                </Button>
              </Box>

              {/* Answered Questions List */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid #b2e5de",
                  backgroundColor: "#fff",
                }}
              >
                {questionHistory.map((item, index) => {
                  const question = savedQuestions[item.questionId];
                  const currentValue = editedAnswers[item.questionId] || item.answer;
                  const hasChanged = editedAnswers[item.questionId] && editedAnswers[item.questionId] !== item.answer;
                  const isUpdating = updatingQuestionId === item.questionId;

                  return (
                    <Box
                      key={item.questionId}
                      sx={{
                        mb: 3,
                        pb: index < questionHistory.length - 1 ? 3 : 0,
                        borderBottom:
                          index < questionHistory.length - 1
                            ? "1px solid #f3f4f6"
                            : "none",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          alignItems: "flex-start",
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            backgroundColor: hasChanged ? "#f59e0b" : "#10b981",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#1f2937",
                              fontWeight: 600,
                              mb: 1.5,
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            {item.questionText || item.questionId}
                          </Typography>

                          {/* Show dropdown if question has options */}
                          {question && question.question_type === "select" && question.options ? (
                            <Box>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={currentValue}
                                  onChange={(e) => {
                                    setEditedAnswers((prev) => ({
                                      ...prev,
                                      [item.questionId]: e.target.value,
                                    }));
                                  }}
                                  disabled={isUpdating}
                                  sx={{
                                    backgroundColor: hasChanged ? "#fef3c7" : "#fff",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: hasChanged ? "#f59e0b" : "#b2e5de",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#00a888",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#00a888",
                                    },
                                  }}
                                >
                                  {question.options.map((option, idx) => (
                                    <MenuItem key={idx} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              {/* Update button appears when value changes */}
                              {hasChanged && (
                                <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleUpdateAnswer(item.questionId, currentValue)}
                                    disabled={isUpdating}
                                    sx={{
                                      backgroundColor: "#00a888",
                                      textTransform: "none",
                                      fontWeight: 600,
                                      fontSize: "13px",
                                      "&:hover": {
                                        backgroundColor: "#008f75",
                                      },
                                    }}
                                  >
                                    {isUpdating ? "Updating..." : "Update Answer"}
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                      setEditedAnswers((prev) => {
                                        const updated = { ...prev };
                                        delete updated[item.questionId];
                                        return updated;
                                      });
                                    }}
                                    disabled={isUpdating}
                                    sx={{
                                      color: "#6b7280",
                                      borderColor: "#d1d5db",
                                      textTransform: "none",
                                      fontSize: "13px",
                                      "&:hover": {
                                        borderColor: "#9ca3af",
                                        backgroundColor: "#f9fafb",
                                      },
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            /* Show plain text for non-select questions */
                            <Typography
                              sx={{
                                fontSize: 15,
                                color: "#1f2937",
                                fontWeight: 500,
                                wordWrap: "break-word",
                                overflowWrap: "break-word",
                                padding: "12px",
                                backgroundColor: "#f9fafb",
                                borderRadius: "6px",
                                border: "1px solid #e5e7eb",
                              }}
                            >
                              {item.answer}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Paper>

              {/* Error Message */}
              {submitError && (
                <Paper
                  elevation={0}
                  sx={{
                    padding: 2,
                    marginTop: 2,
                    backgroundColor: "#ffebee",
                    border: "1px solid #ef9a9a",
                    borderRadius: "8px",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#c62828" }}>
                    {submitError}
                  </Typography>
                </Paper>
              )}
            </Box>
          </Fade>
        </Container>
      </Box>
    );
  }

  // Show dynamic question mode (new approach)
  if (status === "asking_questions_dynamic" && (currentQuestion || isViewingPreviousDynamicQuestion) && !isViewingHistory) {
    // Use displayed question which could be current or historical
    const questionToDisplay = displayedDynamicQuestion || currentQuestion;
    const answerToDisplay = isViewingPreviousDynamicQuestion ? displayedDynamicAnswer : currentAnswer;

    return (
      <Fade in timeout={800}>
        <div className={styles.pressReleaseContent}>
          {/* Historical Step Indicator */}
          {HistoricalStepBanner}

          {/* Previous Question Indicator */}
          {isViewingPreviousDynamicQuestion && (
            <Paper
              elevation={0}
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: "#fffbeb",
                border: "1px solid #fbbf24",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <History sx={{ color: "#92400e", fontSize: 20 }} />
                <Typography sx={{ color: "#92400e", fontSize: "14px", fontWeight: 500 }}>
                  Viewing previous question ({questionHistory.length - dynamicQuestionPosition + 1} of {questionHistory.length + 1})
                </Typography>
              </Box>
              <Button
                onClick={handleDynamicQuestionForward}
                endIcon={<ArrowForwardIcon />}
                size="small"
                variant="contained"
                sx={{
                  backgroundColor: "#00a888",
                  textTransform: "none",
                  fontSize: "13px",
                  "&:hover": {
                    backgroundColor: "#008f75",
                  },
                }}
              >
                Return to current
              </Button>
            </Paper>
          )}

          {/* Validation Results Display */}
          {hasValidationResults && (
            <Box sx={{ mb: 3 }}>
              {/* Success Header */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #d0f2ed 0%, #e0f7f4 100%)",
                  border: "2px solid #00a888",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #00a888 0%, #00d4a8 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                    flexShrink: 0,
                  }}
                >
                  ✓
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#004141",
                      fontWeight: 700,
                      fontSize: "18px",
                      mb: 0.5,
                    }}
                  >
                    Thank you!
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#00685b",
                      fontSize: "14px",
                    }}
                  >
                    {answeredQuestionsCount} questions answered so far
                  </Typography>
                </Box>
              </Paper>

              {/* Validation Results Card */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: "2px solid #fbbf24",
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    p: 3,
                    background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                    borderBottom: "1px solid #fbbf24",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        fontSize: "28px",
                      }}
                    >
                      🔍
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        color: "#92400e",
                        fontWeight: 700,
                        fontSize: "20px",
                      }}
                    >
                      Validation Results
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ fontSize: "24px" }}>⚠️</Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#b45309",
                        fontWeight: 600,
                        fontSize: "16px",
                      }}
                    >
                      Compatibility Issues Detected
                    </Typography>
                  </Box>
                </Box>

                {/* Content with better styling */}
                <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>
                  <Box
                    sx={{
                      "& h2": {
                        display: "none", // Hide markdown h2 headers since we have custom ones
                      },
                      "& h3": {
                        display: "none", // Hide markdown h3 headers
                      },
                      "& p": {
                        fontSize: "15px",
                        color: "#374151",
                        lineHeight: 1.7,
                        mb: 2,
                      },
                      "& p:first-of-type": {
                        color: "#1f2937",
                        fontSize: "15px",
                        fontWeight: 500,
                        mb: 3,
                      },
                      "& ul": {
                        paddingLeft: "1.5rem",
                        mb: 2,
                      },
                      "& li": {
                        fontSize: "14px",
                        color: "#4b5563",
                        lineHeight: 1.7,
                        mb: 1,
                      },
                      "& strong": {
                        fontWeight: 600,
                        color: "#1f2937",
                      },
                      "& hr": {
                        border: "none",
                        borderTop: "1px solid #e5e7eb",
                        my: 3,
                      },
                      // Style for issue blocks
                      "& p:has(+ p)": {
                        position: "relative",
                        pl: 3,
                        "&::before": {
                          content: '"🔴"',
                          position: "absolute",
                          left: 0,
                          top: 0,
                        },
                      },
                    }}
                  >
                    <ReactMarkdown>{message}</ReactMarkdown>
                  </Box>
                </Box>
              </Paper>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap", justifyContent: "flex-end" }}>
                {/* Hide View Your Answers button for now */}
                {/* <Button
                variant="outlined"
                startIcon={<History />}
                onClick={() => setIsViewingHistory(true)}
                sx={{
                  flex: 1,
                  minWidth: "150px",
                  color: "#00a888",
                  borderColor: "#00a888",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#d0f2ed",
                    borderColor: "#00a888",
                  },
                }}
              >
                View Your Answers
              </Button> */}
                <Button
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={handleConfirmData}
                  disabled={isConfirming}
                  sx={{
                    minWidth: "200px",
                    backgroundColor: "#00a888",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#008f75",
                    },
                  }}
                >
                  {isConfirming ? "Generating..." : "Proceed & Generate"}
                </Button>
              </Box>
            </Box>
          )}

          {/* Header Message - Hide generic confirmation messages */}
          {message &&
            !hasValidationResults &&
            !message.includes("questions answered so far") &&
            !message.toLowerCase().includes("thank you") && (
              <Paper
                elevation={0}
                sx={{
                  padding: "20px",
                  marginBottom: "20px",
                  backgroundColor: "#f0fbf9",
                  border: "1px solid #b2e5de",
                  borderRadius: "8px",
                }}
              >
                <Box
                  sx={{
                    fontSize: "15px",
                    fontWeight: 500,
                    color: "#004141",
                    "& p": { margin: "8px 0" },
                    "& strong": { fontWeight: 600 },
                    "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
                    "& li": { marginBottom: "4px" },
                  }}
                >
                  <ReactMarkdown>{message}</ReactMarkdown>
                </Box>
              </Paper>
            )}

          {/* Navigation Controls - Hidden */}

          {/* Progress Indicator - hide since question number is now inline with question text */}

          {/* Question Card - Box stays fixed, only content transitions */}
          {!hasValidationResults && (
            <Paper
              elevation={0}
              sx={{
                padding: "24px 32px 16px 32px",
                marginBottom: 3,
                background: "linear-gradient(135deg, #ffffff 0%, #f0fbf9 100%)",
                border: "2px solid #b2e5de",
                borderRadius: "16px",
                boxShadow: "0 8px 24px rgba(0, 168, 136, 0.15)",
                transition: "all 0.3s ease-in-out",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: "linear-gradient(90deg, #00a888 0%, #00d4a8 50%, #00a888 100%)",
                  backgroundSize: "200% 100%",
                  animation: `${shimmer} 3s linear infinite`,
                },
                "&:hover": {
                  boxShadow: "0 12px 32px rgba(0, 168, 136, 0.2)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              {/* Inner content with transition */}
              <Box>
                <Fade in key={contentKey} timeout={300}>
                  <Box>
                    {/* Question Text with proper wrapping */}
                    <Box sx={{ flex: 1, minWidth: 0, mb: 2 }}>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{
                          color: "#004141",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          fontSize: "18px",
                          lineHeight: 1.5,
                        }}
                      >
                        {questionToDisplay.question_text}
                        {questionToDisplay.is_required && (
                          <span style={{ color: "#ef4444", marginLeft: 6, fontSize: "20px" }}>*</span>
                        )}
                      </Typography>
                    </Box>

                    {/* Help Text - Hidden */}
                    {false && questionToDisplay.help_text && (
                      <Box
                        sx={{
                          padding: 2,
                          marginBottom: 3,
                          backgroundColor: "#fffbeb",
                          border: "1px solid #fbbf24",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            backgroundColor: "#fbbf24",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          i
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#92400e",
                            lineHeight: 1.6,
                          }}
                        >
                          {questionToDisplay.help_text}
                        </Typography>
                      </Box>
                    )}

                    {/* Question Type: Select (Dropdown) */}
                    {questionToDisplay.question_type === "select" && (
                      <>
                        <FormControl
                          fullWidth
                          sx={{
                            mb: 0,
                            "& .MuiInputLabel-root": {
                              color: "#6b7280",
                              "&.Mui-focused": {
                                color: "#00a888",
                              },
                            },
                          }}
                        >
                          <InputLabel>Select an option</InputLabel>
                          <Select
                            value={answerToDisplay}
                            label="Select an option"
                            onChange={(e) => {
                              setCurrentAnswer(e.target.value);
                              handleAnswerSelect(questionToDisplay.question_id, e.target.value);
                            }}
                            sx={{
                              borderRadius: "12px",
                              backgroundColor: "#ffffff",
                              transition: "all 0.3s ease",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#d0e8e4",
                                borderWidth: "2px",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#00a888",
                                boxShadow: "0 4px 12px rgba(0, 168, 136, 0.15)",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#00a888",
                                borderWidth: "2px",
                                boxShadow: "0 4px 16px rgba(0, 168, 136, 0.2)",
                              },
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  borderRadius: "12px",
                                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                                  mt: 1,
                                  "& .MuiMenuItem-root": {
                                    padding: "12px 16px",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: "#d0f2ed",
                                    },
                                    "&.Mui-selected": {
                                      backgroundColor: "#e0f7f4",
                                      "&:hover": {
                                        backgroundColor: "#d0f2ed",
                                      },
                                    },
                                  },
                                },
                              },
                            }}
                          >
                            {questionToDisplay.options.map((option, index) => {
                              // Check if this option is the suggested answer
                              const suggestedAnswer = questionToDisplay.suggested_answer || "";
                              const isSuggested = suggestedAnswer && typeof suggestedAnswer === 'string' &&
                                (suggestedAnswer === option ||
                                 suggestedAnswer === `${index + 1}. ${option}` ||
                                 suggestedAnswer.replace(/^\d+\.\s*/, '') === option);

                              return (
                                <MenuItem key={index} value={option}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    <span style={{ flex: 1 }}>{option}</span>
                                    {isSuggested && (
                                      <Chip
                                        label="Suggested"
                                        size="small"
                                        sx={{
                                          backgroundColor: '#e0f7f4',
                                          color: '#00a888',
                                          fontSize: '11px',
                                          fontWeight: 600,
                                          height: '20px',
                                          '& .MuiChip-label': {
                                            padding: '0 8px',
                                          },
                                        }}
                                      />
                                    )}
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                        {/* Navigation buttons - Back and Continue */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5, minHeight: "48px" }}>
                          <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleDynamicQuestionBack}
                            disabled={questionHistory.length === 0 || dynamicQuestionPosition >= questionHistory.length}
                            sx={{
                              color: "#004141",
                              borderColor: "#b2e5de",
                              textTransform: "none",
                              fontWeight: 500,
                              "&:hover": {
                                borderColor: "#00a888",
                                backgroundColor: "#f0fbf9",
                              },
                              "&.Mui-disabled": {
                                borderColor: "#e0e0e0",
                                color: "#9e9e9e",
                              },
                            }}
                          >
                            Back
                          </Button>
                          <Fade in={!!(currentAnswer && !currentAnswer.toLowerCase().includes("other"))} timeout={300}>
                            <Box>
                              <Button
                                variant="contained"
                                onClick={() => handleSubmitDynamicAnswer()}
                                disabled={isSubmitting || !currentAnswer || currentAnswer.toLowerCase().includes("other")}
                                endIcon={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      transition: "transform 0.3s ease",
                                    }}
                                  >
                                    →
                                  </Box>
                                }
                                sx={{
                                  background: "linear-gradient(135deg, #00a888 0%, #00d4a8 100%)",
                                  color: "#fff",
                                  padding: "12px 32px",
                                  fontSize: "15px",
                                  fontWeight: 600,
                                  textTransform: "none",
                                  borderRadius: "12px",
                                  boxShadow: "0 4px 16px rgba(0, 168, 136, 0.3)",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    background: "linear-gradient(135deg, #008f75 0%, #00a888 100%)",
                                    boxShadow: "0 6px 20px rgba(0, 168, 136, 0.4)",
                                    transform: "translateY(-2px)",
                                    "& .MuiButton-endIcon": {
                                      transform: "translateX(4px)",
                                    },
                                  },
                                  "&.Mui-disabled": {
                                    background: "#e0e0e0",
                                    color: "#9e9e9e",
                                  },
                                }}
                              >
                                {isSubmitting ? "Submitting..." : "Continue"}
                              </Button>
                            </Box>
                          </Fade>
                        </Box>
                      </>
                    )}

                    {/* Custom Answer Input (for "Other" option) - always reserve space */}
                    <Fade in={currentAnswer.toLowerCase().includes("other")} timeout={300}>
                      <Box sx={{ mt: 1.5, minHeight: currentAnswer.toLowerCase().includes("other") ? "auto" : 0, overflow: "hidden" }}>
                        {currentAnswer.toLowerCase().includes("other") && (
                          <>
                            <TextField
                              fullWidth
                              label="Please specify"
                              value={currentCustomAnswer}
                              onChange={(e) =>
                                handleCustomAnswerChange(
                                  currentQuestion.question_id,
                                  e.target.value,
                                )
                              }
                              variant="outlined"
                              multiline
                              rows={3}
                              autoFocus
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "12px",
                                  backgroundColor: "#ffffff",
                                  transition: "all 0.3s ease",
                                  "& fieldset": {
                                    borderColor: "#d0e8e4",
                                    borderWidth: "2px",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#00a888",
                                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.15)",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#00a888",
                                    borderWidth: "2px",
                                    boxShadow: "0 4px 16px rgba(0, 168, 136, 0.2)",
                                  },
                                },
                                "& .MuiInputLabel-root.Mui-focused": {
                                  color: "#00a888",
                                },
                              }}
                            />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5 }}>
                              <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => setIsViewingHistory(true)}
                                disabled={answeredQuestionsCount === 0}
                                sx={{
                                  color: "#004141",
                                  borderColor: "#b2e5de",
                                  textTransform: "none",
                                  fontWeight: 500,
                                  "&:hover": {
                                    borderColor: "#00a888",
                                    backgroundColor: "#f0fbf9",
                                  },
                                  "&.Mui-disabled": {
                                    borderColor: "#e0e0e0",
                                    color: "#9e9e9e",
                                  },
                                }}
                              >
                                Back
                              </Button>
                              <Button
                                variant="contained"
                                onClick={() => handleSubmitDynamicAnswer()}
                                disabled={!currentCustomAnswer || isSubmitting}
                                endIcon={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      transition: "transform 0.3s ease",
                                    }}
                                  >
                                    →
                                  </Box>
                                }
                                sx={{
                                  background: "linear-gradient(135deg, #00a888 0%, #00d4a8 100%)",
                                  color: "#fff",
                                  padding: "12px 32px",
                                  fontSize: "15px",
                                  fontWeight: 600,
                                  textTransform: "none",
                                  borderRadius: "12px",
                                  boxShadow: "0 4px 16px rgba(0, 168, 136, 0.3)",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    background: "linear-gradient(135deg, #008f75 0%, #00a888 100%)",
                                    boxShadow: "0 6px 20px rgba(0, 168, 136, 0.4)",
                                    transform: "translateY(-2px)",
                                    "& .MuiButton-endIcon": {
                                      transform: "translateX(4px)",
                                    },
                                  },
                                  "&.Mui-disabled": {
                                    background: "#e0e0e0",
                                    color: "#9e9e9e",
                                  },
                                }}
                              >
                                {isSubmitting ? "Submitting..." : "Continue"}
                              </Button>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Fade>

                    {/* Question Type: Text and Text Long */}
                    {(currentQuestion.question_type === "text" || currentQuestion.question_type === "text_long") && (
                      <>
                        <TextField
                          fullWidth
                          multiline
                          rows={currentQuestion.question_type === "text_long" ? 6 : 4}
                          value={currentAnswer}
                          onChange={(e) =>
                            handleAnswerSelect(currentQuestion.question_id, e.target.value)
                          }
                          variant="outlined"
                          placeholder="Enter your answer here..."
                          autoFocus
                          sx={{
                            mb: 0,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: "#ffffff",
                              transition: "all 0.3s ease",
                              "& fieldset": {
                                borderColor: "#d0e8e4",
                                borderWidth: "2px",
                              },
                              "&:hover fieldset": {
                                borderColor: "#00a888",
                                boxShadow: "0 4px 12px rgba(0, 168, 136, 0.15)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#00a888",
                                borderWidth: "2px",
                                boxShadow: "0 4px 16px rgba(0, 168, 136, 0.2)",
                              },
                            },
                          }}
                        />
                        {/* Navigation buttons - Back and Continue */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5, minHeight: "48px" }}>
                          <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleDynamicQuestionBack}
                            disabled={questionHistory.length === 0 || dynamicQuestionPosition >= questionHistory.length}
                            sx={{
                              color: "#004141",
                              borderColor: "#b2e5de",
                              textTransform: "none",
                              fontWeight: 500,
                              "&:hover": {
                                borderColor: "#00a888",
                                backgroundColor: "#f0fbf9",
                              },
                              "&.Mui-disabled": {
                                borderColor: "#e0e0e0",
                                color: "#9e9e9e",
                              },
                            }}
                          >
                            Back
                          </Button>
                          <Fade in={!!currentAnswer} timeout={300}>
                            <Box>
                              <Button
                                variant="contained"
                                onClick={() => handleSubmitDynamicAnswer()}
                                disabled={!currentAnswer || isSubmitting}
                                endIcon={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      transition: "transform 0.3s ease",
                                    }}
                                  >
                                    →
                                  </Box>
                                }
                                sx={{
                                  background: "linear-gradient(135deg, #00a888 0%, #00d4a8 100%)",
                                  color: "#fff",
                                  padding: "12px 32px",
                                  fontSize: "15px",
                                  fontWeight: 600,
                                  textTransform: "none",
                                  borderRadius: "12px",
                                  boxShadow: "0 4px 16px rgba(0, 168, 136, 0.3)",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    background: "linear-gradient(135deg, #008f75 0%, #00a888 100%)",
                                    boxShadow: "0 6px 20px rgba(0, 168, 136, 0.4)",
                                    transform: "translateY(-2px)",
                                    "& .MuiButton-endIcon": {
                                      transform: "translateX(4px)",
                                    },
                                  },
                                  "&.Mui-disabled": {
                                    background: "#e0e0e0",
                                    color: "#9e9e9e",
                                  },
                                }}
                              >
                                {isSubmitting ? "Submitting..." : "Continue"}
                              </Button>
                            </Box>
                          </Fade>
                        </Box>
                      </>
                    )}
                  </Box>
                </Fade>
              </Box>
            </Paper>
          )}

          {/* Elegant fading text loading indicator - absolute positioning to prevent layout shift */}
          {!hasValidationResults && (
            <Box
              sx={{
                mt: 1,
                textAlign: "center",
                position: "relative",
                minHeight: isSubmitting ? "28px" : "0",
              }}
            >
              {isSubmitting && (
                <Fade in key={loadingMessageIndex} timeout={800}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#00a888",
                      fontWeight: 500,
                      fontSize: "14px",
                      fontStyle: "italic",
                      position: "absolute",
                      width: "100%",
                      left: 0,
                    }}
                  >
                    {loadingMessages[loadingMessageIndex]}
                  </Typography>
                </Fade>
              )}
            </Box>
          )}

          {/* Submit Error */}
          {submitError && (
            <Fade in timeout={500}>
              <Paper
                elevation={0}
                sx={{
                  padding: 2,
                  marginTop: 2,
                  backgroundColor: "#ffebee",
                  border: "2px solid #ef9a9a",
                  borderRadius: "12px",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#c62828", fontWeight: 500 }}>
                  {submitError}
                </Typography>
              </Paper>
            </Fade>
          )}
        </div>
      </Fade>
    );
  }

  // Show dynamic question mode when validation hasn't been shown yet
  if (status === "asking_questions_dynamic" && !currentQuestion && !isViewingHistory && hasValidationResults) {
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, #f0fbf9 0%, #e0f7f4 100%)",
          py: 3,
          borderRadius: 2,
        }}
      >
        <Container maxWidth="md">
          {/* Validation Results Display */}
          <Box sx={{ mb: 3 }}>
            {/* Success Header */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #d0f2ed 0%, #e0f7f4 100%)",
                border: "2px solid #00a888",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #00a888 0%, #00d4a8 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  flexShrink: 0,
                }}
              >
                ✓
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#004141",
                    fontWeight: 700,
                    fontSize: "18px",
                    mb: 0.5,
                  }}
                >
                  Thank you!
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#00685b",
                    fontSize: "14px",
                  }}
                >
                  {answeredCount} questions answered so far
                </Typography>
              </Box>
            </Paper>

            {/* Validation Results Card */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: "16px",
                overflow: "hidden",
                border: "2px solid #fbbf24",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 3,
                  background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                  borderBottom: "1px solid #fbbf24",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      fontSize: "28px",
                    }}
                  >
                    🔍
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#92400e",
                      fontWeight: 700,
                      fontSize: "20px",
                    }}
                  >
                    Validation Results
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ fontSize: "24px" }}>⚠️</Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#b45309",
                      fontWeight: 600,
                      fontSize: "16px",
                    }}
                  >
                    Compatibility Issues Detected
                  </Typography>
                </Box>
              </Box>

              {/* Content with better styling */}
              <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>
                <Box
                  sx={{
                    "& h2": {
                      display: "none", // Hide markdown h2 headers since we have custom ones
                    },
                    "& h3": {
                      display: "none", // Hide markdown h3 headers
                    },
                    "& p": {
                      fontSize: "15px",
                      color: "#374151",
                      lineHeight: 1.7,
                      mb: 2,
                    },
                    "& p:first-of-type": {
                      color: "#1f2937",
                      fontSize: "15px",
                      fontWeight: 500,
                      mb: 3,
                    },
                    "& ul": {
                      paddingLeft: "1.5rem",
                      mb: 2,
                    },
                    "& li": {
                      fontSize: "14px",
                      color: "#4b5563",
                      lineHeight: 1.7,
                      mb: 1,
                    },
                    "& strong": {
                      fontWeight: 600,
                      color: "#1f2937",
                    },
                    "& hr": {
                      border: "none",
                      borderTop: "1px solid #e5e7eb",
                      my: 3,
                    },
                  }}
                >
                  <ReactMarkdown>{message}</ReactMarkdown>
                </Box>
              </Box>
            </Paper>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {/* Hide View Your Answers button for now */}
              {/* <Button
                variant="outlined"
                startIcon={<History />}
                onClick={() => setIsViewingHistory(true)}
                sx={{
                  flex: 1,
                  minWidth: "150px",
                  color: "#00a888",
                  borderColor: "#00a888",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#d0f2ed",
                    borderColor: "#00a888",
                  },
                }}
              >
                View Your Answers
              </Button> */}
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={handleConfirmData}
                disabled={isConfirming}
                sx={{
                  minWidth: "200px",
                  backgroundColor: "#00a888",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#008f75",
                  },
                }}
              >
                {isConfirming ? "Generating..." : "Proceed & Generate"}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  // Handle Technical Evaluation Template with RFQ Package Question
  if (message.includes("TECHNICAL_TEMPLATE") && allQuestions.length > 0) {
    const question = allQuestions[0];
    const filename = message.match(/TECHNICAL_TEMPLATE_([a-f0-9-]+)\.xlsx/)?.[0] || "";

    return (
      <div className={styles.pressReleaseContent}>
        {/* Header Navigation */}
        {/* <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              color: "#666",
              fontSize: "13px",
              padding: "6px 12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            To the requests
          </Button>
          <Button
            startIcon={<History sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              color: "#666",
              fontSize: "13px",
              padding: "6px 12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            Start request over
          </Button>
        </Box> */}

        <Paper
          elevation={0}
          sx={{
            padding: "32px",
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          {/* Success Message */}
          <Box
            sx={{
              mb: 3,
              "& p:first-of-type": {
                fontSize: "18px",
                fontWeight: 600,
                color: "#00a888",
                marginBottom: "8px",
              },
              "& p": {
                fontSize: "15px",
                color: "#666",
                marginBottom: "8px",
              },
              "& strong": {
                fontWeight: 700,
                color: "#00a888",
              },
              "& ul": {
                display: "none", // Hide bullet list
              },
            }}
          >
            <ReactMarkdown>
              {(() => {
                const lines = message.split('\n');
                const filteredLines = [];
                let skipRest = false;

                for (const line of lines) {
                  if (line.trim().startsWith('-') || line.includes('Next Step:') || line.includes('RFQ package')) {
                    skipRest = true;
                    break;
                  }
                  if (!skipRest) {
                    filteredLines.push(line);
                  }
                }

                return filteredLines.join('\n');
              })()}
            </ReactMarkdown>
          </Box>

          {/* Document Card */}
          {filename && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: "#f0fbf9",
                borderRadius: "8px",
                border: "1px solid #b2e5de",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              {/* Document Name */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    backgroundColor: "#00a888",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ fontSize: "20px" }}>📊</Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    {filename}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                    }}
                  >
                    Technical Evaluation Template
                  </Typography>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => {
                    console.log("Preview clicked for:", filename);
                    handlePreview(filename);
                  }}
                  sx={{
                    borderColor: "#00a888",
                    color: "#00a888",
                    padding: "8px 16px",
                    fontSize: "13px",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "6px",
                    "&:hover": {
                      borderColor: "#008f75",
                      backgroundColor: "rgba(0, 168, 136, 0.08)",
                    },
                  }}
                >
                  Preview
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    console.log("Download clicked for:", filename);
                    handleDownload("evaluation_file", filename);
                  }}
                  disabled={isDownloading}
                  sx={{
                    backgroundColor: "#00a888",
                    color: "#fff",
                    padding: "8px 16px",
                    fontSize: "13px",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "6px",
                    boxShadow: "0 2px 8px rgba(0, 168, 136, 0.3)",
                    "&:hover": {
                      backgroundColor: "#008f75",
                      boxShadow: "0 4px 12px rgba(0, 168, 136, 0.4)",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#e0e0e0",
                      color: "#9e9e9e",
                    },
                  }}
                >
                  {isDownloading ? "Downloading..." : "Download"}
                </Button>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* RFQ Package Question Section */}
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#00a888",
                mb: 2,
              }}
            >
              📦 Next Step: RFQ Package Document
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#666",
                mb: 2,
              }}
            >
              Would you like to generate an RFQ package document?
            </Typography>
          </Box>

          {/* Question */}
          {question && (
            <>
              <RadioGroup
                value={techEvalAnswer}
                onChange={(e) => setTechEvalAnswer(e.target.value)}
              >
                {question.options?.map((option: string) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio />}
                    label={option}
                    sx={{
                      mb: 1,
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                      },
                    }}
                  />
                ))}
              </RadioGroup>

              <Button
                variant="contained"
                onClick={handleSubmitTechEvalAnswer}
                disabled={!techEvalAnswer || isSubmittingTechEval}
                sx={{
                  mt: 3,
                  backgroundColor: "#00a888",
                  color: "#fff",
                  padding: "10px 24px",
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "6px",
                  boxShadow: "0 2px 8px rgba(0, 168, 136, 0.3)",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.4)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {isSubmittingTechEval ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Submitting...
                  </>
                ) : (
                  "Submit Answer"
                )}
              </Button>
            </>
          )}
        </Paper>

        {/* Preview Modal */}
        <Dialog
          open={previewModalOpen}
          onClose={handleClosePreview}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              height: "90vh",
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#f0fbf9",
              borderBottom: "1px solid #b2e5de",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#004141" }}>
                Document Preview
              </Typography>
              {previewFile && (
                <Typography variant="body2" sx={{ color: "#666", marginTop: 0.5 }}>
                  {previewFile.name}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              padding: 0,
              backgroundColor: "#fafafa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {loadingPreview ? (
              <Box sx={{ textAlign: "center", padding: 4 }}>
                <CircularProgress size={48} sx={{ color: "#00a888" }} />
                <Typography sx={{ marginTop: 2, color: "#666" }}>
                  Loading preview...
                </Typography>
              </Box>
            ) : previewFile ? (
              previewFile.type === "pdf" ? (
                <iframe
                  src={previewFile.url}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  title="Document Preview"
                />
              ) : previewHtml ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    padding: 3,
                  }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 4,
                  }}
                >
                  <Typography variant="h6" sx={{ marginBottom: 2, color: "#666" }}>
                    {previewFile.type === "excel" && "📊 Excel Document"}
                    {previewFile.type === "word" && "📄 Word Document"}
                  </Typography>
                  <Typography sx={{ marginBottom: 3, color: "#666", textAlign: "center" }}>
                    Preview is not available for this file type in the browser.
                    <br />
                    Please download the file to view its contents.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      if (previewFile) {
                        handleDownload("evaluation_file", previewFile.name);
                      }
                    }}
                    sx={{
                      backgroundColor: "#00a888",
                      "&:hover": {
                        backgroundColor: "#008f75",
                      },
                    }}
                  >
                    Download {previewFile.name}
                  </Button>
                </Box>
              )
            ) : null}
          </DialogContent>
          <DialogActions sx={{ padding: 2, backgroundColor: "#f0fbf9" }}>
            {previewFile && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  if (previewFile) {
                    handleDownload("evaluation_file", previewFile.name);
                  }
                }}
                sx={{
                  backgroundColor: "#00a888",
                  "&:hover": {
                    backgroundColor: "#008f75",
                  },
                }}
              >
                Download
              </Button>
            )}
            <Button onClick={handleClosePreview} sx={{ color: "#666" }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  // Show loading or no questions state
  if (allQuestions.length === 0 && !currentQuestion) {
    return (
      <div className={styles.rfqBuilderContainer}>
        <RfqBuilderSidebar />
        <div className={styles.rfqBuilderMainContent}>
          <div className={styles.rfqContentCard}>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ fontSize: "14px", color: "#666" }}>
                {message || "Loading questions..."}
              </Typography>
            </Box>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.rfqBuilderContainer}>
      {/* RFQ Builder Sidebar */}
      <RfqBuilderSidebar />

      {/* Main Content Area */}
      <div className={styles.rfqBuilderMainContent}>
        <div className={styles.rfqContentCard}>
          {/* Top Bar */}
          <div className={styles.rfqTopBar}>
            <span className={styles.questionProgress}>
              {status === "step3_specific_questions" || status === "step3_asking_initial_dynamic"
                ? "Specifications"
                : "Questions"}
            </span>
            <span className={styles.questionsRemaining}>
              {answeredQuestionsCount} / {totalQuestions} answered
            </span>
          </div>

          {/* Title and Description */}
          {message && !message.includes("✅ Answer recorded") && !message.includes("Answer recorded") ? (
            <Box sx={{ mb: 3 }}>
              <h1 className={styles.rfqContentTitle}>
                {(status === "step3_specific_questions" || status === "step3_asking_initial_dynamic")
                  ? "Technical Specifications & Requirements"
                  : message.split('\n')[0].replace(/📋\s*\*\*/g, '').replace(/\*\*/g, '').trim()}
              </h1>
              {(status === "step3_specific_questions" || status === "step3_asking_initial_dynamic")
                ? <ReactMarkdown components={markdownSubtitleComponents}>{"I'll ask you 5-6 questions to understand your requirements better. Let's start:"}</ReactMarkdown>
                : (message.split('\n\n')[1] && (
                    <ReactMarkdown components={markdownSubtitleComponents}>
                      {message.split('\n\n')[1].trim()}
                    </ReactMarkdown>
                  ))
              }
            </Box>
          ) : null}

          {/* Question Card */}
          {displayQuestion && (
            <div className={styles.rfqQuestion}>
              <p className={styles.questionText}>
                {displayQuestion.question_text}
                {displayQuestion.is_required && (
                  <Box component="span" sx={{ color: "#d32f2f" }}> *</Box>
                )}
              </p>

              {/* Question Type: Select (Radio) */}
          {displayQuestion.question_type === "select" && (
            <RadioGroup
              value={displayAnswer}
              onChange={(e) =>
                handleAnswerSelect(displayQuestion.question_id, e.target.value)
              }
            >
              {displayQuestion.options.map((option, index) => {
                // Check if this option is the suggested answer
                const suggestedAnswer = displayQuestion.suggested_answer || "";
                const isSuggested = suggestedAnswer && typeof suggestedAnswer === 'string' &&
                  (suggestedAnswer === option ||
                   suggestedAnswer === `${index + 1}. ${option}` ||
                   suggestedAnswer.replace(/^\d+\.\s*/, '') === option);

                return (
                  <FormControlLabel
                    key={index}
                    value={option}
                    control={
                      <Radio
                        sx={{
                          color: "#00a888",
                          "&.Mui-checked": {
                            color: "#00a888",
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{option}</span>
                        {isSuggested && (
                          <Chip
                            label="Suggested"
                            size="small"
                            sx={{
                              backgroundColor: '#e0f7f4',
                              color: '#00a888',
                              fontSize: '11px',
                              fontWeight: 600,
                              height: '20px',
                              '& .MuiChip-label': {
                                padding: '0 8px',
                              },
                            }}
                          />
                        )}
                      </Box>
                    }
                    sx={{
                      marginBottom: 1,
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        color: "#2a312b",
                      },
                    }}
                  />
                );
              })}
            </RadioGroup>
          )}

              {/* Custom Answer Input (for "Other" option) */}
              {displayAnswer.toLowerCase().includes("other") && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Please specify"
                    value={displayCustomAnswer}
                    onChange={(e) =>
                      handleCustomAnswerChange(
                        displayQuestion.question_id,
                        e.target.value,
                      )
                    }
                    placeholder="Enter your custom answer"
                    className={styles.questionField}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#fafafa",
                        fontSize: "15px",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "14px 16px",
                      },
                    }}
                  />
                </Box>
              )}

              {/* Question Type: Text and Text Long */}
              {(displayQuestion.question_type === "text" || displayQuestion.question_type === "text_long") && (
                <TextField
                  fullWidth
                  multiline
                  minRows={displayQuestion.question_type === "text_long" ? 6 : 4}
                  value={displayAnswer}
                  onChange={(e) =>
                    handleAnswerSelect(displayQuestion.question_id, e.target.value)
                  }
                  placeholder={displayQuestion.help_text || "Enter your answer here..."}
                  className={styles.questionField}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fafafa",
                      fontSize: "15px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "14px 16px",
                    },
                  }}
                />
              )}
        </div>
      )}

          {/* Submit Error */}
          {submitError && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: "#ffebee", borderRadius: "8px", border: "1px solid #ef9a9a" }}>
              <Typography sx={{ fontSize: "14px", color: "#c62828" }}>
                {submitError}
              </Typography>
            </Box>
          )}

          {/* Warning if not all questions answered */}
          {!canSubmit && currentQuestionIndex === allQuestions.length - 1 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: "#fff3e0", borderRadius: "8px", border: "1px solid #ffb74d" }}>
              <Typography sx={{ fontSize: "13px", color: "#e65100" }}>
                Please answer all questions before submitting. You have answered{" "}
                {answeredCount} out of {totalQuestions} questions.
              </Typography>
            </Box>
          )}

          {/* Bottom Navigation */}
          <div className={styles.rfqBottomNav}>
            <Button
              variant="outlined"
              className={styles.navBack}
              onClick={() => {
                // If at first question of current batch but have history, use dynamic back
                if (currentQuestionIndex === 0 && questionHistory.length > 0) {
                  handleDynamicQuestionBack();
                } else {
                  handleBack();
                }
              }}
              disabled={currentQuestionIndex === 0 && questionHistory.length === 0}
              sx={{
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 500,
                color: "#666",
                borderColor: "#e0e0e0",
                borderRadius: "8px",
                padding: "10px 24px",
                "&:hover": {
                  borderColor: "#00a888",
                  backgroundColor: "#f0fbf9",
                },
                "&.Mui-disabled": {
                  borderColor: "#e0e0e0",
                  color: "#9e9e9e",
                },
              }}
            >
              Back
            </Button>

            {/* Progress Dots */}
            <div className={styles.navDots}>
              <span className={styles.dot}></span>
              <span className={styles.dot}></span>
              <span className={`${styles.dot} ${styles.active}`}></span>
            </div>

            {currentQuestionIndex < allQuestions.length - 1 ? (
              <Button
                variant="contained"
                className={styles.navContinue}
                onClick={handleNext}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  backgroundColor: "#00a888",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "10px 32px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                  },
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                className={styles.navContinue}
                onClick={handleSubmitAllAnswers}
                disabled={isSubmitting}
                sx={{
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 600,
                  backgroundColor: "#00a888",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "10px 32px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 4px 12px rgba(0, 168, 136, 0.2)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    Submitting...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
